import { d as colors } from "./focus-DaIphPOd.mjs";
import { c as transitions, d as spacing, f as zIndices, g as letterSpacings, h as fonts, l as radii, m as fontWeights, n as durations, p as fontSizes, r as easings, u as shadows } from "./motion-Cg-JnSSP.mjs";
import { a as IconButton$1, i as Textarea, n as Badge, o as Button, r as Select, t as Switch } from "./Switch-I-T1TqTa.mjs";
import { n as Kbd, r as Input, t as StatusDot } from "./StatusDot-CP0x3IGI.mjs";
import { f as useHaptic } from "./hooks-ChkgkdFK.mjs";
import { r as Avatar } from "./ThemeSwatch-JAmwSMrS.mjs";
import { a as StatDisplay, i as Modal, n as ThemeSwitcher, o as FormField, r as TreeView, s as Tabs, t as BottomSheet } from "./BottomSheet-Cdx2XRCw.mjs";
import { a as THEMES_BY_ID, n as useTheme, r as DEFAULT_THEME_ID } from "./context-BiO95ZTa.mjs";
import { t as GlassPanel } from "./GlassPanel-Cf6NZdLp.mjs";
import React, { createContext, forwardRef, useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import { Fragment, jsx, jsxs } from "react/jsx-runtime";
import { z } from "zod";
//#region organisms/Sidebar.tsx
/**
* EITELKEIT Sidebar
* ═══════════════════════════════════════════════════════════════════
* Collapsible sidebar with progressive disclosure sections
*
* Features:
* - 64px collapsed / 240px expanded
* - Section expand/collapse
* - Badge counts for nav items
* - Keyboard shortcuts display
* - Active item highlighting with mint accent
*/
const SidebarContext = createContext({
	isCollapsed: false,
	activeId: null
});
function Sidebar({ isCollapsed = false, onToggle, activeId = null, expandedWidth = 240, collapsedWidth = 64, style, children }) {
	const collapsed = isCollapsed ?? false;
	const width = collapsed ? collapsedWidth ?? 64 : expandedWidth ?? 240;
	return /* @__PURE__ */ jsx(SidebarContext.Provider, {
		value: {
			isCollapsed: collapsed,
			activeId
		},
		children: /* @__PURE__ */ jsxs("aside", {
			style: {
				width,
				minWidth: width,
				background: colors.voidLight,
				borderRight: `1px solid ${colors.border}`,
				display: "flex",
				flexDirection: "column",
				height: "100%",
				overflow: "hidden",
				transition: `width ${transitions.normal}`,
				...style
			},
			children: [/* @__PURE__ */ jsx("div", {
				style: {
					flex: 1,
					overflowY: "auto",
					overflowX: "hidden",
					padding: collapsed ? "16px 8px" : "16px 12px"
				},
				children
			}), onToggle && /* @__PURE__ */ jsxs("button", {
				onClick: onToggle ?? void 0,
				"aria-label": collapsed ? "Expand sidebar" : "Collapse sidebar",
				style: {
					display: "flex",
					alignItems: "center",
					justifyContent: "center",
					gap: 8,
					padding: "12px",
					margin: "8px",
					borderRadius: radii.md,
					border: `1px solid ${colors.border}`,
					background: "transparent",
					color: colors.textMuted,
					cursor: "pointer",
					fontSize: 12,
					transition: transitions.fast
				},
				children: [/* @__PURE__ */ jsx("span", {
					style: { fontSize: 16 },
					children: collapsed ? "→" : "←"
				}), !collapsed && /* @__PURE__ */ jsx("span", { children: "Collapse" })]
			})]
		})
	});
}
Sidebar.Logo = function SidebarLogo({ icon, title, subtitle, style, ...props }) {
	const { isCollapsed } = useContext(SidebarContext);
	return /* @__PURE__ */ jsxs("div", {
		style: {
			display: "flex",
			alignItems: "center",
			gap: 12,
			padding: isCollapsed ? "8px 4px" : "8px 12px",
			marginBottom: 24,
			justifyContent: isCollapsed ? "center" : "flex-start",
			...style
		},
		...props,
		children: [/* @__PURE__ */ jsx("div", {
			style: {
				width: 36,
				height: 36,
				background: `linear-gradient(135deg, ${colors.mint} 0%, ${colors.mintDim} 100%)`,
				borderRadius: radii.lg,
				display: "flex",
				alignItems: "center",
				justifyContent: "center",
				fontSize: 18,
				boxShadow: shadows.mintGlow,
				flexShrink: 0
			},
			children: icon
		}), !isCollapsed && /* @__PURE__ */ jsxs("div", {
			style: { overflow: "hidden" },
			children: [/* @__PURE__ */ jsx("div", {
				style: {
					fontFamily: fonts.mono,
					fontWeight: 700,
					fontSize: 16,
					letterSpacing: "0.05em",
					color: colors.textPrimary,
					whiteSpace: "nowrap"
				},
				children: title
			}), subtitle && /* @__PURE__ */ jsx("div", {
				style: {
					fontSize: 9,
					color: colors.textMuted,
					letterSpacing: "0.2em",
					textTransform: "uppercase",
					whiteSpace: "nowrap"
				},
				children: subtitle
			})]
		})]
	});
};
Sidebar.Section = function SidebarSection({ label, icon, defaultExpanded = true, children }) {
	const { isCollapsed: sidebarCollapsed } = useContext(SidebarContext);
	const [isExpanded, setIsExpanded] = useState(defaultExpanded);
	const showChildren = sidebarCollapsed || isExpanded;
	return /* @__PURE__ */ jsxs("nav", {
		style: { marginBottom: 16 },
		children: [!sidebarCollapsed && /* @__PURE__ */ jsxs("button", {
			onClick: () => setIsExpanded(!isExpanded),
			style: {
				display: "flex",
				alignItems: "center",
				gap: 8,
				width: "100%",
				padding: "6px 12px",
				marginBottom: 4,
				border: "none",
				background: "transparent",
				cursor: "pointer",
				textAlign: "left",
				borderRadius: radii.sm
			},
			children: [
				/* @__PURE__ */ jsx("span", {
					style: {
						fontSize: 10,
						color: colors.textMuted,
						transition: transitions.fast,
						transform: isExpanded ? "rotate(90deg)" : "rotate(0deg)"
					},
					children: "▶"
				}),
				icon && /* @__PURE__ */ jsx("span", {
					style: {
						fontSize: 12,
						color: colors.textMuted
					},
					children: icon
				}),
				/* @__PURE__ */ jsx("span", {
					style: {
						fontSize: 10,
						fontWeight: 600,
						color: colors.textMuted,
						textTransform: "uppercase",
						letterSpacing: "0.1em",
						flex: 1
					},
					children: label
				})
			]
		}), /* @__PURE__ */ jsx("div", {
			style: {
				display: showChildren ? "block" : "none",
				marginTop: sidebarCollapsed ? 0 : 4
			},
			children
		})]
	});
};
Sidebar.Item = function SidebarItem({ id, icon, label, shortcut, badge, badgeVariant, onClick }) {
	const { isCollapsed, activeId } = useContext(SidebarContext);
	const [isFocused, setIsFocused] = useState(false);
	const isActive = activeId === id;
	const variant = badgeVariant ?? "default";
	const badgeColors = {
		default: {
			bg: colors.surface,
			text: colors.textMuted
		},
		mint: {
			bg: colors.mintGlow,
			text: colors.mint
		},
		warning: {
			bg: colors.warningGlow,
			text: colors.warning
		},
		danger: {
			bg: colors.dangerGlow,
			text: colors.danger
		}
	};
	return /* @__PURE__ */ jsxs("button", {
		onClick: onClick ?? void 0,
		onFocus: () => setIsFocused(true),
		onBlur: () => setIsFocused(false),
		title: isCollapsed ? label : void 0,
		style: {
			display: "flex",
			alignItems: "center",
			gap: 10,
			width: "100%",
			padding: isCollapsed ? "10px 0" : "8px 12px",
			marginBottom: 2,
			borderRadius: radii.md,
			border: "none",
			background: isActive ? colors.surfaceElevated : "transparent",
			cursor: "pointer",
			textAlign: "left",
			justifyContent: isCollapsed ? "center" : "flex-start",
			position: "relative",
			transition: transitions.fast,
			outline: "none",
			boxShadow: isFocused ? shadows.focusRing : "none"
		},
		children: [
			isActive && /* @__PURE__ */ jsx("div", { style: {
				position: "absolute",
				left: 0,
				top: "50%",
				transform: "translateY(-50%)",
				width: 3,
				height: 16,
				borderRadius: "0 2px 2px 0",
				background: colors.mint,
				boxShadow: shadows.mintGlow
			} }),
			/* @__PURE__ */ jsx("span", {
				style: {
					fontSize: 18,
					color: isActive ? colors.mint : colors.textSecondary,
					display: "flex",
					alignItems: "center",
					justifyContent: "center",
					width: 24,
					flexShrink: 0
				},
				children: icon
			}),
			!isCollapsed && /* @__PURE__ */ jsxs(Fragment, { children: [
				/* @__PURE__ */ jsx("span", {
					style: {
						flex: 1,
						fontSize: 13,
						color: isActive ? colors.textPrimary : colors.textSecondary,
						whiteSpace: "nowrap",
						overflow: "hidden",
						textOverflow: "ellipsis"
					},
					children: label
				}),
				badge !== void 0 && /* @__PURE__ */ jsx("span", {
					style: {
						fontSize: 10,
						fontWeight: 600,
						padding: "2px 6px",
						borderRadius: radii.full,
						background: badgeColors[variant].bg,
						color: badgeColors[variant].text
					},
					children: badge
				}),
				shortcut && !badge && /* @__PURE__ */ jsx("span", {
					style: {
						display: "flex",
						gap: 2
					},
					children: shortcut.map((key, i) => /* @__PURE__ */ jsx(Kbd, {
						size: null,
						style: {
							fontSize: 9,
							padding: "2px 4px"
						},
						children: key
					}, i))
				})
			] }),
			isCollapsed && badge !== void 0 && /* @__PURE__ */ jsx("span", { style: {
				position: "absolute",
				top: 6,
				right: 6,
				width: 8,
				height: 8,
				borderRadius: "50%",
				background: badgeColors[variant].text
			} })
		]
	});
};
Sidebar.Divider = function SidebarDivider() {
	return /* @__PURE__ */ jsx("div", { style: {
		height: 1,
		background: colors.border,
		margin: "12px 8px"
	} });
};
Sidebar.Footer = function SidebarFooter({ children, style, ...props }) {
	return /* @__PURE__ */ jsx("div", {
		style: {
			padding: "12px 8px",
			borderTop: `1px solid ${colors.border}`,
			...style
		},
		...props,
		children
	});
};
//#endregion
//#region organisms/Header.tsx
function Header({ title, titleIcon, badge, actions, glass = true, style, children, ...props }) {
	return /* @__PURE__ */ jsxs("header", {
		style: {
			display: "flex",
			alignItems: "center",
			justifyContent: "space-between",
			padding: "16px 24px",
			borderBottom: `1px solid ${colors.border}`,
			background: glass ? colors.glassBg : colors.voidLight,
			backdropFilter: glass ? "blur(20px)" : "none",
			WebkitBackdropFilter: glass ? "blur(20px)" : "none",
			...style
		},
		...props,
		children: [
			/* @__PURE__ */ jsxs("div", {
				style: {
					display: "flex",
					alignItems: "center",
					gap: "8px",
					fontFamily: fonts.mono,
					fontWeight: 600
				},
				children: [
					titleIcon && /* @__PURE__ */ jsx("span", {
						style: { color: colors.mint },
						children: titleIcon
					}),
					title,
					badge
				]
			}),
			actions && /* @__PURE__ */ jsx("div", {
				style: {
					display: "flex",
					gap: "8px"
				},
				children: actions
			}),
			children
		]
	});
}
//#endregion
//#region organisms/Toast.tsx
/**
* EITELKEIT Toast
* ═══════════════════════════════════════════════════════════════════
* Toast notification system
*/
const ToastContext = createContext(null);
function useToast() {
	const context = useContext(ToastContext);
	if (!context) throw new Error("useToast must be used within ToastProvider");
	return context;
}
const variantStyles = {
	success: {
		bg: colors.mintGlow,
		border: `1px solid rgba(57, 255, 159, 0.3)`,
		color: colors.mint,
		icon: "✓"
	},
	error: {
		bg: colors.dangerGlow,
		border: `1px solid rgba(255, 71, 87, 0.3)`,
		color: colors.danger,
		icon: "✕"
	},
	warning: {
		bg: colors.warningGlow,
		border: `1px solid rgba(255, 184, 57, 0.3)`,
		color: colors.warning,
		icon: "⚠"
	},
	info: {
		bg: colors.infoGlow,
		border: `1px solid rgba(57, 192, 255, 0.3)`,
		color: colors.info,
		icon: "ℹ"
	}
};
function ToastProvider({ children }) {
	const [toasts, setToasts] = useState([]);
	const timeoutRefs = useRef(/* @__PURE__ */ new Map());
	const removeToast = useCallback((id) => {
		const timeout = timeoutRefs.current.get(id);
		if (timeout) {
			clearTimeout(timeout);
			timeoutRefs.current.delete(id);
		}
		setToasts((prev) => prev.filter((t) => t.id !== id));
	}, []);
	const addToast = useCallback((message, variant, duration, description = null) => {
		const v = variant ?? "info";
		const d = duration ?? 5e3;
		const id = Math.random().toString(36).slice(2);
		setToasts((prev) => [...prev, {
			id,
			message,
			description,
			variant: v,
			duration: d
		}]);
		if (d > 0) {
			const timeout = setTimeout(() => {
				timeoutRefs.current.delete(id);
				setToasts((prev) => prev.filter((t) => t.id !== id));
			}, d);
			timeoutRefs.current.set(id, timeout);
		}
	}, []);
	useEffect(() => {
		const currentTimeouts = timeoutRefs.current;
		return () => {
			currentTimeouts.forEach((timeout) => clearTimeout(timeout));
			currentTimeouts.clear();
		};
	}, []);
	const toast = useMemo(() => ({
		success: (message, options) => addToast(message, "success", options?.duration ?? null, options?.description ?? null),
		error: (message, options) => addToast(message, "error", options?.duration ?? null, options?.description ?? null),
		warning: (message, options) => addToast(message, "warning", options?.duration ?? null, options?.description ?? null),
		info: (message, options) => addToast(message, "info", options?.duration ?? null, options?.description ?? null)
	}), [addToast]);
	return /* @__PURE__ */ jsxs(ToastContext.Provider, {
		value: {
			toasts,
			addToast,
			removeToast,
			toast
		},
		children: [children, /* @__PURE__ */ jsx(ToastContainer, {
			toasts,
			onRemove: removeToast
		})]
	});
}
function ToastContainer({ toasts, onRemove }) {
	return /* @__PURE__ */ jsx("div", {
		style: {
			position: "fixed",
			bottom: "24px",
			right: "24px",
			display: "flex",
			flexDirection: "column",
			gap: "8px",
			zIndex: zIndices.toast,
			pointerEvents: "none"
		},
		children: toasts.map((toast) => /* @__PURE__ */ jsx(ToastItem, {
			toast,
			onRemove
		}, toast.id))
	});
}
function ToastItem({ toast, onRemove }) {
	const style = variantStyles[toast.variant];
	return /* @__PURE__ */ jsxs("div", {
		style: {
			display: "flex",
			alignItems: toast.description !== null ? "flex-start" : "center",
			gap: "10px",
			padding: "12px 16px",
			background: style.bg,
			border: style.border,
			borderRadius: radii.md,
			fontFamily: fonts.sans,
			fontSize: "13px",
			color: colors.textPrimary,
			boxShadow: "0 8px 32px rgba(0, 0, 0, 0.4)",
			animation: "e-toast-in 0.2s ease",
			pointerEvents: "auto",
			minWidth: "280px",
			maxWidth: "400px"
		},
		children: [
			/* @__PURE__ */ jsx("span", {
				style: {
					color: style.color,
					marginTop: toast.description !== null ? "2px" : 0
				},
				children: style.icon
			}),
			/* @__PURE__ */ jsxs("div", {
				style: {
					flex: 1,
					minWidth: 0
				},
				children: [/* @__PURE__ */ jsx("span", {
					style: { fontWeight: 500 },
					children: toast.message
				}), toast.description !== null && /* @__PURE__ */ jsx("p", {
					style: {
						margin: "4px 0 0 0",
						fontSize: "12px",
						color: colors.textMuted,
						lineHeight: 1.4
					},
					children: toast.description
				})]
			}),
			/* @__PURE__ */ jsx(IconButton$1, {
				onClick: () => onRemove(toast.id),
				variant: null,
				size: null,
				ariaLabel: "Dismiss notification",
				isDisabled: null,
				glyph: "×",
				icon: null,
				style: {
					width: "auto",
					height: "auto",
					background: "transparent",
					borderWidth: 0,
					borderStyle: "none",
					borderColor: "transparent",
					color: colors.textMuted,
					cursor: "pointer",
					padding: "4px",
					fontSize: "14px",
					flexShrink: 0,
					marginTop: toast.description !== null ? "-2px" : 0
				}
			}),
			/* @__PURE__ */ jsx("style", { children: `
        @keyframes e-toast-in {
          from {
            opacity: 0;
            transform: translateX(100%);
          }
          to {
            opacity: 1;
            transform: translateX(0);
          }
        }
      ` })
		]
	});
}
//#endregion
//#region organisms/BentoGrid.tsx
/**
* EITELKEIT BentoGrid
* ═══════════════════════════════════════════════════════════════════
* Flexible grid layout for dashboard panels
* Supports drag-resize and responsive layouts
*/
const gapMap = {
	sm: spacing.sm,
	md: spacing.md,
	lg: spacing.lg
};
function BentoGrid({ items, columns, gap, rowHeight, resizable, onLayoutChange, style }) {
	const resolvedColumns = columns ?? 4;
	const resolvedGap = gap ?? "md";
	const resolvedRowHeight = rowHeight ?? 200;
	const resolvedResizable = resizable ?? false;
	const [localItems, setLocalItems] = useState(items);
	const [resizing, setResizing] = useState(null);
	const gridRef = useRef(null);
	useEffect(() => {
		setLocalItems(items);
	}, [items]);
	const handleResizeStart = useCallback((itemId) => {
		if (!resolvedResizable) return;
		setResizing(itemId);
	}, [resolvedResizable]);
	const handleResizeEnd = useCallback(() => {
		setResizing(null);
		onLayoutChange?.(localItems);
	}, [localItems, onLayoutChange]);
	return /* @__PURE__ */ jsx("div", {
		ref: gridRef,
		style: {
			display: "grid",
			gridTemplateColumns: `repeat(${resolvedColumns}, 1fr)`,
			gridAutoRows: `${resolvedRowHeight}px`,
			gap: gapMap[resolvedGap],
			width: "100%",
			height: "100%",
			...style
		},
		children: localItems.map((item) => /* @__PURE__ */ jsx(BentoCell, {
			item,
			resizable: resolvedResizable,
			isResizing: resizing === item.id,
			onResizeStart: () => handleResizeStart(item.id),
			onResizeEnd: handleResizeEnd
		}, item.id))
	});
}
function BentoCell({ item, resizable, isResizing, onResizeStart, onResizeEnd }) {
	const [isHovered, setIsHovered] = useState(false);
	return /* @__PURE__ */ jsxs("div", {
		style: {
			gridColumn: `span ${item.colSpan ?? 1}`,
			gridRow: `span ${item.rowSpan ?? 1}`,
			position: "relative",
			borderRadius: radii.lg,
			background: colors.surface,
			border: `1px solid ${isResizing ? colors.mint : isHovered ? colors.borderHover : colors.border}`,
			overflow: "hidden",
			transition: transitions.all,
			boxShadow: isResizing ? `0 0 20px ${colors.mintGlow}` : "none"
		},
		onMouseEnter: () => setIsHovered(true),
		onMouseLeave: () => setIsHovered(false),
		children: [/* @__PURE__ */ jsx("div", {
			style: {
				width: "100%",
				height: "100%",
				overflow: "auto"
			},
			children: item.content
		}), resizable && /* @__PURE__ */ jsx("div", {
			style: {
				position: "absolute",
				right: 0,
				bottom: 0,
				width: "20px",
				height: "20px",
				cursor: "se-resize",
				opacity: isHovered ? 1 : 0,
				transition: transitions.opacity
			},
			onMouseDown: (e) => {
				e.preventDefault();
				onResizeStart();
			},
			onMouseUp: onResizeEnd,
			children: /* @__PURE__ */ jsx("svg", {
				width: "12",
				height: "12",
				viewBox: "0 0 12 12",
				fill: "none",
				style: {
					position: "absolute",
					right: "4px",
					bottom: "4px"
				},
				children: /* @__PURE__ */ jsx("path", {
					d: "M10 2L2 10M10 6L6 10M10 10L10 10",
					stroke: colors.textMuted,
					strokeWidth: "1.5",
					strokeLinecap: "round"
				})
			})
		})]
	});
}
const BentoLayouts = {
	/** Single large panel */
	single: [{
		id: "main",
		colSpan: 4,
		rowSpan: 4,
		minColSpan: null,
		minRowSpan: null
	}],
	/** Two equal columns */
	twoColumn: [{
		id: "left",
		colSpan: 2,
		rowSpan: 4,
		minColSpan: null,
		minRowSpan: null
	}, {
		id: "right",
		colSpan: 2,
		rowSpan: 4,
		minColSpan: null,
		minRowSpan: null
	}],
	/** Three columns */
	threeColumn: [
		{
			id: "left",
			colSpan: 1,
			rowSpan: 4,
			minColSpan: null,
			minRowSpan: null
		},
		{
			id: "center",
			colSpan: 2,
			rowSpan: 4,
			minColSpan: null,
			minRowSpan: null
		},
		{
			id: "right",
			colSpan: 1,
			rowSpan: 4,
			minColSpan: null,
			minRowSpan: null
		}
	],
	/** 2x2 Grid */
	quadrant: [
		{
			id: "tl",
			colSpan: 2,
			rowSpan: 2,
			minColSpan: null,
			minRowSpan: null
		},
		{
			id: "tr",
			colSpan: 2,
			rowSpan: 2,
			minColSpan: null,
			minRowSpan: null
		},
		{
			id: "bl",
			colSpan: 2,
			rowSpan: 2,
			minColSpan: null,
			minRowSpan: null
		},
		{
			id: "br",
			colSpan: 2,
			rowSpan: 2,
			minColSpan: null,
			minRowSpan: null
		}
	],
	/** Main + sidebar + bottom */
	dashboard: [
		{
			id: "main",
			colSpan: 3,
			rowSpan: 3,
			minColSpan: null,
			minRowSpan: null
		},
		{
			id: "sidebar",
			colSpan: 1,
			rowSpan: 4,
			minColSpan: null,
			minRowSpan: null
		},
		{
			id: "bottom",
			colSpan: 3,
			rowSpan: 1,
			minColSpan: null,
			minRowSpan: null
		}
	],
	/** Orchestra: broadcast + multiple agents */
	orchestra: [
		{
			id: "broadcast",
			colSpan: 4,
			rowSpan: 1,
			minColSpan: null,
			minRowSpan: null
		},
		{
			id: "agent1",
			colSpan: 2,
			rowSpan: 3,
			minColSpan: null,
			minRowSpan: null
		},
		{
			id: "agent2",
			colSpan: 2,
			rowSpan: 3,
			minColSpan: null,
			minRowSpan: null
		}
	],
	/** Orchestra: 4 agents */
	orchestraQuad: [
		{
			id: "broadcast",
			colSpan: 4,
			rowSpan: 1,
			minColSpan: null,
			minRowSpan: null
		},
		{
			id: "agent1",
			colSpan: 1,
			rowSpan: 3,
			minColSpan: null,
			minRowSpan: null
		},
		{
			id: "agent2",
			colSpan: 1,
			rowSpan: 3,
			minColSpan: null,
			minRowSpan: null
		},
		{
			id: "agent3",
			colSpan: 1,
			rowSpan: 3,
			minColSpan: null,
			minRowSpan: null
		},
		{
			id: "agent4",
			colSpan: 1,
			rowSpan: 3,
			minColSpan: null,
			minRowSpan: null
		}
	]
};
//#endregion
//#region organisms/AgentPanel.tsx
/**
* EITELKEIT AgentPanel
* ═══════════════════════════════════════════════════════════════════
* Individual agent chat and control interface
* Supports multiple modes: deliberation, casual, joke, adversarial
*/
const modeConfig = {
	deliberation: {
		label: "Deliberation",
		icon: "🤔",
		color: colors.mint
	},
	casual: {
		label: "Casual",
		icon: "💬",
		color: colors.info
	},
	joke: {
		label: "Joke",
		icon: "😂",
		color: colors.warning
	},
	adversarial: {
		label: "Devil's Advocate",
		icon: "😈",
		color: colors.error
	},
	silent: {
		label: "Silent Observer",
		icon: "👁️",
		color: colors.textMuted
	}
};
const statusConfig$1 = {
	idle: {
		label: "Idle",
		color: colors.textMuted,
		pulse: false
	},
	thinking: {
		label: "Thinking...",
		color: colors.mint,
		pulse: true
	},
	responding: {
		label: "Responding",
		color: colors.info,
		pulse: true
	},
	error: {
		label: "Error",
		color: colors.error,
		pulse: false
	},
	offline: {
		label: "Offline",
		color: colors.textMuted,
		pulse: false
	}
};
function AgentPanel({ agent, messages, showThinking = true, currentThinking, modeSelector = true, compact = false, readOnly = false, onSendMessage, onModeChange, style }) {
	const [input, setInput] = useState("");
	const [isModeOpen, setIsModeOpen] = useState(false);
	const messagesEndRef = useRef(null);
	const inputRef = useRef(null);
	useEffect(() => {
		messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
	}, [messages, currentThinking]);
	const handleSubmit = useCallback(() => {
		if (!input.trim() || readOnly) return;
		onSendMessage?.(input.trim());
		setInput("");
		inputRef.current?.focus();
	}, [
		input,
		readOnly,
		onSendMessage
	]);
	const handleKeyDown = useCallback((e) => {
		if (e.key === "Enter" && !e.shiftKey) {
			e.preventDefault();
			handleSubmit();
		}
	}, [handleSubmit]);
	const agentColor = agent.color ?? colors.mint;
	const status = statusConfig$1[agent.status];
	const mode = modeConfig[agent.mode];
	return /* @__PURE__ */ jsxs("div", {
		style: {
			display: "flex",
			flexDirection: "column",
			height: "100%",
			background: colors.voidDeep,
			borderRadius: radii.lg,
			overflow: "hidden",
			...style
		},
		children: [
			/* @__PURE__ */ jsx(AgentHeader, {
				agent,
				mode,
				status,
				agentColor,
				compact,
				modeSelector,
				isModeOpen,
				onModeToggle: () => setIsModeOpen(!isModeOpen),
				onModeChange: (m) => {
					onModeChange?.(m);
					setIsModeOpen(false);
				}
			}),
			/* @__PURE__ */ jsxs("div", {
				style: {
					flex: 1,
					overflowY: "auto",
					padding: compact ? spacing.sm : spacing.md,
					display: "flex",
					flexDirection: "column",
					gap: spacing.sm
				},
				children: [
					messages.map((msg) => /* @__PURE__ */ jsx(MessageBubble, {
						message: msg,
						agentColor,
						compact,
						showThinking
					}, msg.id)),
					currentThinking && agent.status === "thinking" && /* @__PURE__ */ jsx(ThinkingIndicator, {
						content: currentThinking,
						color: agentColor
					}),
					/* @__PURE__ */ jsx("div", { ref: messagesEndRef })
				]
			}),
			!readOnly && /* @__PURE__ */ jsx("div", {
				style: {
					padding: compact ? spacing.sm : spacing.md,
					borderTop: `1px solid ${colors.border}`,
					background: colors.void
				},
				children: /* @__PURE__ */ jsxs("div", {
					style: {
						display: "flex",
						gap: spacing.sm,
						alignItems: "flex-end"
					},
					children: [/* @__PURE__ */ jsx("textarea", {
						ref: inputRef,
						value: input,
						onChange: (e) => setInput(e.target.value),
						onKeyDown: handleKeyDown,
						placeholder: `Message ${agent.name}...`,
						rows: 1,
						style: {
							flex: 1,
							background: colors.surface,
							border: `1px solid ${colors.border}`,
							borderRadius: radii.md,
							padding: `${spacing.sm} ${spacing.md}`,
							color: colors.text,
							fontFamily: fonts.sans,
							fontSize: compact ? fontSizes.sm : fontSizes.base,
							resize: "none",
							outline: "none",
							transition: transitions.colors,
							minHeight: "38px",
							maxHeight: "120px"
						}
					}), /* @__PURE__ */ jsx("button", {
						onClick: handleSubmit,
						disabled: !input.trim(),
						style: {
							padding: `${spacing.sm} ${spacing.md}`,
							background: input.trim() ? agentColor : colors.surface,
							color: input.trim() ? colors.void : colors.textMuted,
							border: "none",
							borderRadius: radii.md,
							fontFamily: fonts.sans,
							fontSize: fontSizes.sm,
							fontWeight: fontWeights.semibold,
							cursor: input.trim() ? "pointer" : "not-allowed",
							transition: transitions.all,
							opacity: input.trim() ? 1 : .5
						},
						children: "Send"
					})]
				})
			})
		]
	});
}
function AgentHeader({ agent, mode, status, agentColor, compact: compactProp, modeSelector: modeSelectorProp, isModeOpen, onModeToggle, onModeChange }) {
	const compact = compactProp ?? false;
	const modeSelector = modeSelectorProp ?? true;
	return /* @__PURE__ */ jsxs("div", {
		style: {
			display: "flex",
			alignItems: "center",
			justifyContent: "space-between",
			padding: compact ? `${spacing.sm} ${spacing.md}` : spacing.md,
			borderBottom: `1px solid ${colors.border}`,
			background: colors.void
		},
		children: [
			/* @__PURE__ */ jsxs("div", {
				style: {
					display: "flex",
					alignItems: "center",
					gap: spacing.sm
				},
				children: [/* @__PURE__ */ jsxs("div", {
					style: {
						width: compact ? "24px" : "32px",
						height: compact ? "24px" : "32px",
						borderRadius: "50%",
						background: `linear-gradient(135deg, ${agentColor}20, ${agentColor}40)`,
						border: `2px solid ${agentColor}`,
						display: "flex",
						alignItems: "center",
						justifyContent: "center",
						position: "relative"
					},
					children: [/* @__PURE__ */ jsx("span", {
						style: { fontSize: compact ? "10px" : "12px" },
						children: agent.avatar ?? agent.name.charAt(0).toUpperCase()
					}), /* @__PURE__ */ jsx("div", { style: {
						position: "absolute",
						bottom: "-2px",
						right: "-2px",
						width: "10px",
						height: "10px",
						borderRadius: "50%",
						background: status.color,
						border: `2px solid ${colors.void}`,
						animation: status.pulse ? "e-pulse 2s ease-in-out infinite" : "none"
					} })]
				}), /* @__PURE__ */ jsxs("div", { children: [/* @__PURE__ */ jsx("div", {
					style: {
						fontFamily: fonts.sans,
						fontSize: compact ? fontSizes.sm : fontSizes.base,
						fontWeight: fontWeights.semibold,
						color: colors.text
					},
					children: agent.name
				}), /* @__PURE__ */ jsx("div", {
					style: {
						fontFamily: fonts.mono,
						fontSize: fontSizes.xs,
						color: colors.textMuted,
						textTransform: "uppercase",
						letterSpacing: letterSpacings.wider
					},
					children: agent.role
				})] })]
			}),
			modeSelector && /* @__PURE__ */ jsxs("div", {
				style: { position: "relative" },
				children: [/* @__PURE__ */ jsxs("button", {
					onClick: onModeToggle,
					style: {
						display: "flex",
						alignItems: "center",
						gap: spacing.xs,
						padding: `${spacing.xs} ${spacing.sm}`,
						background: colors.surface,
						border: `1px solid ${colors.border}`,
						borderRadius: radii.md,
						color: mode.color,
						fontFamily: fonts.mono,
						fontSize: fontSizes.xs,
						cursor: "pointer",
						transition: transitions.all
					},
					children: [
						/* @__PURE__ */ jsx("span", { children: mode.icon }),
						/* @__PURE__ */ jsx("span", { children: mode.label }),
						/* @__PURE__ */ jsx("span", {
							style: { opacity: .5 },
							children: "▼"
						})
					]
				}), isModeOpen && /* @__PURE__ */ jsx("div", {
					style: {
						position: "absolute",
						top: "100%",
						right: 0,
						marginTop: spacing.xs,
						background: colors.surface,
						border: `1px solid ${colors.border}`,
						borderRadius: radii.md,
						overflow: "hidden",
						zIndex: 100,
						minWidth: "150px",
						boxShadow: shadows.lg
					},
					children: Object.entries(modeConfig).map(([key, cfg]) => /* @__PURE__ */ jsxs("button", {
						onClick: () => onModeChange(key),
						style: {
							display: "flex",
							alignItems: "center",
							gap: spacing.sm,
							width: "100%",
							padding: `${spacing.sm} ${spacing.md}`,
							background: agent.mode === key ? colors.surfaceElevated : "transparent",
							border: "none",
							color: cfg.color,
							fontFamily: fonts.mono,
							fontSize: fontSizes.xs,
							cursor: "pointer",
							textAlign: "left",
							transition: transitions.colors
						},
						children: [/* @__PURE__ */ jsx("span", { children: cfg.icon }), /* @__PURE__ */ jsx("span", { children: cfg.label })]
					}, key))
				})]
			}),
			/* @__PURE__ */ jsx("style", { children: `
        @keyframes e-pulse {
          0%, 100% { opacity: 1; transform: scale(1); }
          50% { opacity: 0.6; transform: scale(1.1); }
        }
      ` })
		]
	});
}
function MessageBubble({ message, agentColor, compact: compactProp, showThinking: showThinkingProp }) {
	const compact = compactProp ?? false;
	const showThinking = showThinkingProp ?? true;
	const [thinkingExpanded, setThinkingExpanded] = useState(false);
	const isAgent = message.role === "agent";
	const isSystem = message.role === "system";
	return /* @__PURE__ */ jsxs("div", {
		style: {
			alignSelf: isAgent ? "flex-start" : isSystem ? "center" : "flex-end",
			maxWidth: isSystem ? "100%" : "85%"
		},
		children: [showThinking && message.thinking && isAgent && /* @__PURE__ */ jsxs("div", {
			style: {
				marginBottom: spacing.xs,
				padding: `${spacing.xs} ${spacing.sm}`,
				background: `${agentColor}10`,
				borderRadius: radii.sm,
				borderLeft: `2px solid ${agentColor}40`
			},
			children: [/* @__PURE__ */ jsxs("button", {
				onClick: () => setThinkingExpanded(!thinkingExpanded),
				style: {
					display: "flex",
					alignItems: "center",
					gap: spacing.xs,
					background: "none",
					border: "none",
					color: colors.textMuted,
					fontFamily: fonts.mono,
					fontSize: fontSizes.xs,
					cursor: "pointer",
					padding: 0
				},
				children: [/* @__PURE__ */ jsx("span", {
					style: {
						transform: thinkingExpanded ? "rotate(90deg)" : "none",
						transition: transitions.transform
					},
					children: "▶"
				}), /* @__PURE__ */ jsx("span", { children: "Thinking" })]
			}), thinkingExpanded && /* @__PURE__ */ jsx("div", {
				style: {
					marginTop: spacing.xs,
					fontFamily: fonts.mono,
					fontSize: fontSizes.sm,
					color: colors.textSecondary,
					whiteSpace: "pre-wrap",
					lineHeight: 1.5
				},
				children: message.thinking
			})]
		}), /* @__PURE__ */ jsxs("div", {
			style: {
				padding: compact ? `${spacing.sm} ${spacing.md}` : spacing.md,
				background: isAgent ? colors.surface : isSystem ? `${colors.warning}15` : `${agentColor}20`,
				borderRadius: radii.md,
				border: `1px solid ${isAgent ? colors.border : isSystem ? `${colors.warning}30` : `${agentColor}30`}`
			},
			children: [/* @__PURE__ */ jsx("div", {
				style: {
					fontFamily: fonts.sans,
					fontSize: compact ? fontSizes.sm : fontSizes.base,
					color: colors.text,
					whiteSpace: "pre-wrap",
					lineHeight: 1.5
				},
				children: message.content
			}), /* @__PURE__ */ jsx("div", {
				style: {
					marginTop: spacing.xs,
					fontFamily: fonts.mono,
					fontSize: fontSizes.xs,
					color: colors.textMuted
				},
				children: message.timestamp.toLocaleTimeString()
			})]
		})]
	});
}
function ThinkingIndicator({ content, color }) {
	return /* @__PURE__ */ jsxs("div", {
		style: {
			alignSelf: "flex-start",
			maxWidth: "85%",
			padding: spacing.md,
			background: `${color}10`,
			borderRadius: radii.md,
			borderLeft: `3px solid ${color}`,
			animation: "e-breathe 3s ease-in-out infinite"
		},
		children: [
			/* @__PURE__ */ jsxs("div", {
				style: {
					display: "flex",
					alignItems: "center",
					gap: spacing.sm,
					marginBottom: spacing.xs
				},
				children: [/* @__PURE__ */ jsx("div", { style: {
					width: "8px",
					height: "8px",
					borderRadius: "50%",
					background: color,
					animation: "e-pulse 1.5s ease-in-out infinite"
				} }), /* @__PURE__ */ jsx("span", {
					style: {
						fontFamily: fonts.mono,
						fontSize: fontSizes.xs,
						color,
						textTransform: "uppercase",
						letterSpacing: letterSpacings.wider
					},
					children: "Thinking"
				})]
			}),
			/* @__PURE__ */ jsx("div", {
				style: {
					fontFamily: fonts.mono,
					fontSize: fontSizes.sm,
					color: colors.textSecondary,
					whiteSpace: "pre-wrap",
					lineHeight: 1.5,
					maxHeight: "200px",
					overflow: "auto"
				},
				children: content
			}),
			/* @__PURE__ */ jsx("style", { children: `
        @keyframes e-breathe {
          0%, 100% { opacity: 0.8; }
          50% { opacity: 1; }
        }
      ` })
		]
	});
}
//#endregion
//#region organisms/AgentOrchestra.tsx
/**
* EITELKEIT AgentOrchestra
* ═══════════════════════════════════════════════════════════════════
* Multi-agent orchestration dashboard
* Supports broadcast mode, tabs, and bento grid layout
*/
const layoutConfigs = {
	two: () => [{
		id: "agent-0",
		colSpan: 2,
		rowSpan: 4,
		minColSpan: 1,
		minRowSpan: 1
	}, {
		id: "agent-1",
		colSpan: 2,
		rowSpan: 4,
		minColSpan: 1,
		minRowSpan: 1
	}],
	three: () => [
		{
			id: "agent-0",
			colSpan: 2,
			rowSpan: 4,
			minColSpan: 1,
			minRowSpan: 1
		},
		{
			id: "agent-1",
			colSpan: 1,
			rowSpan: 4,
			minColSpan: 1,
			minRowSpan: 1
		},
		{
			id: "agent-2",
			colSpan: 1,
			rowSpan: 4,
			minColSpan: 1,
			minRowSpan: 1
		}
	],
	quad: () => [
		{
			id: "agent-0",
			colSpan: 2,
			rowSpan: 2,
			minColSpan: 1,
			minRowSpan: 1
		},
		{
			id: "agent-1",
			colSpan: 2,
			rowSpan: 2,
			minColSpan: 1,
			minRowSpan: 1
		},
		{
			id: "agent-2",
			colSpan: 2,
			rowSpan: 2,
			minColSpan: 1,
			minRowSpan: 1
		},
		{
			id: "agent-3",
			colSpan: 2,
			rowSpan: 2,
			minColSpan: 1,
			minRowSpan: 1
		}
	],
	custom: (count) => {
		const cols = count <= 2 ? 2 : count <= 4 ? 2 : 3;
		return Array.from({ length: count }, (_, i) => ({
			id: `agent-${i}`,
			colSpan: Math.floor(4 / cols),
			rowSpan: count <= 4 ? 2 : 1,
			minColSpan: 1,
			minRowSpan: 1
		}));
	}
};
function AgentOrchestra({ agents, view = "bento", layout = "two", broadcast = true, consciousnessStream = false, onBroadcast, onAgentMessage, onAgentModeChange, onViewChange, onLayoutChange, style }) {
	const [broadcastInput, setBroadcastInput] = useState("");
	const [activeTab, setActiveTab] = useState(0);
	const [focusedAgent, setFocusedAgent] = useState(null);
	const handleBroadcast = useCallback(() => {
		if (!broadcastInput.trim()) return;
		onBroadcast?.(broadcastInput.trim());
		setBroadcastInput("");
	}, [broadcastInput, onBroadcast]);
	const handleKeyDown = useCallback((e) => {
		if (e.key === "Enter" && (e.metaKey || e.ctrlKey)) {
			e.preventDefault();
			handleBroadcast();
		}
	}, [handleBroadcast]);
	const bentoItems = useMemo(() => {
		const resolvedLayout = layout ?? "two";
		const layoutConfig = layoutConfigs[resolvedLayout](agents.length);
		return agents.slice(0, layoutConfig.length).map((agent, i) => ({
			...layoutConfig[i],
			content: /* @__PURE__ */ jsx(AgentPanel, {
				agent,
				messages: agent.messages,
				currentThinking: agent.currentThinking,
				showThinking: true,
				modeSelector: true,
				compact: resolvedLayout === "quad" || agents.length > 3,
				readOnly: false,
				onSendMessage: (content) => onAgentMessage?.(agent.id, content),
				onModeChange: (mode) => onAgentModeChange?.(agent.id, mode),
				style: { height: "100%" }
			})
		}));
	}, [
		agents,
		layout,
		onAgentMessage,
		onAgentModeChange
	]);
	return /* @__PURE__ */ jsxs("div", {
		style: {
			display: "flex",
			flexDirection: "column",
			height: "100%",
			background: colors.voidDeep,
			...style
		},
		children: [
			/* @__PURE__ */ jsx(OrchestraToolbar, {
				view: view ?? "bento",
				layout: layout ?? "two",
				agentCount: agents.length,
				onViewChange,
				onLayoutChange
			}),
			broadcast && /* @__PURE__ */ jsx(BroadcastInput, {
				value: broadcastInput,
				onChange: setBroadcastInput,
				onSubmit: handleBroadcast,
				onKeyDown: handleKeyDown,
				agentCount: agents.length
			}),
			/* @__PURE__ */ jsxs("div", {
				style: {
					flex: 1,
					overflow: "hidden",
					padding: spacing.md
				},
				children: [
					view === "bento" && /* @__PURE__ */ jsx(BentoGrid, {
						items: bentoItems,
						columns: 4,
						gap: "md",
						rowHeight: 180,
						resizable: false,
						onLayoutChange: () => null,
						style: { height: "100%" }
					}),
					view === "tabs" && /* @__PURE__ */ jsx(TabsView, {
						agents,
						activeTab,
						onTabChange: setActiveTab,
						onAgentMessage,
						onAgentModeChange
					}),
					view === "focus" && /* @__PURE__ */ jsx(FocusView, {
						agents,
						focusedAgent,
						onFocusChange: setFocusedAgent,
						onAgentMessage,
						onAgentModeChange
					})
				]
			})
		]
	});
}
function OrchestraToolbar({ view, layout, agentCount, onViewChange, onLayoutChange }) {
	return /* @__PURE__ */ jsxs("div", {
		style: {
			display: "flex",
			alignItems: "center",
			justifyContent: "space-between",
			padding: `${spacing.sm} ${spacing.md}`,
			borderBottom: `1px solid ${colors.border}`,
			background: colors.void
		},
		children: [/* @__PURE__ */ jsxs("div", {
			style: {
				fontFamily: fonts.mono,
				fontSize: fontSizes.xs,
				fontWeight: fontWeights.semibold,
				color: colors.mint,
				textTransform: "uppercase",
				letterSpacing: letterSpacings.widest
			},
			children: ["Agent Orchestra", /* @__PURE__ */ jsxs("span", {
				style: {
					color: colors.textMuted,
					marginLeft: spacing.sm
				},
				children: [agentCount, " agents"]
			})]
		}), /* @__PURE__ */ jsxs("div", {
			style: {
				display: "flex",
				gap: spacing.md
			},
			children: [/* @__PURE__ */ jsx("div", {
				style: {
					display: "flex",
					gap: "1px",
					background: colors.border,
					borderRadius: radii.md,
					overflow: "hidden"
				},
				children: [
					{
						key: "bento",
						label: "Grid",
						icon: "⊞"
					},
					{
						key: "tabs",
						label: "Tabs",
						icon: "☰"
					},
					{
						key: "focus",
						label: "Focus",
						icon: "◉"
					}
				].map((v) => /* @__PURE__ */ jsxs("button", {
					onClick: () => onViewChange?.(v.key),
					style: {
						padding: `${spacing.xs} ${spacing.sm}`,
						background: view === v.key ? colors.surfaceElevated : colors.surface,
						border: "none",
						color: view === v.key ? colors.mint : colors.textMuted,
						fontFamily: fonts.mono,
						fontSize: fontSizes.xs,
						cursor: "pointer",
						transition: transitions.colors,
						display: "flex",
						alignItems: "center",
						gap: spacing.xs
					},
					title: v.label,
					children: [/* @__PURE__ */ jsx("span", { children: v.icon }), /* @__PURE__ */ jsx("span", { children: v.label })]
				}, v.key))
			}), view === "bento" && /* @__PURE__ */ jsx("div", {
				style: {
					display: "flex",
					gap: "1px",
					background: colors.border,
					borderRadius: radii.md,
					overflow: "hidden"
				},
				children: [
					{
						key: "two",
						label: "2-Split",
						minAgents: 2
					},
					{
						key: "three",
						label: "3-Panel",
						minAgents: 3
					},
					{
						key: "quad",
						label: "4-Grid",
						minAgents: 4
					},
					{
						key: "custom",
						label: "Auto",
						minAgents: 1
					}
				].filter((l) => l.minAgents <= agentCount).map((l) => /* @__PURE__ */ jsx("button", {
					onClick: () => onLayoutChange?.(l.key),
					style: {
						padding: `${spacing.xs} ${spacing.sm}`,
						background: layout === l.key ? colors.surfaceElevated : colors.surface,
						border: "none",
						color: layout === l.key ? colors.mint : colors.textMuted,
						fontFamily: fonts.mono,
						fontSize: fontSizes.xs,
						cursor: "pointer",
						transition: transitions.colors
					},
					children: l.label
				}, l.key))
			})]
		})]
	});
}
function BroadcastInput({ value, onChange, onSubmit, onKeyDown, agentCount }) {
	return /* @__PURE__ */ jsxs("div", {
		style: {
			padding: spacing.md,
			borderBottom: `1px solid ${colors.border}`,
			background: `linear-gradient(180deg, ${colors.mintGlow} 0%, transparent 100%)`
		},
		children: [/* @__PURE__ */ jsxs("div", {
			style: {
				display: "flex",
				alignItems: "center",
				gap: spacing.sm,
				marginBottom: spacing.xs
			},
			children: [/* @__PURE__ */ jsx("div", { style: {
				width: "8px",
				height: "8px",
				borderRadius: "50%",
				background: colors.mint,
				boxShadow: shadows.mintGlow
			} }), /* @__PURE__ */ jsxs("span", {
				style: {
					fontFamily: fonts.mono,
					fontSize: fontSizes.xs,
					color: colors.mint,
					textTransform: "uppercase",
					letterSpacing: letterSpacings.wider
				},
				children: [
					"Broadcast to all ",
					agentCount,
					" agents"
				]
			})]
		}), /* @__PURE__ */ jsxs("div", {
			style: {
				display: "flex",
				gap: spacing.sm
			},
			children: [/* @__PURE__ */ jsx("textarea", {
				value,
				onChange: (e) => onChange(e.target.value),
				onKeyDown,
				placeholder: "Type a message to send to all agents... (⌘+Enter to send)",
				rows: 2,
				style: {
					flex: 1,
					background: colors.surface,
					border: `1px solid ${colors.mintGlow}`,
					borderRadius: radii.md,
					padding: spacing.md,
					color: colors.text,
					fontFamily: fonts.sans,
					fontSize: fontSizes.base,
					resize: "none",
					outline: "none",
					transition: transitions.colors
				}
			}), /* @__PURE__ */ jsx("button", {
				onClick: onSubmit,
				disabled: !value.trim(),
				style: {
					padding: `${spacing.md} ${spacing.lg}`,
					background: value.trim() ? colors.mint : colors.surface,
					color: value.trim() ? colors.void : colors.textMuted,
					border: "none",
					borderRadius: radii.md,
					fontFamily: fonts.mono,
					fontSize: fontSizes.sm,
					fontWeight: fontWeights.bold,
					textTransform: "uppercase",
					letterSpacing: letterSpacings.wide,
					cursor: value.trim() ? "pointer" : "not-allowed",
					transition: transitions.all,
					opacity: value.trim() ? 1 : .5,
					boxShadow: value.trim() ? shadows.mintGlow : "none"
				},
				children: "Broadcast"
			})]
		})]
	});
}
function TabsView({ agents, activeTab, onTabChange, onAgentMessage, onAgentModeChange }) {
	return /* @__PURE__ */ jsxs("div", {
		style: {
			display: "flex",
			flexDirection: "column",
			height: "100%"
		},
		children: [/* @__PURE__ */ jsx("div", {
			style: {
				display: "flex",
				gap: "1px",
				background: colors.border,
				borderRadius: `${radii.md} ${radii.md} 0 0`,
				overflow: "hidden"
			},
			children: agents.map((agent, i) => /* @__PURE__ */ jsxs("button", {
				onClick: () => onTabChange(i),
				style: {
					flex: 1,
					padding: `${spacing.sm} ${spacing.md}`,
					background: activeTab === i ? colors.surface : colors.void,
					border: "none",
					borderBottom: activeTab === i ? `2px solid ${agent.color ?? colors.mint}` : "none",
					color: activeTab === i ? colors.text : colors.textMuted,
					fontFamily: fonts.sans,
					fontSize: fontSizes.sm,
					fontWeight: fontWeights.medium,
					cursor: "pointer",
					transition: transitions.colors,
					display: "flex",
					alignItems: "center",
					justifyContent: "center",
					gap: spacing.xs
				},
				children: [/* @__PURE__ */ jsx("span", { style: {
					width: "8px",
					height: "8px",
					borderRadius: "50%",
					background: agent.color ?? colors.mint
				} }), agent.name]
			}, agent.id))
		}), /* @__PURE__ */ jsx("div", {
			style: {
				flex: 1,
				overflow: "hidden"
			},
			children: agents[activeTab] && /* @__PURE__ */ jsx(AgentPanel, {
				agent: agents[activeTab],
				messages: agents[activeTab].messages,
				currentThinking: agents[activeTab].currentThinking,
				showThinking: true,
				modeSelector: true,
				compact: false,
				readOnly: false,
				onSendMessage: (content) => onAgentMessage?.(agents[activeTab].id, content),
				onModeChange: (mode) => onAgentModeChange?.(agents[activeTab].id, mode),
				style: {
					height: "100%",
					borderRadius: `0 0 ${radii.md} ${radii.md}`
				}
			})
		})]
	});
}
function FocusView({ agents, focusedAgent, onFocusChange, onAgentMessage, onAgentModeChange }) {
	const focused = agents.find((a) => a.id === focusedAgent) ?? agents[0];
	return /* @__PURE__ */ jsxs("div", {
		style: {
			display: "flex",
			height: "100%",
			gap: spacing.md
		},
		children: [/* @__PURE__ */ jsxs("div", {
			style: {
				width: "200px",
				background: colors.surface,
				borderRadius: radii.md,
				padding: spacing.sm,
				display: "flex",
				flexDirection: "column",
				gap: spacing.xs
			},
			children: [/* @__PURE__ */ jsx("div", {
				style: {
					fontFamily: fonts.mono,
					fontSize: fontSizes.xs,
					color: colors.textMuted,
					textTransform: "uppercase",
					letterSpacing: letterSpacings.wider,
					padding: `${spacing.xs} ${spacing.sm}`
				},
				children: "Agents"
			}), agents.map((agent) => /* @__PURE__ */ jsxs("button", {
				onClick: () => onFocusChange(agent.id),
				style: {
					display: "flex",
					alignItems: "center",
					gap: spacing.sm,
					padding: `${spacing.sm} ${spacing.md}`,
					background: focused?.id === agent.id ? colors.surfaceElevated : "transparent",
					border: focused?.id === agent.id ? `1px solid ${agent.color ?? colors.mint}30` : "1px solid transparent",
					borderRadius: radii.md,
					color: colors.text,
					fontFamily: fonts.sans,
					fontSize: fontSizes.sm,
					cursor: "pointer",
					textAlign: "left",
					transition: transitions.all
				},
				children: [/* @__PURE__ */ jsx("div", {
					style: {
						width: "24px",
						height: "24px",
						borderRadius: "50%",
						background: `${agent.color ?? colors.mint}20`,
						border: `2px solid ${agent.color ?? colors.mint}`,
						display: "flex",
						alignItems: "center",
						justifyContent: "center",
						fontSize: fontSizes.xs
					},
					children: agent.avatar ?? agent.name.charAt(0)
				}), /* @__PURE__ */ jsxs("div", { children: [/* @__PURE__ */ jsx("div", {
					style: { fontWeight: fontWeights.medium },
					children: agent.name
				}), /* @__PURE__ */ jsx("div", {
					style: {
						fontFamily: fonts.mono,
						fontSize: fontSizes.xs,
						color: colors.textMuted
					},
					children: agent.status
				})] })]
			}, agent.id))]
		}), /* @__PURE__ */ jsx("div", {
			style: { flex: 1 },
			children: focused && /* @__PURE__ */ jsx(AgentPanel, {
				agent: focused,
				messages: focused.messages,
				currentThinking: focused.currentThinking,
				showThinking: true,
				modeSelector: true,
				compact: false,
				readOnly: false,
				onSendMessage: (content) => onAgentMessage?.(focused.id, content),
				onModeChange: (mode) => onAgentModeChange?.(focused.id, mode),
				style: { height: "100%" }
			})
		})]
	});
}
//#endregion
//#region organisms/ConsciousnessStream.tsx
/**
* EITELKEIT ConsciousnessStream
* ═══════════════════════════════════════════════════════════════════
* Unified event stream of all agent activities
* Real-time visualization of thoughts, tool calls, and memory operations
*/
const eventTypeConfig = {
	thinking: {
		label: "THINK",
		icon: "🧠",
		color: colors.mint
	},
	tool_use: {
		label: "TOOL",
		icon: "🔧",
		color: colors.info
	},
	tool_result: {
		label: "RESULT",
		icon: "✓",
		color: colors.success
	},
	memory_read: {
		label: "MEM↓",
		icon: "📖",
		color: "#a855f7"
	},
	memory_write: {
		label: "MEM↑",
		icon: "💾",
		color: "#a855f7"
	},
	message: {
		label: "MSG",
		icon: "💬",
		color: colors.textSecondary
	},
	error: {
		label: "ERROR",
		icon: "⚠️",
		color: colors.error
	},
	consensus: {
		label: "CONSENSUS",
		icon: "🤝",
		color: colors.warning
	},
	spawn: {
		label: "SPAWN",
		icon: "🌱",
		color: colors.mint
	},
	system: {
		label: "SYS",
		icon: "⚙️",
		color: colors.textMuted
	}
};
function ConsciousnessStream({ events, filter, agentFilter, autoScroll = true, pauseOnHover = true, maxEvents = 100, showTimestamps = true, compact = false, onEventClick, style }) {
	const [isPaused, setIsPaused] = useState(false);
	const [selectedType, setSelectedType] = useState(null);
	const containerRef = useRef(null);
	const endRef = useRef(null);
	const resolvedMaxEvents = maxEvents ?? 100;
	const filteredEvents = events.filter((e) => !filter || filter.includes(e.type)).filter((e) => !agentFilter || !e.agentId || agentFilter.includes(e.agentId)).filter((e) => !selectedType || e.type === selectedType).slice(-resolvedMaxEvents);
	useEffect(() => {
		if (autoScroll && !isPaused && endRef.current) endRef.current.scrollIntoView({ behavior: "smooth" });
	}, [
		filteredEvents.length,
		autoScroll,
		isPaused
	]);
	return /* @__PURE__ */ jsxs("div", {
		style: {
			display: "flex",
			flexDirection: "column",
			height: "100%",
			background: colors.voidDeep,
			borderRadius: radii.lg,
			overflow: "hidden",
			...style
		},
		children: [
			/* @__PURE__ */ jsx(StreamHeader, {
				eventCount: filteredEvents.length,
				totalCount: events.length,
				selectedType,
				onTypeSelect: setSelectedType,
				isPaused,
				compact: compact ?? false
			}),
			/* @__PURE__ */ jsxs("div", {
				ref: containerRef,
				onMouseEnter: () => pauseOnHover && setIsPaused(true),
				onMouseLeave: () => pauseOnHover && setIsPaused(false),
				style: {
					flex: 1,
					overflowY: "auto",
					padding: compact ? spacing.sm : spacing.md
				},
				children: [filteredEvents.map((event, index) => /* @__PURE__ */ jsx(StreamEventItem, {
					event,
					showTimestamp: showTimestamps ?? true,
					compact: compact ?? false,
					isNew: index === filteredEvents.length - 1,
					onClick: () => onEventClick?.(event)
				}, event.id)), /* @__PURE__ */ jsx("div", { ref: endRef })]
			}),
			isPaused && /* @__PURE__ */ jsx("div", {
				style: {
					position: "absolute",
					bottom: spacing.md,
					left: "50%",
					transform: "translateX(-50%)",
					padding: `${spacing.xs} ${spacing.md}`,
					background: colors.warning,
					color: colors.void,
					borderRadius: radii.full,
					fontFamily: fonts.mono,
					fontSize: fontSizes.xs,
					fontWeight: fontWeights.bold,
					textTransform: "uppercase",
					letterSpacing: letterSpacings.wide
				},
				children: "⏸ Paused"
			})
		]
	});
}
function StreamHeader({ eventCount, totalCount, selectedType, onTypeSelect, isPaused, compact }) {
	return /* @__PURE__ */ jsxs("div", {
		style: {
			padding: compact ? `${spacing.sm} ${spacing.md}` : spacing.md,
			borderBottom: `1px solid ${colors.border}`,
			background: colors.void
		},
		children: [
			/* @__PURE__ */ jsxs("div", {
				style: {
					display: "flex",
					alignItems: "center",
					justifyContent: "space-between",
					marginBottom: spacing.sm
				},
				children: [/* @__PURE__ */ jsxs("div", {
					style: {
						display: "flex",
						alignItems: "center",
						gap: spacing.sm
					},
					children: [/* @__PURE__ */ jsx("div", { style: {
						width: "10px",
						height: "10px",
						borderRadius: "50%",
						background: isPaused ? colors.warning : colors.mint,
						boxShadow: `0 0 10px ${isPaused ? colors.warningGlow : colors.mintGlow}`,
						animation: isPaused ? "none" : "e-stream-pulse 2s ease-in-out infinite"
					} }), /* @__PURE__ */ jsx("span", {
						style: {
							fontFamily: fonts.mono,
							fontSize: fontSizes.xs,
							fontWeight: fontWeights.semibold,
							color: colors.mint,
							textTransform: "uppercase",
							letterSpacing: letterSpacings.widest
						},
						children: "Consciousness Stream"
					})]
				}), /* @__PURE__ */ jsxs("span", {
					style: {
						fontFamily: fonts.mono,
						fontSize: fontSizes.xs,
						color: colors.textMuted
					},
					children: [
						eventCount,
						"/",
						totalCount
					]
				})]
			}),
			/* @__PURE__ */ jsxs("div", {
				style: {
					display: "flex",
					gap: spacing.xs,
					flexWrap: "wrap"
				},
				children: [/* @__PURE__ */ jsx(FilterPill, {
					label: "All",
					icon: null,
					isActive: selectedType === null,
					onClick: () => onTypeSelect(null),
					color: colors.textSecondary
				}), Object.entries(eventTypeConfig).map(([type, cfg]) => /* @__PURE__ */ jsx(FilterPill, {
					label: cfg.label,
					icon: cfg.icon,
					isActive: selectedType === type,
					onClick: () => onTypeSelect(selectedType === type ? null : type),
					color: cfg.color
				}, type))]
			}),
			/* @__PURE__ */ jsx("style", { children: `
        @keyframes e-stream-pulse {
          0%, 100% { opacity: 1; box-shadow: 0 0 10px ${colors.mintGlow}; }
          50% { opacity: 0.6; box-shadow: 0 0 20px ${colors.mintGlowStrong}; }
        }
      ` })
		]
	});
}
function FilterPill({ label, icon, isActive, onClick, color }) {
	return /* @__PURE__ */ jsxs("button", {
		onClick,
		style: {
			display: "flex",
			alignItems: "center",
			gap: "4px",
			padding: `2px ${spacing.sm}`,
			background: isActive ? `${color}20` : "transparent",
			border: `1px solid ${isActive ? color : colors.border}`,
			borderRadius: radii.full,
			color: isActive ? color : colors.textMuted,
			fontFamily: fonts.mono,
			fontSize: "10px",
			cursor: "pointer",
			transition: transitions.colors
		},
		children: [icon && /* @__PURE__ */ jsx("span", { children: icon }), /* @__PURE__ */ jsx("span", { children: label })]
	});
}
function StreamEventItem({ event, showTimestamp, compact, isNew, onClick }) {
	const config = eventTypeConfig[event.type];
	return /* @__PURE__ */ jsxs("div", {
		onClick,
		style: {
			display: "flex",
			gap: spacing.sm,
			padding: compact ? `${spacing.xs} ${spacing.sm}` : spacing.sm,
			marginBottom: spacing.xs,
			background: isNew ? `${config.color}08` : "transparent",
			borderRadius: radii.sm,
			borderLeft: `2px solid ${config.color}`,
			cursor: "pointer",
			transition: transitions.colors,
			animation: isNew ? "e-slide-in 0.3s ease-out" : "none"
		},
		children: [
			/* @__PURE__ */ jsxs("div", {
				style: {
					display: "flex",
					alignItems: "center",
					gap: "4px",
					padding: `2px ${spacing.xs}`,
					background: `${config.color}20`,
					borderRadius: radii.sm,
					flexShrink: 0,
					height: "fit-content"
				},
				children: [/* @__PURE__ */ jsx("span", {
					style: { fontSize: "10px" },
					children: config.icon
				}), /* @__PURE__ */ jsx("span", {
					style: {
						fontFamily: fonts.mono,
						fontSize: "9px",
						fontWeight: fontWeights.bold,
						color: config.color,
						textTransform: "uppercase",
						letterSpacing: letterSpacings.wide
					},
					children: config.label
				})]
			}),
			/* @__PURE__ */ jsxs("div", {
				style: {
					flex: 1,
					minWidth: 0
				},
				children: [event.agentName && /* @__PURE__ */ jsxs("span", {
					style: {
						fontFamily: fonts.mono,
						fontSize: fontSizes.xs,
						fontWeight: fontWeights.semibold,
						color: event.agentColor ?? colors.mint,
						marginRight: spacing.xs
					},
					children: [event.agentName, ":"]
				}), /* @__PURE__ */ jsx("span", {
					style: {
						fontFamily: fonts.sans,
						fontSize: compact ? fontSizes.sm : fontSizes.md,
						color: colors.text,
						wordBreak: "break-word"
					},
					children: event.content.length > 200 ? `${event.content.slice(0, 200)}...` : event.content
				})]
			}),
			showTimestamp && /* @__PURE__ */ jsx("span", {
				style: {
					fontFamily: fonts.mono,
					fontSize: "10px",
					color: colors.textMuted,
					flexShrink: 0
				},
				children: event.timestamp.toLocaleTimeString()
			}),
			/* @__PURE__ */ jsx("style", { children: `
        @keyframes e-slide-in {
          from {
            opacity: 0;
            transform: translateX(-10px);
          }
          to {
            opacity: 1;
            transform: translateX(0);
          }
        }
      ` })
		]
	});
}
//#endregion
//#region organisms/MemoryViewer.tsx
/**
* EITELKEIT MemoryViewer
* ═══════════════════════════════════════════════════════════════════
* Semantic memory browser with similarity scores
* View, search, and inject memories into agent context
*/
function MemoryViewer({ memories, viewMode = "list", sortBy = "relevance", searchable = true, filterable = true, injectable = true, onMemoryClick, onInject, onSearch, style }) {
	const [searchQuery, setSearchQuery] = useState("");
	const [selectedTags, setSelectedTags] = useState([]);
	const [localSortBy, setLocalSortBy] = useState(sortBy ?? "relevance");
	const [localViewMode, setLocalViewMode] = useState(viewMode ?? "list");
	const [expandedMemory, setExpandedMemory] = useState(null);
	const allTags = useMemo(() => {
		const tags = /* @__PURE__ */ new Set();
		memories.forEach((m) => m.tags.forEach((t) => tags.add(t)));
		return Array.from(tags).sort();
	}, [memories]);
	const filteredMemories = useMemo(() => {
		let result = [...memories];
		if (selectedTags.length > 0) result = result.filter((m) => selectedTags.some((t) => m.tags.includes(t)));
		switch (localSortBy) {
			case "relevance":
				result.sort((a, b) => (b.similarity ?? 0) - (a.similarity ?? 0));
				break;
			case "importance":
				result.sort((a, b) => b.importance - a.importance);
				break;
			case "recency":
				result.sort((a, b) => b.createdAt.getTime() - a.createdAt.getTime());
				break;
			case "alphabetical":
				result.sort((a, b) => a.content.localeCompare(b.content));
				break;
		}
		return result;
	}, [
		memories,
		selectedTags,
		localSortBy
	]);
	const handleSearch = useCallback((query) => {
		setSearchQuery(query);
		onSearch?.(query);
	}, [onSearch]);
	const handleTagToggle = useCallback((tag) => {
		setSelectedTags((prev) => prev.includes(tag) ? prev.filter((t) => t !== tag) : [...prev, tag]);
	}, []);
	return /* @__PURE__ */ jsxs("div", {
		style: {
			display: "flex",
			flexDirection: "column",
			height: "100%",
			background: colors.voidDeep,
			borderRadius: radii.lg,
			overflow: "hidden",
			...style
		},
		children: [
			/* @__PURE__ */ jsx(MemoryHeader, {
				memoryCount: filteredMemories.length,
				totalCount: memories.length,
				viewMode: localViewMode,
				sortBy: localSortBy,
				onViewModeChange: setLocalViewMode,
				onSortByChange: setLocalSortBy
			}),
			searchable && /* @__PURE__ */ jsx("div", {
				style: { padding: `0 ${spacing.md} ${spacing.sm}` },
				children: /* @__PURE__ */ jsxs("div", {
					style: {
						display: "flex",
						alignItems: "center",
						gap: spacing.sm,
						padding: `${spacing.sm} ${spacing.md}`,
						background: colors.surface,
						border: `1px solid ${colors.border}`,
						borderRadius: radii.md
					},
					children: [
						/* @__PURE__ */ jsx("span", {
							style: { color: colors.textMuted },
							children: "🔍"
						}),
						/* @__PURE__ */ jsx("input", {
							type: "text",
							value: searchQuery,
							onChange: (e) => handleSearch(e.target.value),
							placeholder: "Semantic search memories...",
							style: {
								flex: 1,
								background: "transparent",
								border: "none",
								outline: "none",
								color: colors.text,
								fontFamily: fonts.sans,
								fontSize: fontSizes.sm
							}
						}),
						searchQuery && /* @__PURE__ */ jsx("button", {
							onClick: () => handleSearch(""),
							style: {
								background: "none",
								border: "none",
								color: colors.textMuted,
								cursor: "pointer",
								padding: 0
							},
							children: "✕"
						})
					]
				})
			}),
			filterable && allTags.length > 0 && /* @__PURE__ */ jsxs("div", {
				style: {
					padding: `0 ${spacing.md} ${spacing.sm}`,
					display: "flex",
					gap: spacing.xs,
					flexWrap: "wrap"
				},
				children: [allTags.slice(0, 20).map((tag) => /* @__PURE__ */ jsx(TagChip, {
					tag,
					isSelected: selectedTags.includes(tag),
					onClick: () => handleTagToggle(tag)
				}, tag)), allTags.length > 20 && /* @__PURE__ */ jsxs("span", {
					style: {
						fontFamily: fonts.mono,
						fontSize: fontSizes.xs,
						color: colors.textMuted,
						padding: `2px ${spacing.xs}`
					},
					children: [
						"+",
						allTags.length - 20,
						" more"
					]
				})]
			}),
			/* @__PURE__ */ jsxs("div", {
				style: {
					flex: 1,
					overflowY: "auto",
					padding: spacing.md
				},
				children: [
					localViewMode === "list" && /* @__PURE__ */ jsx(MemoryList, {
						memories: filteredMemories,
						expandedMemory,
						injectable: injectable ?? true,
						onMemoryClick: (m) => {
							setExpandedMemory(expandedMemory === m.id ? null : m.id);
							onMemoryClick?.(m);
						},
						onInject
					}),
					localViewMode === "timeline" && /* @__PURE__ */ jsx(MemoryTimeline, {
						memories: filteredMemories,
						onMemoryClick,
						onInject,
						injectable: injectable ?? true
					}),
					localViewMode === "tags" && /* @__PURE__ */ jsx(MemoryTagCloud, {
						memories: filteredMemories,
						allTags,
						selectedTags,
						onTagSelect: handleTagToggle
					})
				]
			})
		]
	});
}
function MemoryHeader({ memoryCount, totalCount, viewMode, sortBy, onViewModeChange, onSortByChange }) {
	return /* @__PURE__ */ jsxs("div", {
		style: {
			padding: spacing.md,
			borderBottom: `1px solid ${colors.border}`,
			background: colors.void
		},
		children: [/* @__PURE__ */ jsxs("div", {
			style: {
				display: "flex",
				alignItems: "center",
				justifyContent: "space-between",
				marginBottom: spacing.sm
			},
			children: [/* @__PURE__ */ jsxs("div", {
				style: {
					display: "flex",
					alignItems: "center",
					gap: spacing.sm
				},
				children: [/* @__PURE__ */ jsx("span", {
					style: { fontSize: "16px" },
					children: "🧠"
				}), /* @__PURE__ */ jsx("span", {
					style: {
						fontFamily: fonts.mono,
						fontSize: fontSizes.xs,
						fontWeight: fontWeights.semibold,
						color: "#a855f7",
						textTransform: "uppercase",
						letterSpacing: letterSpacings.widest
					},
					children: "Memory Viewer"
				})]
			}), /* @__PURE__ */ jsxs("span", {
				style: {
					fontFamily: fonts.mono,
					fontSize: fontSizes.xs,
					color: colors.textMuted
				},
				children: [
					memoryCount,
					"/",
					totalCount,
					" memories"
				]
			})]
		}), /* @__PURE__ */ jsxs("div", {
			style: {
				display: "flex",
				gap: spacing.md
			},
			children: [/* @__PURE__ */ jsx("div", {
				style: {
					display: "flex",
					gap: "1px",
					background: colors.border,
					borderRadius: radii.sm,
					overflow: "hidden"
				},
				children: [
					{
						key: "list",
						label: "List",
						icon: "☰"
					},
					{
						key: "timeline",
						label: "Timeline",
						icon: "⏱"
					},
					{
						key: "tags",
						label: "Tags",
						icon: "#"
					}
				].map((v) => /* @__PURE__ */ jsx("button", {
					onClick: () => onViewModeChange(v.key),
					style: {
						padding: `${spacing.xs} ${spacing.sm}`,
						background: viewMode === v.key ? colors.surfaceElevated : colors.surface,
						border: "none",
						color: viewMode === v.key ? "#a855f7" : colors.textMuted,
						fontFamily: fonts.mono,
						fontSize: fontSizes.xs,
						cursor: "pointer",
						transition: transitions.colors
					},
					title: v.label,
					children: v.icon
				}, v.key))
			}), /* @__PURE__ */ jsx("select", {
				value: sortBy,
				onChange: (e) => onSortByChange(e.target.value),
				style: {
					padding: `${spacing.xs} ${spacing.sm}`,
					background: colors.surface,
					border: `1px solid ${colors.border}`,
					borderRadius: radii.sm,
					color: colors.text,
					fontFamily: fonts.mono,
					fontSize: fontSizes.xs,
					cursor: "pointer",
					outline: "none"
				},
				children: [
					{
						key: "relevance",
						label: "Relevance"
					},
					{
						key: "importance",
						label: "Importance"
					},
					{
						key: "recency",
						label: "Recent"
					}
				].map((s) => /* @__PURE__ */ jsx("option", {
					value: s.key,
					children: s.label
				}, s.key))
			})]
		})]
	});
}
function TagChip({ tag, isSelected, onClick }) {
	return /* @__PURE__ */ jsxs("button", {
		onClick,
		style: {
			padding: `2px ${spacing.sm}`,
			background: isSelected ? "#a855f720" : "transparent",
			border: `1px solid ${isSelected ? "#a855f7" : colors.border}`,
			borderRadius: radii.full,
			color: isSelected ? "#a855f7" : colors.textMuted,
			fontFamily: fonts.mono,
			fontSize: "10px",
			cursor: "pointer",
			transition: transitions.colors
		},
		children: ["#", tag]
	});
}
function MemoryList({ memories, expandedMemory, injectable, onMemoryClick, onInject }) {
	return /* @__PURE__ */ jsxs("div", {
		style: {
			display: "flex",
			flexDirection: "column",
			gap: spacing.sm
		},
		children: [memories.map((memory) => /* @__PURE__ */ jsx(MemoryCard, {
			memory,
			isExpanded: expandedMemory === memory.id,
			injectable,
			onClick: () => onMemoryClick?.(memory),
			onInject: () => onInject?.(memory.id)
		}, memory.id)), memories.length === 0 && /* @__PURE__ */ jsx("div", {
			style: {
				padding: spacing.xl,
				textAlign: "center",
				color: colors.textMuted,
				fontFamily: fonts.sans,
				fontSize: fontSizes.sm
			},
			children: "No memories found"
		})]
	});
}
function MemoryCard({ memory, isExpanded, injectable, onClick, onInject }) {
	const similarityPercent = memory.similarity ? Math.round(memory.similarity * 100) : null;
	const importancePercent = Math.round(memory.importance * 100);
	return /* @__PURE__ */ jsxs("div", {
		onClick,
		style: {
			padding: spacing.md,
			background: colors.surface,
			border: `1px solid ${isExpanded ? "#a855f740" : colors.border}`,
			borderRadius: radii.md,
			cursor: "pointer",
			transition: transitions.all
		},
		children: [
			/* @__PURE__ */ jsxs("div", {
				style: {
					display: "flex",
					alignItems: "center",
					justifyContent: "space-between",
					marginBottom: spacing.sm
				},
				children: [/* @__PURE__ */ jsxs("div", {
					style: {
						display: "flex",
						gap: spacing.md
					},
					children: [similarityPercent !== null && /* @__PURE__ */ jsxs("div", {
						style: {
							display: "flex",
							alignItems: "center",
							gap: spacing.xs
						},
						children: [/* @__PURE__ */ jsx("span", {
							style: {
								fontFamily: fonts.mono,
								fontSize: fontSizes.xs,
								color: colors.textMuted
							},
							children: "SIM"
						}), /* @__PURE__ */ jsxs("span", {
							style: {
								fontFamily: fonts.mono,
								fontSize: fontSizes.sm,
								fontWeight: fontWeights.bold,
								color: similarityPercent > 70 ? colors.mint : colors.textSecondary
							},
							children: [similarityPercent, "%"]
						})]
					}), /* @__PURE__ */ jsxs("div", {
						style: {
							display: "flex",
							alignItems: "center",
							gap: spacing.xs
						},
						children: [/* @__PURE__ */ jsx("span", {
							style: {
								fontFamily: fonts.mono,
								fontSize: fontSizes.xs,
								color: colors.textMuted
							},
							children: "IMP"
						}), /* @__PURE__ */ jsxs("span", {
							style: {
								fontFamily: fonts.mono,
								fontSize: fontSizes.sm,
								fontWeight: fontWeights.bold,
								color: importancePercent > 70 ? "#a855f7" : colors.textSecondary
							},
							children: [importancePercent, "%"]
						})]
					})]
				}), /* @__PURE__ */ jsx("div", {
					style: {
						display: "flex",
						gap: spacing.xs
					},
					children: injectable && /* @__PURE__ */ jsx("button", {
						onClick: (e) => {
							e.stopPropagation();
							onInject();
						},
						style: {
							padding: `${spacing.xs} ${spacing.sm}`,
							background: "#a855f720",
							border: `1px solid #a855f740`,
							borderRadius: radii.sm,
							color: "#a855f7",
							fontFamily: fonts.mono,
							fontSize: "10px",
							cursor: "pointer",
							transition: transitions.colors
						},
						title: "Inject into context",
						children: "⇢ Inject"
					})
				})]
			}),
			/* @__PURE__ */ jsx("div", {
				style: {
					fontFamily: fonts.sans,
					fontSize: fontSizes.sm,
					color: colors.text,
					lineHeight: 1.5,
					overflow: "hidden",
					maxHeight: isExpanded ? "none" : "60px",
					textOverflow: isExpanded ? "clip" : "ellipsis"
				},
				children: memory.content
			}),
			memory.tags.length > 0 && /* @__PURE__ */ jsx("div", {
				style: {
					marginTop: spacing.sm,
					display: "flex",
					gap: spacing.xs,
					flexWrap: "wrap"
				},
				children: memory.tags.map((tag) => /* @__PURE__ */ jsxs("span", {
					style: {
						padding: `1px ${spacing.xs}`,
						background: colors.surfaceElevated,
						borderRadius: radii.sm,
						fontFamily: fonts.mono,
						fontSize: "9px",
						color: colors.textMuted
					},
					children: ["#", tag]
				}, tag))
			}),
			/* @__PURE__ */ jsxs("div", {
				style: {
					marginTop: spacing.sm,
					fontFamily: fonts.mono,
					fontSize: "10px",
					color: colors.textMuted
				},
				children: [
					memory.createdAt.toLocaleDateString(),
					" ",
					memory.createdAt.toLocaleTimeString()
				]
			})
		]
	});
}
function MemoryTimeline({ memories, injectable, onMemoryClick, onInject }) {
	const grouped = useMemo(() => {
		const groups = {};
		memories.forEach((m) => {
			const date = m.createdAt.toLocaleDateString();
			if (!groups[date]) groups[date] = [];
			groups[date].push(m);
		});
		return groups;
	}, [memories]);
	return /* @__PURE__ */ jsxs("div", {
		style: {
			position: "relative",
			paddingLeft: spacing.lg
		},
		children: [/* @__PURE__ */ jsx("div", { style: {
			position: "absolute",
			left: "6px",
			top: 0,
			bottom: 0,
			width: "2px",
			background: `linear-gradient(180deg, #a855f7 0%, ${colors.border} 100%)`
		} }), Object.entries(grouped).map(([date, mems]) => /* @__PURE__ */ jsxs("div", {
			style: { marginBottom: spacing.lg },
			children: [/* @__PURE__ */ jsxs("div", {
				style: {
					display: "flex",
					alignItems: "center",
					gap: spacing.sm,
					marginBottom: spacing.sm,
					marginLeft: `-${spacing.lg}`
				},
				children: [/* @__PURE__ */ jsx("div", { style: {
					width: "12px",
					height: "12px",
					borderRadius: "50%",
					background: "#a855f7",
					border: `2px solid ${colors.void}`
				} }), /* @__PURE__ */ jsx("span", {
					style: {
						fontFamily: fonts.mono,
						fontSize: fontSizes.xs,
						fontWeight: fontWeights.semibold,
						color: "#a855f7"
					},
					children: date
				})]
			}), /* @__PURE__ */ jsx("div", {
				style: {
					display: "flex",
					flexDirection: "column",
					gap: spacing.xs
				},
				children: mems.map((memory) => /* @__PURE__ */ jsxs("div", {
					onClick: () => onMemoryClick?.(memory),
					style: {
						padding: spacing.sm,
						background: colors.surface,
						borderRadius: radii.sm,
						cursor: "pointer",
						transition: transitions.colors
					},
					children: [/* @__PURE__ */ jsx("div", {
						style: {
							fontFamily: fonts.sans,
							fontSize: fontSizes.sm,
							color: colors.text,
							overflow: "hidden",
							textOverflow: "ellipsis",
							whiteSpace: "nowrap"
						},
						children: memory.content
					}), /* @__PURE__ */ jsx("div", {
						style: {
							fontFamily: fonts.mono,
							fontSize: "10px",
							color: colors.textMuted,
							marginTop: "4px"
						},
						children: memory.createdAt.toLocaleTimeString()
					})]
				}, memory.id))
			})]
		}, date))]
	});
}
function MemoryTagCloud({ memories, allTags, selectedTags, onTagSelect }) {
	const tagCounts = useMemo(() => {
		const counts = {};
		memories.forEach((m) => m.tags.forEach((t) => counts[t] = (counts[t] || 0) + 1));
		return counts;
	}, [memories]);
	const maxCount = Math.max(...Object.values(tagCounts), 1);
	return /* @__PURE__ */ jsx("div", {
		style: {
			display: "flex",
			flexWrap: "wrap",
			gap: spacing.sm,
			justifyContent: "center",
			padding: spacing.md
		},
		children: allTags.map((tag) => {
			const count = tagCounts[tag] || 0;
			const scale = .7 + count / maxCount * .6;
			const isSelected = selectedTags.includes(tag);
			return /* @__PURE__ */ jsxs("button", {
				onClick: () => onTagSelect(tag),
				style: {
					padding: `${spacing.xs} ${spacing.md}`,
					background: isSelected ? "#a855f720" : colors.surface,
					border: `1px solid ${isSelected ? "#a855f7" : colors.border}`,
					borderRadius: radii.full,
					color: isSelected ? "#a855f7" : colors.text,
					fontFamily: fonts.mono,
					fontSize: `${scale}rem`,
					fontWeight: count > maxCount / 2 ? fontWeights.semibold : fontWeights.normal,
					cursor: "pointer",
					transition: transitions.all
				},
				children: [
					"#",
					tag,
					/* @__PURE__ */ jsx("span", {
						style: {
							marginLeft: spacing.xs,
							opacity: .5,
							fontSize: "0.8em"
						},
						children: count
					})
				]
			}, tag);
		})
	});
}
//#endregion
//#region organisms/ThinkingStateViewer.tsx
/**
* EITELKEIT ThinkingStateViewer
* ═══════════════════════════════════════════════════════════════════
* Extended thinking visualization with streaming support
* Chain-of-thought timeline with confidence indicators
*/
const blockTypeConfig = {
	reasoning: {
		label: "Reasoning",
		icon: "💭",
		color: colors.mint
	},
	planning: {
		label: "Planning",
		icon: "📋",
		color: colors.info
	},
	analysis: {
		label: "Analysis",
		icon: "🔬",
		color: "#a855f7"
	},
	conclusion: {
		label: "Conclusion",
		icon: "✓",
		color: colors.success
	},
	uncertainty: {
		label: "Uncertainty",
		icon: "❓",
		color: colors.warning
	},
	revision: {
		label: "Revision",
		icon: "↻",
		color: colors.error
	}
};
function ThinkingStateViewer({ state, streamingContent, expanded = true, showStats = true, scrubbable = false, compact = false, onBlockClick, style }) {
	const [isExpanded, setIsExpanded] = useState(expanded);
	const [selectedBlockIndex, setSelectedBlockIndex] = useState(null);
	const [scrubPosition, setScrubPosition] = useState(null);
	const containerRef = useRef(null);
	const endRef = useRef(null);
	useEffect(() => {
		if (state.status === "thinking" && endRef.current) endRef.current.scrollIntoView({ behavior: "smooth" });
	}, [
		state.blocks.length,
		streamingContent,
		state.status
	]);
	const avgConfidence = useMemo(() => {
		const withConfidence = state.blocks.filter((b) => b.confidence !== void 0);
		if (withConfidence.length === 0) return null;
		return withConfidence.reduce((sum, b) => sum + (b.confidence ?? 0), 0) / withConfidence.length;
	}, [state.blocks]);
	return /* @__PURE__ */ jsxs("div", {
		ref: containerRef,
		style: {
			display: "flex",
			flexDirection: "column",
			background: colors.voidDeep,
			borderRadius: radii.lg,
			overflow: "hidden",
			border: `1px solid ${state.status === "thinking" ? colors.mintGlow : colors.border}`,
			boxShadow: state.status === "thinking" ? shadows.mintGlow : "none",
			transition: transitions.all,
			...style
		},
		children: [
			/* @__PURE__ */ jsx(ThinkingHeader, {
				state,
				avgConfidence,
				showStats: showStats ?? true,
				isExpanded: isExpanded ?? true,
				compact: compact ?? false,
				onToggle: () => setIsExpanded(!isExpanded)
			}),
			scrubbable && isExpanded && state.blocks.length > 1 && /* @__PURE__ */ jsx(TimelineScrubber, {
				blocks: state.blocks,
				scrubPosition,
				onScrub: setScrubPosition
			}),
			isExpanded && /* @__PURE__ */ jsxs("div", {
				style: {
					flex: 1,
					overflowY: "auto",
					padding: compact ? spacing.sm : spacing.md
				},
				children: [
					state.blocks.map((block, index) => /* @__PURE__ */ jsx(ThinkingBlockItem, {
						block,
						index,
						total: state.blocks.length,
						isActive: state.currentBlock === block.id,
						isSelected: selectedBlockIndex === index,
						compact: compact ?? false,
						onClick: () => {
							setSelectedBlockIndex(selectedBlockIndex === index ? null : index);
							onBlockClick?.(block);
						}
					}, block.id)),
					state.status === "thinking" && streamingContent && /* @__PURE__ */ jsx(StreamingBlock, { content: streamingContent }),
					/* @__PURE__ */ jsx("div", { ref: endRef })
				]
			})
		]
	});
}
function ThinkingHeader({ state, avgConfidence, showStats, isExpanded, compact, onToggle }) {
	const statusColors = {
		idle: colors.textMuted,
		thinking: colors.mint,
		done: colors.success,
		error: colors.error
	};
	return /* @__PURE__ */ jsxs("div", {
		onClick: onToggle,
		style: {
			display: "flex",
			alignItems: "center",
			justifyContent: "space-between",
			padding: compact ? `${spacing.sm} ${spacing.md}` : spacing.md,
			background: colors.void,
			borderBottom: isExpanded ? `1px solid ${colors.border}` : "none",
			cursor: "pointer",
			transition: transitions.colors
		},
		children: [
			/* @__PURE__ */ jsxs("div", {
				style: {
					display: "flex",
					alignItems: "center",
					gap: spacing.sm
				},
				children: [/* @__PURE__ */ jsx("div", { style: {
					width: "12px",
					height: "12px",
					borderRadius: "50%",
					background: statusColors[state.status],
					boxShadow: state.status === "thinking" ? `0 0 10px ${colors.mintGlow}` : "none",
					animation: state.status === "thinking" ? "e-think-pulse 1.5s ease-in-out infinite" : "none"
				} }), /* @__PURE__ */ jsxs("div", { children: [/* @__PURE__ */ jsx("span", {
					style: {
						fontFamily: fonts.mono,
						fontSize: fontSizes.xs,
						fontWeight: fontWeights.semibold,
						color: colors.mint,
						textTransform: "uppercase",
						letterSpacing: letterSpacings.wider
					},
					children: state.agentName
				}), /* @__PURE__ */ jsx("span", {
					style: {
						fontFamily: fonts.mono,
						fontSize: fontSizes.xs,
						color: colors.textMuted,
						marginLeft: spacing.sm
					},
					children: state.status === "thinking" ? "Thinking..." : `${state.blocks.length} blocks`
				})] })]
			}),
			/* @__PURE__ */ jsxs("div", {
				style: {
					display: "flex",
					alignItems: "center",
					gap: spacing.md
				},
				children: [showStats && /* @__PURE__ */ jsxs(Fragment, { children: [
					avgConfidence !== null && /* @__PURE__ */ jsx(ConfidenceMeter, {
						value: avgConfidence,
						size: compact ? "sm" : "md"
					}),
					state.totalTokens && /* @__PURE__ */ jsxs("div", {
						style: {
							fontFamily: fonts.mono,
							fontSize: fontSizes.xs,
							color: colors.textMuted
						},
						children: [state.totalTokens.toLocaleString(), " tokens"]
					}),
					state.totalDuration && /* @__PURE__ */ jsxs("div", {
						style: {
							fontFamily: fonts.mono,
							fontSize: fontSizes.xs,
							color: colors.textMuted
						},
						children: [(state.totalDuration / 1e3).toFixed(1), "s"]
					})
				] }), /* @__PURE__ */ jsx("span", {
					style: {
						transform: isExpanded ? "rotate(180deg)" : "none",
						transition: transitions.transform,
						color: colors.textMuted
					},
					children: "▼"
				})]
			}),
			/* @__PURE__ */ jsx("style", { children: `
        @keyframes e-think-pulse {
          0%, 100% { opacity: 1; transform: scale(1); }
          50% { opacity: 0.5; transform: scale(1.2); }
        }
      ` })
		]
	});
}
function ConfidenceMeter({ value, size = "md" }) {
	const width = size === "sm" ? 60 : 80;
	const height = size === "sm" ? 6 : 8;
	const percentage = Math.round(value * 100);
	const getColor = (v) => {
		if (v > .8) return colors.mint;
		if (v > .6) return colors.info;
		if (v > .4) return colors.warning;
		return colors.error;
	};
	return /* @__PURE__ */ jsxs("div", {
		style: {
			display: "flex",
			alignItems: "center",
			gap: spacing.xs
		},
		children: [
			/* @__PURE__ */ jsx("span", {
				style: {
					fontFamily: fonts.mono,
					fontSize: size === "sm" ? "10px" : fontSizes.xs,
					color: colors.textMuted
				},
				children: "CONF"
			}),
			/* @__PURE__ */ jsx("div", {
				style: {
					width: `${width}px`,
					height: `${height}px`,
					background: colors.surface,
					borderRadius: radii.full,
					overflow: "hidden"
				},
				children: /* @__PURE__ */ jsx("div", { style: {
					width: `${percentage}%`,
					height: "100%",
					background: getColor(value),
					borderRadius: radii.full,
					transition: "width 0.3s ease"
				} })
			}),
			/* @__PURE__ */ jsxs("span", {
				style: {
					fontFamily: fonts.mono,
					fontSize: size === "sm" ? "10px" : fontSizes.xs,
					fontWeight: fontWeights.bold,
					color: getColor(value),
					minWidth: "30px"
				},
				children: [percentage, "%"]
			})
		]
	});
}
function TimelineScrubber({ blocks, scrubPosition, onScrub }) {
	const totalDuration = blocks.reduce((sum, b) => sum + (b.duration ?? 1e3), 0);
	return /* @__PURE__ */ jsx("div", {
		style: {
			padding: `${spacing.xs} ${spacing.md}`,
			background: colors.void,
			borderBottom: `1px solid ${colors.border}`
		},
		children: /* @__PURE__ */ jsx("div", {
			style: {
				display: "flex",
				height: "20px",
				background: colors.surface,
				borderRadius: radii.sm,
				overflow: "hidden",
				cursor: "pointer"
			},
			children: blocks.map((block, i) => {
				const config = blockTypeConfig[block.type];
				return /* @__PURE__ */ jsx("div", {
					onClick: () => onScrub(scrubPosition === i ? null : i),
					style: {
						width: `${(block.duration ?? 1e3) / totalDuration * 100}%`,
						height: "100%",
						background: scrubPosition === i ? config.color : `${config.color}40`,
						borderRight: i < blocks.length - 1 ? `1px solid ${colors.void}` : "none",
						transition: transitions.colors
					},
					title: `${config.label}: ${block.content.slice(0, 50)}...`
				}, block.id);
			})
		})
	});
}
function ThinkingBlockItem({ block, index, total, isActive, isSelected, compact, onClick }) {
	const config = blockTypeConfig[block.type];
	return /* @__PURE__ */ jsxs("div", {
		onClick,
		style: {
			display: "flex",
			gap: spacing.sm,
			padding: compact ? spacing.sm : spacing.md,
			marginBottom: spacing.xs,
			background: isActive ? `${config.color}10` : isSelected ? colors.surface : "transparent",
			borderRadius: radii.md,
			borderLeft: `3px solid ${config.color}`,
			cursor: "pointer",
			transition: transitions.colors,
			animation: isActive ? "e-block-glow 2s ease-in-out infinite" : "none"
		},
		children: [
			/* @__PURE__ */ jsxs("div", {
				style: {
					display: "flex",
					flexDirection: "column",
					alignItems: "center",
					gap: "4px"
				},
				children: [
					/* @__PURE__ */ jsxs("span", {
						style: {
							fontFamily: fonts.mono,
							fontSize: "10px",
							color: colors.textMuted
						},
						children: [
							index + 1,
							"/",
							total
						]
					}),
					/* @__PURE__ */ jsx("span", {
						style: { fontSize: compact ? "12px" : "16px" },
						children: config.icon
					}),
					/* @__PURE__ */ jsx("span", {
						style: {
							fontFamily: fonts.mono,
							fontSize: "9px",
							color: config.color,
							textTransform: "uppercase"
						},
						children: config.label
					})
				]
			}),
			/* @__PURE__ */ jsxs("div", {
				style: {
					flex: 1,
					minWidth: 0
				},
				children: [/* @__PURE__ */ jsx("div", {
					style: {
						fontFamily: fonts.sans,
						fontSize: compact ? fontSizes.sm : fontSizes.base,
						color: colors.text,
						lineHeight: 1.5,
						whiteSpace: "pre-wrap",
						overflow: "hidden",
						maxHeight: isSelected ? "none" : compact ? "40px" : "60px"
					},
					children: block.content
				}), /* @__PURE__ */ jsxs("div", {
					style: {
						display: "flex",
						gap: spacing.md,
						marginTop: spacing.xs,
						fontFamily: fonts.mono,
						fontSize: "10px",
						color: colors.textMuted
					},
					children: [
						block.confidence != null && /* @__PURE__ */ jsxs("span", { children: [
							"Conf: ",
							Math.round(block.confidence * 100),
							"%"
						] }),
						block.tokens && /* @__PURE__ */ jsxs("span", { children: [block.tokens, " tok"] }),
						block.duration && /* @__PURE__ */ jsxs("span", { children: [(block.duration / 1e3).toFixed(1), "s"] })
					]
				})]
			}),
			/* @__PURE__ */ jsx("style", { children: `
        @keyframes e-block-glow {
          0%, 100% { box-shadow: inset 0 0 0 rgba(57, 255, 159, 0); }
          50% { box-shadow: inset 0 0 20px rgba(57, 255, 159, 0.1); }
        }
      ` })
		]
	});
}
function StreamingBlock({ content }) {
	return /* @__PURE__ */ jsxs("div", {
		style: {
			padding: spacing.md,
			background: `${colors.mint}08`,
			borderRadius: radii.md,
			borderLeft: `3px solid ${colors.mint}`,
			animation: "e-stream-breathe 2s ease-in-out infinite"
		},
		children: [
			/* @__PURE__ */ jsxs("div", {
				style: {
					display: "flex",
					alignItems: "center",
					gap: spacing.sm,
					marginBottom: spacing.xs
				},
				children: [/* @__PURE__ */ jsx("div", { style: {
					width: "8px",
					height: "8px",
					borderRadius: "50%",
					background: colors.mint,
					animation: "e-think-pulse 1s ease-in-out infinite"
				} }), /* @__PURE__ */ jsx("span", {
					style: {
						fontFamily: fonts.mono,
						fontSize: fontSizes.xs,
						color: colors.mint,
						textTransform: "uppercase",
						letterSpacing: letterSpacings.wider
					},
					children: "Streaming..."
				})]
			}),
			/* @__PURE__ */ jsxs("div", {
				style: {
					fontFamily: fonts.sans,
					fontSize: fontSizes.base,
					color: colors.text,
					lineHeight: 1.5,
					whiteSpace: "pre-wrap"
				},
				children: [content, /* @__PURE__ */ jsx("span", { style: {
					display: "inline-block",
					width: "2px",
					height: "1em",
					background: colors.mint,
					marginLeft: "2px",
					animation: "e-cursor-blink 1s step-end infinite"
				} })]
			}),
			/* @__PURE__ */ jsx("style", { children: `
        @keyframes e-stream-breathe {
          0%, 100% { opacity: 0.9; }
          50% { opacity: 1; }
        }
        @keyframes e-cursor-blink {
          0%, 50% { opacity: 1; }
          51%, 100% { opacity: 0; }
        }
      ` })
		]
	});
}
//#endregion
//#region organisms/CouncilDeliberation.tsx
/**
* EITELKEIT CouncilDeliberation
* ═══════════════════════════════════════════════════════════════════
* Multi-agent deliberation with voting and consensus visualization
* Shows agent positions, arguments, and vote tallies
*/
const positionConfig = {
	support: {
		label: "Support",
		icon: "✓",
		color: colors.mint
	},
	oppose: {
		label: "Oppose",
		icon: "✗",
		color: colors.error
	},
	abstain: {
		label: "Abstain",
		icon: "○",
		color: colors.textMuted
	},
	undecided: {
		label: "Undecided",
		icon: "?",
		color: colors.warning
	}
};
const phaseConfig = {
	opening: {
		label: "Opening",
		icon: "📢",
		description: "Topic introduction"
	},
	arguments: {
		label: "Arguments",
		icon: "💬",
		description: "Present positions"
	},
	rebuttals: {
		label: "Rebuttals",
		icon: "⚔️",
		description: "Counter-arguments"
	},
	voting: {
		label: "Voting",
		icon: "🗳️",
		description: "Cast votes"
	},
	consensus: {
		label: "Consensus",
		icon: "🤝",
		description: "Building agreement"
	},
	closed: {
		label: "Closed",
		icon: "✓",
		description: "Decision made"
	}
};
function CouncilDeliberation({ deliberation, agents, canVote = false, canArgue = false, showConfidence = true, compact = false, onVote, onArgument, onAdvancePhase, style }) {
	const [newArgument, setNewArgument] = useState("");
	const [argumentType, setArgumentType] = useState("neutral");
	const [showVoteDialog, setShowVoteDialog] = useState(false);
	const voteTally = useMemo(() => {
		const tally = {
			support: 0,
			oppose: 0,
			abstain: 0,
			undecided: 0
		};
		deliberation.votes.forEach((v) => tally[v.position]++);
		return tally;
	}, [deliberation.votes]);
	const totalVotes = deliberation.votes.length;
	const supportPercentage = totalVotes > 0 ? voteTally.support / totalVotes * 100 : 0;
	const consensusReached = supportPercentage >= deliberation.consensusThreshold * 100;
	const handleSubmitArgument = useCallback(() => {
		if (!newArgument.trim()) return;
		onArgument?.(newArgument.trim(), argumentType);
		setNewArgument("");
	}, [
		newArgument,
		argumentType,
		onArgument
	]);
	return /* @__PURE__ */ jsxs("div", {
		style: {
			display: "flex",
			flexDirection: "column",
			height: "100%",
			background: colors.voidDeep,
			borderRadius: radii.lg,
			overflow: "hidden",
			...style
		},
		children: [
			/* @__PURE__ */ jsx(DeliberationHeader, {
				deliberation,
				compact: compact ?? false,
				onAdvancePhase
			}),
			/* @__PURE__ */ jsx(VoteSummary, {
				voteTally,
				totalVotes,
				totalAgents: agents.length,
				consensusThreshold: deliberation.consensusThreshold,
				consensusReached,
				compact: compact ?? false
			}),
			/* @__PURE__ */ jsx(AgentPositions, {
				agents,
				votes: deliberation.votes,
				showConfidence: showConfidence ?? true,
				compact: compact ?? false
			}),
			/* @__PURE__ */ jsxs("div", {
				style: {
					flex: 1,
					overflowY: "auto",
					padding: compact ? spacing.sm : spacing.md
				},
				children: [
					/* @__PURE__ */ jsxs("div", {
						style: {
							fontFamily: fonts.mono,
							fontSize: fontSizes.xs,
							color: colors.textMuted,
							textTransform: "uppercase",
							letterSpacing: letterSpacings.wider,
							marginBottom: spacing.sm
						},
						children: [
							"Arguments (",
							deliberation.arguments.length,
							")"
						]
					}),
					deliberation.arguments.map((arg) => {
						const agent = agents.find((a) => a.id === arg.agentId);
						return /* @__PURE__ */ jsx(ArgumentCard, {
							argument: arg,
							agentName: agent?.name ?? "Unknown",
							agentColor: agent?.color ?? null,
							compact: compact ?? false
						}, arg.id);
					}),
					deliberation.arguments.length === 0 && /* @__PURE__ */ jsx("div", {
						style: {
							padding: spacing.xl,
							textAlign: "center",
							color: colors.textMuted,
							fontFamily: fonts.sans,
							fontSize: fontSizes.sm
						},
						children: "No arguments yet. Be the first to share your position."
					})
				]
			}),
			canArgue && ["arguments", "rebuttals"].includes(deliberation.phase) && /* @__PURE__ */ jsx(ArgumentInput, {
				value: newArgument,
				type: argumentType,
				onChange: setNewArgument,
				onTypeChange: setArgumentType,
				onSubmit: handleSubmitArgument,
				compact: compact ?? false
			}),
			canVote && deliberation.phase === "voting" && /* @__PURE__ */ jsx("div", {
				style: {
					padding: spacing.md,
					borderTop: `1px solid ${colors.border}`,
					background: colors.void
				},
				children: /* @__PURE__ */ jsx("button", {
					onClick: () => setShowVoteDialog(true),
					style: {
						width: "100%",
						padding: spacing.md,
						background: colors.mint,
						color: colors.void,
						border: "none",
						borderRadius: radii.md,
						fontFamily: fonts.mono,
						fontSize: fontSizes.sm,
						fontWeight: fontWeights.bold,
						textTransform: "uppercase",
						letterSpacing: letterSpacings.wide,
						cursor: "pointer",
						transition: transitions.all
					},
					children: "🗳️ Cast Your Vote"
				})
			}),
			showVoteDialog && /* @__PURE__ */ jsx(VoteDialog, {
				onVote: (position, reasoning) => {
					onVote?.(position, reasoning);
					setShowVoteDialog(false);
				},
				onClose: () => setShowVoteDialog(false)
			}),
			deliberation.phase === "closed" && deliberation.finalDecision && /* @__PURE__ */ jsx(ConsensusResult, {
				decision: deliberation.finalDecision,
				consensusReached: deliberation.consensusReached ?? false,
				supportPercentage
			})
		]
	});
}
function DeliberationHeader({ deliberation, compact, onAdvancePhase }) {
	const phase = phaseConfig[deliberation.phase];
	return /* @__PURE__ */ jsxs("div", {
		style: {
			padding: compact ? `${spacing.sm} ${spacing.md}` : spacing.md,
			borderBottom: `1px solid ${colors.border}`,
			background: colors.void
		},
		children: [
			/* @__PURE__ */ jsxs("div", {
				style: {
					display: "flex",
					alignItems: "center",
					justifyContent: "space-between",
					marginBottom: spacing.sm
				},
				children: [/* @__PURE__ */ jsxs("div", {
					style: {
						display: "flex",
						alignItems: "center",
						gap: spacing.sm
					},
					children: [
						/* @__PURE__ */ jsx("span", {
							style: { fontSize: "16px" },
							children: phase.icon
						}),
						/* @__PURE__ */ jsx("span", {
							style: {
								fontFamily: fonts.mono,
								fontSize: fontSizes.xs,
								fontWeight: fontWeights.semibold,
								color: colors.warning,
								textTransform: "uppercase",
								letterSpacing: letterSpacings.wider
							},
							children: phase.label
						}),
						/* @__PURE__ */ jsxs("span", {
							style: {
								fontFamily: fonts.mono,
								fontSize: fontSizes.xs,
								color: colors.textMuted
							},
							children: ["— ", phase.description]
						})
					]
				}), onAdvancePhase && deliberation.phase !== "closed" && /* @__PURE__ */ jsx("button", {
					onClick: onAdvancePhase,
					style: {
						padding: `${spacing.xs} ${spacing.sm}`,
						background: colors.surface,
						border: `1px solid ${colors.border}`,
						borderRadius: radii.sm,
						color: colors.textSecondary,
						fontFamily: fonts.mono,
						fontSize: fontSizes.xs,
						cursor: "pointer",
						transition: transitions.colors
					},
					children: "Next Phase →"
				})]
			}),
			/* @__PURE__ */ jsx("div", {
				style: {
					fontFamily: fonts.sans,
					fontSize: compact ? fontSizes.base : fontSizes.lg,
					fontWeight: fontWeights.semibold,
					color: colors.text
				},
				children: deliberation.topic
			}),
			deliberation.description && /* @__PURE__ */ jsx("div", {
				style: {
					marginTop: spacing.xs,
					fontFamily: fonts.sans,
					fontSize: fontSizes.sm,
					color: colors.textSecondary
				},
				children: deliberation.description
			})
		]
	});
}
function VoteSummary({ voteTally, totalVotes, totalAgents, consensusThreshold, consensusReached, compact }) {
	const supportPct = totalVotes > 0 ? voteTally.support / totalVotes * 100 : 0;
	const opposePct = totalVotes > 0 ? voteTally.oppose / totalVotes * 100 : 0;
	return /* @__PURE__ */ jsxs("div", {
		style: {
			padding: compact ? spacing.sm : spacing.md,
			background: colors.surface,
			borderBottom: `1px solid ${colors.border}`
		},
		children: [
			/* @__PURE__ */ jsxs("div", {
				style: {
					height: "24px",
					display: "flex",
					borderRadius: radii.md,
					overflow: "hidden",
					marginBottom: spacing.sm
				},
				children: [
					/* @__PURE__ */ jsx("div", {
						style: {
							width: `${supportPct}%`,
							background: colors.mint,
							transition: "width 0.5s ease",
							display: "flex",
							alignItems: "center",
							justifyContent: "center"
						},
						children: supportPct > 15 && /* @__PURE__ */ jsxs("span", {
							style: {
								fontFamily: fonts.mono,
								fontSize: "10px",
								color: colors.void
							},
							children: [Math.round(supportPct), "%"]
						})
					}),
					/* @__PURE__ */ jsx("div", {
						style: {
							width: `${opposePct}%`,
							background: colors.error,
							transition: "width 0.5s ease",
							display: "flex",
							alignItems: "center",
							justifyContent: "center"
						},
						children: opposePct > 15 && /* @__PURE__ */ jsxs("span", {
							style: {
								fontFamily: fonts.mono,
								fontSize: "10px",
								color: colors.text
							},
							children: [Math.round(opposePct), "%"]
						})
					}),
					/* @__PURE__ */ jsx("div", { style: {
						flex: 1,
						background: colors.surfaceElevated
					} })
				]
			}),
			/* @__PURE__ */ jsx("div", {
				style: {
					position: "relative",
					height: "2px",
					marginBottom: spacing.sm
				},
				children: /* @__PURE__ */ jsxs("div", {
					style: {
						position: "absolute",
						left: `${consensusThreshold * 100}%`,
						top: "-10px",
						transform: "translateX(-50%)",
						display: "flex",
						flexDirection: "column",
						alignItems: "center"
					},
					children: [/* @__PURE__ */ jsx("div", { style: {
						width: "2px",
						height: "8px",
						background: colors.warning
					} }), /* @__PURE__ */ jsxs("span", {
						style: {
							fontFamily: fonts.mono,
							fontSize: "9px",
							color: colors.warning,
							whiteSpace: "nowrap"
						},
						children: [consensusThreshold * 100, "% threshold"]
					})]
				})
			}),
			/* @__PURE__ */ jsxs("div", {
				style: {
					display: "flex",
					justifyContent: "space-between",
					alignItems: "center"
				},
				children: [
					/* @__PURE__ */ jsx("div", {
						style: {
							display: "flex",
							gap: spacing.lg
						},
						children: [
							"support",
							"oppose",
							"abstain"
						].map((pos) => {
							const cfg = positionConfig[pos];
							return /* @__PURE__ */ jsxs("div", {
								style: {
									display: "flex",
									alignItems: "center",
									gap: spacing.xs
								},
								children: [/* @__PURE__ */ jsx("span", {
									style: { color: cfg.color },
									children: cfg.icon
								}), /* @__PURE__ */ jsx("span", {
									style: {
										fontFamily: fonts.mono,
										fontSize: fontSizes.sm,
										color: cfg.color
									},
									children: voteTally[pos]
								})]
							}, pos);
						})
					}),
					/* @__PURE__ */ jsxs("div", {
						style: {
							fontFamily: fonts.mono,
							fontSize: fontSizes.xs,
							color: colors.textMuted
						},
						children: [
							totalVotes,
							"/",
							totalAgents,
							" voted"
						]
					}),
					consensusReached && /* @__PURE__ */ jsx("div", {
						style: {
							padding: `${spacing.xs} ${spacing.sm}`,
							background: `${colors.mint}20`,
							border: `1px solid ${colors.mint}`,
							borderRadius: radii.full,
							fontFamily: fonts.mono,
							fontSize: "10px",
							fontWeight: fontWeights.bold,
							color: colors.mint,
							textTransform: "uppercase"
						},
						children: "🤝 Consensus"
					})
				]
			})
		]
	});
}
function AgentPositions({ agents, votes, showConfidence, compact }) {
	return /* @__PURE__ */ jsx("div", {
		style: {
			padding: compact ? spacing.sm : spacing.md,
			borderBottom: `1px solid ${colors.border}`,
			display: "flex",
			gap: spacing.sm,
			flexWrap: "wrap"
		},
		children: agents.map((agent) => {
			const vote = votes.find((v) => v.agentId === agent.id);
			const cfg = positionConfig[vote?.position ?? "undecided"];
			return /* @__PURE__ */ jsxs("div", {
				style: {
					display: "flex",
					alignItems: "center",
					gap: spacing.xs,
					padding: `${spacing.xs} ${spacing.sm}`,
					background: `${cfg.color}15`,
					border: `1px solid ${cfg.color}40`,
					borderRadius: radii.full
				},
				title: vote?.reasoning ?? "No vote yet",
				children: [
					/* @__PURE__ */ jsx("div", {
						style: {
							width: "16px",
							height: "16px",
							borderRadius: "50%",
							background: agent.color ?? colors.mint,
							display: "flex",
							alignItems: "center",
							justifyContent: "center",
							fontSize: "8px",
							color: colors.void
						},
						children: agent.name.charAt(0)
					}),
					/* @__PURE__ */ jsx("span", {
						style: {
							fontFamily: fonts.sans,
							fontSize: fontSizes.xs,
							color: colors.text
						},
						children: agent.name
					}),
					/* @__PURE__ */ jsx("span", {
						style: {
							color: cfg.color,
							fontSize: "12px"
						},
						children: cfg.icon
					}),
					showConfidence && vote && /* @__PURE__ */ jsxs("span", {
						style: {
							fontFamily: fonts.mono,
							fontSize: "10px",
							color: colors.textMuted
						},
						children: [Math.round(vote.confidence * 100), "%"]
					})
				]
			}, agent.id);
		})
	});
}
function ArgumentCard({ argument, agentName, agentColor, compact }) {
	const cfg = {
		pro: {
			label: "PRO",
			color: colors.mint
		},
		con: {
			label: "CON",
			color: colors.error
		},
		neutral: {
			label: "INFO",
			color: colors.textMuted
		},
		question: {
			label: "?",
			color: colors.warning
		}
	}[argument.type];
	return /* @__PURE__ */ jsxs("div", {
		style: {
			padding: compact ? spacing.sm : spacing.md,
			marginBottom: spacing.sm,
			background: colors.surface,
			borderRadius: radii.md,
			borderLeft: `3px solid ${cfg.color}`
		},
		children: [/* @__PURE__ */ jsxs("div", {
			style: {
				display: "flex",
				alignItems: "center",
				gap: spacing.sm,
				marginBottom: spacing.xs
			},
			children: [
				/* @__PURE__ */ jsx("span", {
					style: {
						padding: `1px ${spacing.xs}`,
						background: `${cfg.color}20`,
						borderRadius: radii.sm,
						fontFamily: fonts.mono,
						fontSize: "9px",
						fontWeight: fontWeights.bold,
						color: cfg.color
					},
					children: cfg.label
				}),
				/* @__PURE__ */ jsx("span", {
					style: {
						fontFamily: fonts.mono,
						fontSize: fontSizes.xs,
						fontWeight: fontWeights.semibold,
						color: agentColor ?? colors.mint
					},
					children: agentName
				}),
				/* @__PURE__ */ jsx("span", {
					style: {
						fontFamily: fonts.mono,
						fontSize: "10px",
						color: colors.textMuted
					},
					children: argument.timestamp.toLocaleTimeString()
				})
			]
		}), /* @__PURE__ */ jsx("div", {
			style: {
				fontFamily: fonts.sans,
				fontSize: compact ? fontSizes.sm : fontSizes.base,
				color: colors.text,
				lineHeight: 1.5
			},
			children: argument.content
		})]
	});
}
function ArgumentInput({ value, type, onChange, onTypeChange, onSubmit, compact }) {
	return /* @__PURE__ */ jsxs("div", {
		style: {
			padding: spacing.md,
			borderTop: `1px solid ${colors.border}`,
			background: colors.void
		},
		children: [/* @__PURE__ */ jsx("div", {
			style: {
				display: "flex",
				gap: spacing.xs,
				marginBottom: spacing.sm
			},
			children: [
				"pro",
				"con",
				"neutral",
				"question"
			].map((t) => {
				const cfg = {
					pro: {
						label: "Pro",
						color: colors.mint
					},
					con: {
						label: "Con",
						color: colors.error
					},
					neutral: {
						label: "Info",
						color: colors.textMuted
					},
					question: {
						label: "Question",
						color: colors.warning
					}
				}[t];
				return /* @__PURE__ */ jsx("button", {
					onClick: () => onTypeChange(t),
					style: {
						padding: `${spacing.xs} ${spacing.sm}`,
						background: type === t ? `${cfg.color}20` : "transparent",
						border: `1px solid ${type === t ? cfg.color : colors.border}`,
						borderRadius: radii.sm,
						color: type === t ? cfg.color : colors.textMuted,
						fontFamily: fonts.mono,
						fontSize: fontSizes.xs,
						cursor: "pointer",
						transition: transitions.colors
					},
					children: cfg.label
				}, t);
			})
		}), /* @__PURE__ */ jsxs("div", {
			style: {
				display: "flex",
				gap: spacing.sm
			},
			children: [/* @__PURE__ */ jsx("textarea", {
				value,
				onChange: (e) => onChange(e.target.value),
				placeholder: "Add your argument...",
				rows: 2,
				style: {
					flex: 1,
					background: colors.surface,
					border: `1px solid ${colors.border}`,
					borderRadius: radii.md,
					padding: spacing.sm,
					color: colors.text,
					fontFamily: fonts.sans,
					fontSize: fontSizes.sm,
					resize: "none",
					outline: "none"
				}
			}), /* @__PURE__ */ jsx("button", {
				onClick: onSubmit,
				disabled: !value.trim(),
				style: {
					padding: `${spacing.sm} ${spacing.md}`,
					background: value.trim() ? colors.mint : colors.surface,
					color: value.trim() ? colors.void : colors.textMuted,
					border: "none",
					borderRadius: radii.md,
					fontFamily: fonts.mono,
					fontSize: fontSizes.xs,
					fontWeight: fontWeights.bold,
					cursor: value.trim() ? "pointer" : "not-allowed",
					transition: transitions.all
				},
				children: "Submit"
			})]
		})]
	});
}
function VoteDialog({ onVote, onClose }) {
	const [position, setPosition] = useState(null);
	const [reasoning, setReasoning] = useState("");
	return /* @__PURE__ */ jsx("div", {
		style: {
			position: "fixed",
			inset: 0,
			background: "rgba(0, 0, 0, 0.8)",
			display: "flex",
			alignItems: "center",
			justifyContent: "center",
			zIndex: 1e3
		},
		onClick: onClose,
		children: /* @__PURE__ */ jsxs("div", {
			onClick: (e) => e.stopPropagation(),
			style: {
				width: "400px",
				background: colors.surface,
				borderRadius: radii.lg,
				padding: spacing.lg,
				boxShadow: shadows.lg
			},
			children: [
				/* @__PURE__ */ jsx("div", {
					style: {
						fontFamily: fonts.mono,
						fontSize: fontSizes.sm,
						fontWeight: fontWeights.semibold,
						color: colors.mint,
						textTransform: "uppercase",
						letterSpacing: letterSpacings.wider,
						marginBottom: spacing.md
					},
					children: "Cast Your Vote"
				}),
				/* @__PURE__ */ jsx("div", {
					style: {
						display: "flex",
						gap: spacing.sm,
						marginBottom: spacing.md
					},
					children: [
						"support",
						"oppose",
						"abstain"
					].map((pos) => {
						const cfg = positionConfig[pos];
						return /* @__PURE__ */ jsxs("button", {
							onClick: () => setPosition(pos),
							style: {
								flex: 1,
								padding: spacing.md,
								background: position === pos ? `${cfg.color}20` : colors.surfaceElevated,
								border: `2px solid ${position === pos ? cfg.color : colors.border}`,
								borderRadius: radii.md,
								color: position === pos ? cfg.color : colors.textSecondary,
								fontFamily: fonts.mono,
								fontSize: fontSizes.sm,
								fontWeight: fontWeights.semibold,
								cursor: "pointer",
								transition: transitions.all
							},
							children: [/* @__PURE__ */ jsx("div", {
								style: {
									fontSize: "20px",
									marginBottom: spacing.xs
								},
								children: cfg.icon
							}), cfg.label]
						}, pos);
					})
				}),
				/* @__PURE__ */ jsx("textarea", {
					value: reasoning,
					onChange: (e) => setReasoning(e.target.value),
					placeholder: "Explain your reasoning (optional)...",
					rows: 3,
					style: {
						width: "100%",
						background: colors.void,
						border: `1px solid ${colors.border}`,
						borderRadius: radii.md,
						padding: spacing.sm,
						color: colors.text,
						fontFamily: fonts.sans,
						fontSize: fontSizes.sm,
						resize: "none",
						outline: "none",
						marginBottom: spacing.md
					}
				}),
				/* @__PURE__ */ jsxs("div", {
					style: {
						display: "flex",
						gap: spacing.sm,
						justifyContent: "flex-end"
					},
					children: [/* @__PURE__ */ jsx("button", {
						onClick: onClose,
						style: {
							padding: `${spacing.sm} ${spacing.lg}`,
							background: "transparent",
							border: `1px solid ${colors.border}`,
							borderRadius: radii.md,
							color: colors.textSecondary,
							fontFamily: fonts.sans,
							fontSize: fontSizes.sm,
							cursor: "pointer"
						},
						children: "Cancel"
					}), /* @__PURE__ */ jsx("button", {
						onClick: () => position && onVote(position, reasoning || null),
						disabled: !position,
						style: {
							padding: `${spacing.sm} ${spacing.lg}`,
							background: position ? colors.mint : colors.surface,
							color: position ? colors.void : colors.textMuted,
							border: "none",
							borderRadius: radii.md,
							fontFamily: fonts.mono,
							fontSize: fontSizes.sm,
							fontWeight: fontWeights.bold,
							cursor: position ? "pointer" : "not-allowed"
						},
						children: "Confirm Vote"
					})]
				})
			]
		})
	});
}
function ConsensusResult({ decision, consensusReached, supportPercentage }) {
	return /* @__PURE__ */ jsxs("div", {
		style: {
			padding: spacing.lg,
			background: consensusReached ? `${colors.mint}10` : `${colors.error}10`,
			borderTop: `2px solid ${consensusReached ? colors.mint : colors.error}`
		},
		children: [/* @__PURE__ */ jsxs("div", {
			style: {
				display: "flex",
				alignItems: "center",
				gap: spacing.sm,
				marginBottom: spacing.sm
			},
			children: [
				/* @__PURE__ */ jsx("span", {
					style: { fontSize: "24px" },
					children: consensusReached ? "🤝" : "⚖️"
				}),
				/* @__PURE__ */ jsx("span", {
					style: {
						fontFamily: fonts.mono,
						fontSize: fontSizes.xs,
						fontWeight: fontWeights.bold,
						color: consensusReached ? colors.mint : colors.error,
						textTransform: "uppercase",
						letterSpacing: letterSpacings.widest
					},
					children: consensusReached ? "Consensus Reached" : "No Consensus"
				}),
				/* @__PURE__ */ jsxs("span", {
					style: {
						fontFamily: fonts.mono,
						fontSize: fontSizes.xs,
						color: colors.textMuted
					},
					children: [
						"(",
						Math.round(supportPercentage),
						"% support)"
					]
				})
			]
		}), /* @__PURE__ */ jsx("div", {
			style: {
				fontFamily: fonts.sans,
				fontSize: fontSizes.base,
				color: colors.text,
				lineHeight: 1.5
			},
			children: decision
		})]
	});
}
//#endregion
//#region organisms/ToolCallVisualizer.tsx
/**
* EITELKEIT ToolCallVisualizer
* ═══════════════════════════════════════════════════════════════════
* Visual representation of tool calls with input/output
* Supports streaming results and error states
*/
const statusConfig = {
	pending: {
		label: "Pending",
		icon: "⏳",
		color: colors.textMuted
	},
	running: {
		label: "Running",
		icon: "⚡",
		color: colors.info
	},
	success: {
		label: "Success",
		icon: "✓",
		color: colors.mint
	},
	error: {
		label: "Error",
		icon: "✗",
		color: colors.error
	},
	cancelled: {
		label: "Cancelled",
		icon: "⊘",
		color: colors.warning
	}
};
const toolCategories = {
	files_read: {
		icon: "📄",
		color: "#a855f7"
	},
	files_write: {
		icon: "✏️",
		color: "#a855f7"
	},
	files_list: {
		icon: "📁",
		color: "#a855f7"
	},
	files_delete: {
		icon: "🗑️",
		color: colors.error
	},
	code_search: {
		icon: "🔍",
		color: colors.info
	},
	code_index: {
		icon: "📚",
		color: colors.info
	},
	architect: {
		icon: "🏗️",
		color: colors.warning
	},
	memory_store: {
		icon: "💾",
		color: "#a855f7"
	},
	memory_search: {
		icon: "🧠",
		color: "#a855f7"
	},
	memory_get: {
		icon: "📖",
		color: "#a855f7"
	},
	git: {
		icon: "🌿",
		color: colors.mint
	},
	ai_chat: {
		icon: "💬",
		color: colors.mint
	},
	primus: {
		icon: "👁️",
		color: colors.warning
	},
	commands: {
		icon: "⌨️",
		color: colors.textSecondary
	},
	terminal: {
		icon: "🖥️",
		color: colors.textSecondary
	},
	default: {
		icon: "🔧",
		color: colors.textSecondary
	}
};
function getToolCategory(toolName) {
	if (toolCategories[toolName]) return toolCategories[toolName];
	for (const [prefix, config] of Object.entries(toolCategories)) if (toolName.startsWith(prefix)) return config;
	return toolCategories.default;
}
function ToolCallVisualizer({ calls, groupByAgent = false, expandAll = false, showTiming = true, compact = false, onCallClick, onRetry, style }) {
	const groupedCalls = useMemo(() => {
		if (!groupByAgent) return { ungrouped: calls };
		const groups = {};
		calls.forEach((call) => {
			const key = call.agentName ?? call.agentId ?? "Unknown";
			if (!groups[key]) groups[key] = [];
			groups[key].push(call);
		});
		return groups;
	}, [calls, groupByAgent]);
	const stats = useMemo(() => {
		return {
			running: calls.filter((c) => c.status === "running").length,
			success: calls.filter((c) => c.status === "success").length,
			errors: calls.filter((c) => c.status === "error").length,
			totalDuration: calls.reduce((sum, c) => sum + (c.duration ?? 0), 0),
			total: calls.length
		};
	}, [calls]);
	return /* @__PURE__ */ jsxs("div", {
		style: {
			display: "flex",
			flexDirection: "column",
			height: "100%",
			background: colors.voidDeep,
			borderRadius: radii.lg,
			overflow: "hidden",
			...style
		},
		children: [/* @__PURE__ */ jsx("div", {
			style: {
				padding: compact ? `${spacing.sm} ${spacing.md}` : spacing.md,
				borderBottom: `1px solid ${colors.border}`,
				background: colors.void
			},
			children: /* @__PURE__ */ jsxs("div", {
				style: {
					display: "flex",
					alignItems: "center",
					justifyContent: "space-between"
				},
				children: [/* @__PURE__ */ jsxs("div", {
					style: {
						display: "flex",
						alignItems: "center",
						gap: spacing.sm
					},
					children: [/* @__PURE__ */ jsx("span", {
						style: { fontSize: "16px" },
						children: "🔧"
					}), /* @__PURE__ */ jsx("span", {
						style: {
							fontFamily: fonts.mono,
							fontSize: fontSizes.xs,
							fontWeight: fontWeights.semibold,
							color: colors.info,
							textTransform: "uppercase",
							letterSpacing: letterSpacings.widest
						},
						children: "Tool Calls"
					})]
				}), /* @__PURE__ */ jsxs("div", {
					style: {
						display: "flex",
						gap: spacing.md
					},
					children: [
						stats.running > 0 && /* @__PURE__ */ jsx(StatBadge, {
							icon: "⚡",
							value: stats.running,
							label: "running",
							color: colors.info
						}),
						/* @__PURE__ */ jsx(StatBadge, {
							icon: "✓",
							value: stats.success,
							label: "success",
							color: colors.mint
						}),
						stats.errors > 0 && /* @__PURE__ */ jsx(StatBadge, {
							icon: "✗",
							value: stats.errors,
							label: "errors",
							color: colors.error
						}),
						showTiming && stats.totalDuration > 0 && /* @__PURE__ */ jsxs("span", {
							style: {
								fontFamily: fonts.mono,
								fontSize: fontSizes.xs,
								color: colors.textMuted
							},
							children: [(stats.totalDuration / 1e3).toFixed(1), "s total"]
						})
					]
				})]
			})
		}), /* @__PURE__ */ jsxs("div", {
			style: {
				flex: 1,
				overflowY: "auto",
				padding: compact ? spacing.sm : spacing.md
			},
			children: [Object.entries(groupedCalls).map(([group, groupCalls]) => /* @__PURE__ */ jsxs("div", { children: [groupByAgent && group !== "ungrouped" && /* @__PURE__ */ jsx("div", {
				style: {
					fontFamily: fonts.mono,
					fontSize: fontSizes.xs,
					color: colors.mint,
					textTransform: "uppercase",
					letterSpacing: letterSpacings.wider,
					marginBottom: spacing.sm,
					marginTop: spacing.md
				},
				children: group
			}), groupCalls.map((call) => /* @__PURE__ */ jsx(ToolCallCard, {
				call,
				expandAll: expandAll ?? false,
				showTiming: showTiming ?? true,
				compact: compact ?? false,
				onClick: () => onCallClick?.(call),
				onRetry: onRetry != null ? () => onRetry(call.id) : () => null
			}, call.id))] }, group)), calls.length === 0 && /* @__PURE__ */ jsx("div", {
				style: {
					padding: spacing.xl,
					textAlign: "center",
					color: colors.textMuted,
					fontFamily: fonts.sans,
					fontSize: fontSizes.sm
				},
				children: "No tool calls yet"
			})]
		})]
	});
}
function StatBadge({ icon, value, label, color }) {
	return /* @__PURE__ */ jsxs("div", {
		style: {
			display: "flex",
			alignItems: "center",
			gap: "4px"
		},
		title: `${value} ${label}`,
		children: [/* @__PURE__ */ jsx("span", {
			style: {
				color,
				fontSize: "12px"
			},
			children: icon
		}), /* @__PURE__ */ jsx("span", {
			style: {
				fontFamily: fonts.mono,
				fontSize: fontSizes.xs,
				fontWeight: fontWeights.bold,
				color
			},
			children: value
		})]
	});
}
function ToolCallCard({ call, expandAll, showTiming, compact, onClick, onRetry }) {
	const [isExpanded, setIsExpanded] = useState(expandAll);
	const status = statusConfig[call.status];
	const category = getToolCategory(call.name);
	return /* @__PURE__ */ jsxs("div", {
		style: {
			marginBottom: spacing.sm,
			background: colors.surface,
			borderRadius: radii.md,
			border: `1px solid ${call.status === "running" ? colors.info : colors.border}`,
			overflow: "hidden",
			transition: transitions.all,
			boxShadow: call.status === "running" ? `0 0 10px ${colors.infoGlow}` : "none"
		},
		children: [
			/* @__PURE__ */ jsxs("div", {
				onClick: () => {
					setIsExpanded(!isExpanded);
					onClick?.();
				},
				style: {
					display: "flex",
					alignItems: "center",
					gap: spacing.sm,
					padding: compact ? spacing.sm : spacing.md,
					cursor: "pointer",
					transition: transitions.colors
				},
				children: [
					/* @__PURE__ */ jsx("span", {
						style: { fontSize: compact ? "14px" : "16px" },
						children: category.icon
					}),
					/* @__PURE__ */ jsxs("div", {
						style: {
							flex: 1,
							minWidth: 0
						},
						children: [/* @__PURE__ */ jsx("div", {
							style: {
								fontFamily: fonts.mono,
								fontSize: compact ? fontSizes.sm : fontSizes.base,
								fontWeight: fontWeights.medium,
								color: category.color,
								overflow: "hidden",
								textOverflow: "ellipsis",
								whiteSpace: "nowrap"
							},
							children: call.name
						}), call.agentName && /* @__PURE__ */ jsxs("div", {
							style: {
								fontFamily: fonts.mono,
								fontSize: "10px",
								color: colors.textMuted
							},
							children: ["via ", call.agentName]
						})]
					}),
					showTiming && call.duration && /* @__PURE__ */ jsxs("span", {
						style: {
							fontFamily: fonts.mono,
							fontSize: fontSizes.xs,
							color: colors.textMuted
						},
						children: [call.duration, "ms"]
					}),
					/* @__PURE__ */ jsxs("div", {
						style: {
							display: "flex",
							alignItems: "center",
							gap: "4px",
							padding: `2px ${spacing.xs}`,
							background: `${status.color}20`,
							borderRadius: radii.sm
						},
						children: [/* @__PURE__ */ jsx("span", {
							style: {
								fontSize: "10px",
								animation: call.status === "running" ? "e-tool-pulse 1s ease-in-out infinite" : "none"
							},
							children: status.icon
						}), /* @__PURE__ */ jsx("span", {
							style: {
								fontFamily: fonts.mono,
								fontSize: "10px",
								fontWeight: fontWeights.bold,
								color: status.color,
								textTransform: "uppercase"
							},
							children: status.label
						})]
					}),
					/* @__PURE__ */ jsx("span", {
						style: {
							transform: isExpanded ? "rotate(180deg)" : "none",
							transition: transitions.transform,
							color: colors.textMuted,
							fontSize: "10px"
						},
						children: "▼"
					})
				]
			}),
			isExpanded && /* @__PURE__ */ jsxs("div", {
				style: {
					borderTop: `1px solid ${colors.border}`,
					padding: compact ? spacing.sm : spacing.md
				},
				children: [
					/* @__PURE__ */ jsxs("div", {
						style: { marginBottom: spacing.md },
						children: [/* @__PURE__ */ jsx("div", {
							style: {
								fontFamily: fonts.mono,
								fontSize: fontSizes.xs,
								color: colors.textMuted,
								textTransform: "uppercase",
								letterSpacing: letterSpacings.wider,
								marginBottom: spacing.xs
							},
							children: "Input"
						}), /* @__PURE__ */ jsx("pre", {
							style: {
								margin: 0,
								padding: spacing.sm,
								background: colors.void,
								borderRadius: radii.sm,
								fontFamily: fonts.mono,
								fontSize: fontSizes.sm,
								color: colors.text,
								overflow: "auto",
								maxHeight: "150px"
							},
							children: JSON.stringify(call.input, null, 2)
						})]
					}),
					(call.output !== void 0 || call.error) && /* @__PURE__ */ jsxs("div", { children: [/* @__PURE__ */ jsx("div", {
						style: {
							fontFamily: fonts.mono,
							fontSize: fontSizes.xs,
							color: call.error ? colors.error : colors.textMuted,
							textTransform: "uppercase",
							letterSpacing: letterSpacings.wider,
							marginBottom: spacing.xs
						},
						children: call.error ? "Error" : "Output"
					}), /* @__PURE__ */ jsx("pre", {
						style: {
							margin: 0,
							padding: spacing.sm,
							background: call.error ? `${colors.error}10` : colors.void,
							borderRadius: radii.sm,
							border: call.error ? `1px solid ${colors.error}40` : "none",
							fontFamily: fonts.mono,
							fontSize: fontSizes.sm,
							color: call.error ? colors.error : colors.text,
							overflow: "auto",
							maxHeight: "200px",
							whiteSpace: "pre-wrap",
							wordBreak: "break-word"
						},
						children: call.error ?? JSON.stringify(call.output, null, 2)
					})] }),
					call.status === "error" && onRetry && /* @__PURE__ */ jsx("button", {
						onClick: (e) => {
							e.stopPropagation();
							onRetry();
						},
						style: {
							marginTop: spacing.sm,
							padding: `${spacing.xs} ${spacing.sm}`,
							background: colors.error,
							color: colors.text,
							border: "none",
							borderRadius: radii.sm,
							fontFamily: fonts.mono,
							fontSize: fontSizes.xs,
							cursor: "pointer",
							transition: transitions.colors
						},
						children: "↻ Retry"
					})
				]
			}),
			/* @__PURE__ */ jsx("style", { children: `
        @keyframes e-tool-pulse {
          0%, 100% { opacity: 1; }
          50% { opacity: 0.5; }
        }
      ` })
		]
	});
}
//#endregion
//#region organisms/ShareDialog.tsx
/**
* EITELKEIT ShareDialog
* ═══════════════════════════════════════════════════════════════════
* Modal for managing share links and project members
*/
const LinkIcon = () => /* @__PURE__ */ jsxs("svg", {
	width: "16",
	height: "16",
	viewBox: "0 0 16 16",
	fill: "none",
	stroke: "currentColor",
	strokeWidth: "1.5",
	strokeLinecap: "round",
	strokeLinejoin: "round",
	children: [/* @__PURE__ */ jsx("path", { d: "M6.5 9.5a3.5 3.5 0 0 0 5 0l2-2a3.5 3.5 0 0 0-5-5l-.5.5" }), /* @__PURE__ */ jsx("path", { d: "M9.5 6.5a3.5 3.5 0 0 0-5 0l-2 2a3.5 3.5 0 0 0 5 5l.5-.5" })]
});
const CopyIcon = () => /* @__PURE__ */ jsxs("svg", {
	width: "14",
	height: "14",
	viewBox: "0 0 14 14",
	fill: "none",
	stroke: "currentColor",
	strokeWidth: "1.5",
	strokeLinecap: "round",
	strokeLinejoin: "round",
	children: [/* @__PURE__ */ jsx("rect", {
		x: "4.5",
		y: "4.5",
		width: "7",
		height: "7",
		rx: "1"
	}), /* @__PURE__ */ jsx("path", { d: "M2.5 9.5V3a.5.5 0 0 1 .5-.5h6.5" })]
});
const TrashIcon = () => /* @__PURE__ */ jsx("svg", {
	width: "14",
	height: "14",
	viewBox: "0 0 14 14",
	fill: "none",
	stroke: "currentColor",
	strokeWidth: "1.5",
	strokeLinecap: "round",
	strokeLinejoin: "round",
	children: /* @__PURE__ */ jsx("path", { d: "M2 4h10M5 4V3a1 1 0 0 1 1-1h2a1 1 0 0 1 1 1v1M11 4v8a1 1 0 0 1-1 1H4a1 1 0 0 1-1-1V4" })
});
const PlusIcon = () => /* @__PURE__ */ jsxs("svg", {
	width: "14",
	height: "14",
	viewBox: "0 0 14 14",
	fill: "none",
	stroke: "currentColor",
	strokeWidth: "2",
	strokeLinecap: "round",
	strokeLinejoin: "round",
	children: [/* @__PURE__ */ jsx("line", {
		x1: "7",
		y1: "3",
		x2: "7",
		y2: "11"
	}), /* @__PURE__ */ jsx("line", {
		x1: "3",
		y1: "7",
		x2: "11",
		y2: "7"
	})]
});
const SendIcon$1 = () => /* @__PURE__ */ jsx("svg", {
	width: "14",
	height: "14",
	viewBox: "0 0 14 14",
	fill: "none",
	stroke: "currentColor",
	strokeWidth: "1.5",
	strokeLinecap: "round",
	strokeLinejoin: "round",
	children: /* @__PURE__ */ jsx("path", { d: "M12 2L2 6l4 2 2 4 4-10z" })
});
const roleLabels = {
	owner: "Owner",
	admin: "Admin",
	editor: "Editor",
	viewer: "Viewer"
};
const roleBadgeVariants = {
	owner: "vip",
	admin: "mod",
	editor: "sub",
	viewer: "muted"
};
const permissionLabels = {
	view: "View",
	edit: "Edit",
	admin: "Admin"
};
const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
function validateEmail(email) {
	return EMAIL_REGEX.test(email);
}
function formatDate(isoString) {
	return new Date(isoString).toLocaleDateString(void 0, {
		month: "short",
		day: "numeric",
		year: "numeric"
	});
}
function formatExpiry(isoString) {
	if (!isoString) return "Never expires";
	if (new Date(isoString) < /* @__PURE__ */ new Date()) return "Expired";
	return `Expires ${formatDate(isoString)}`;
}
const sectionStyle = { marginBottom: spacing.lg };
const sectionTitleStyle = {
	fontFamily: fonts.sans,
	fontSize: fontSizes.sm,
	fontWeight: fontWeights.semibold,
	color: colors.textMuted,
	textTransform: "uppercase",
	letterSpacing: "0.05em",
	marginBottom: spacing.md
};
const listStyle = {
	display: "flex",
	flexDirection: "column",
	gap: spacing.sm
};
const linkInfoStyle = {
	display: "flex",
	alignItems: "center",
	gap: spacing.md
};
const linkTextStyle = {
	display: "flex",
	flexDirection: "column",
	gap: "2px"
};
const linkTokenStyle = {
	fontFamily: fonts.mono,
	fontSize: fontSizes.sm,
	color: colors.textPrimary
};
const linkMetaStyle = {
	fontFamily: fonts.sans,
	fontSize: fontSizes.xs,
	color: colors.textMuted
};
const actionsStyle$1 = {
	display: "flex",
	alignItems: "center",
	gap: spacing.xs
};
const memberInfoStyle = {
	display: "flex",
	alignItems: "center",
	gap: spacing.md
};
const memberTextStyle = {
	display: "flex",
	flexDirection: "column",
	gap: "2px"
};
const memberNameStyle = {
	fontFamily: fonts.sans,
	fontSize: fontSizes.base,
	fontWeight: fontWeights.medium,
	color: colors.textPrimary
};
const memberEmailStyle = {
	fontFamily: fonts.sans,
	fontSize: fontSizes.sm,
	color: colors.textMuted
};
const createRowStyle = {
	display: "flex",
	alignItems: "center",
	gap: spacing.sm,
	marginBottom: spacing.lg
};
const baseSelectStyle = {
	fontFamily: fonts.sans,
	fontSize: fontSizes.sm,
	padding: `${spacing.sm} ${spacing.md}`,
	background: colors.surface,
	border: `1px solid ${colors.border}`,
	borderRadius: radii.md,
	color: colors.textPrimary,
	cursor: "pointer",
	outline: "none",
	transition: transitions.all
};
const emptyStateStyle$1 = {
	textAlign: "center",
	padding: spacing.xl,
	color: colors.textMuted,
	fontFamily: fonts.sans,
	fontSize: fontSizes.sm
};
function getItemStyle(isHovered, isFocused) {
	return {
		display: "flex",
		alignItems: "center",
		justifyContent: "space-between",
		padding: spacing.md,
		background: isHovered || isFocused ? colors.surfaceElevated : colors.surface,
		border: `1px solid ${isFocused ? colors.mint : colors.border}`,
		borderRadius: radii.md,
		transition: transitions.all,
		outline: isFocused ? `2px solid ${colors.mint}` : "none",
		outlineOffset: "2px"
	};
}
function getIconButtonStyle(variant, isHovered, isFocused) {
	const baseColor = variant === "danger" ? colors.danger : colors.textMuted;
	const hoverColor = variant === "danger" ? colors.danger : colors.mint;
	return {
		display: "flex",
		alignItems: "center",
		justifyContent: "center",
		width: "28px",
		height: "28px",
		padding: 0,
		background: isHovered || isFocused ? colors.surfaceElevated : "transparent",
		border: "none",
		borderRadius: radii.sm,
		cursor: "pointer",
		color: isHovered || isFocused ? hoverColor : baseColor,
		transition: transitions.all,
		outline: isFocused ? `2px solid ${colors.mint}` : "none",
		outlineOffset: "2px"
	};
}
function getSelectStyle(isFocused) {
	return {
		...baseSelectStyle,
		borderColor: isFocused ? colors.mint : colors.border,
		outline: isFocused ? `2px solid ${colors.mint}` : "none",
		outlineOffset: "2px"
	};
}
function IconButton({ variant, onClick, "aria-label": ariaLabel, title, disabled, children, "data-testid": testId }) {
	const [isHovered, setIsHovered] = useState(false);
	const [isFocused, setIsFocused] = useState(false);
	const isDisabled = disabled ?? false;
	return /* @__PURE__ */ jsx("button", {
		style: {
			...getIconButtonStyle(variant, isHovered, isFocused),
			opacity: isDisabled ? .5 : 1,
			cursor: isDisabled ? "not-allowed" : "pointer"
		},
		onClick,
		onMouseEnter: () => setIsHovered(true),
		onMouseLeave: () => setIsHovered(false),
		onFocus: () => setIsFocused(true),
		onBlur: () => setIsFocused(false),
		"aria-label": ariaLabel,
		title,
		disabled: isDisabled,
		"data-testid": testId,
		children
	});
}
function LinksTab({ links, newLinkPermission, onNewLinkPermissionChange, onCreateLink, onCopyLink, onDeleteLink, isLoading, loadingLinkId }) {
	const [hoveredLinkId, setHoveredLinkId] = useState(null);
	const [focusedLinkId, setFocusedLinkId] = useState(null);
	const [selectFocused, setSelectFocused] = useState(false);
	return /* @__PURE__ */ jsxs(Fragment, { children: [/* @__PURE__ */ jsxs("div", {
		style: sectionStyle,
		children: [/* @__PURE__ */ jsx("div", {
			style: sectionTitleStyle,
			children: "Create New Link"
		}), /* @__PURE__ */ jsxs("div", {
			style: createRowStyle,
			children: [/* @__PURE__ */ jsxs("select", {
				value: newLinkPermission,
				onChange: (e) => onNewLinkPermissionChange(e.target.value),
				style: getSelectStyle(selectFocused),
				onFocus: () => setSelectFocused(true),
				onBlur: () => setSelectFocused(false),
				"aria-label": "Link permission",
				disabled: isLoading,
				children: [
					/* @__PURE__ */ jsx("option", {
						value: "view",
						children: "View only"
					}),
					/* @__PURE__ */ jsx("option", {
						value: "edit",
						children: "Can edit"
					}),
					/* @__PURE__ */ jsx("option", {
						value: "admin",
						children: "Admin access"
					})
				]
			}), /* @__PURE__ */ jsx(Button, {
				variant: "primary",
				size: "sm",
				loading: isLoading && loadingLinkId === null,
				leftIcon: /* @__PURE__ */ jsx(PlusIcon, {}),
				rightIcon: null,
				onClick: onCreateLink,
				disabled: isLoading,
				"data-testid": "create-link-button",
				children: "Create Link"
			})]
		})]
	}), /* @__PURE__ */ jsxs("div", {
		style: sectionStyle,
		children: [/* @__PURE__ */ jsx("div", {
			style: sectionTitleStyle,
			children: "Active Links"
		}), links.length === 0 ? /* @__PURE__ */ jsxs("div", {
			style: emptyStateStyle$1,
			children: [/* @__PURE__ */ jsx(LinkIcon, {}), /* @__PURE__ */ jsx("p", {
				style: { marginTop: spacing.sm },
				children: "No share links yet"
			})]
		}) : /* @__PURE__ */ jsx("div", {
			style: listStyle,
			children: links.map((link) => {
				const isHovered = hoveredLinkId === link.id;
				const isFocused = focusedLinkId === link.id;
				const isLinkLoading = loadingLinkId === link.id;
				return /* @__PURE__ */ jsxs("div", {
					style: getItemStyle(isHovered, isFocused),
					onMouseEnter: () => setHoveredLinkId(link.id),
					onMouseLeave: () => setHoveredLinkId(null),
					onFocus: () => setFocusedLinkId(link.id),
					onBlur: () => setFocusedLinkId(null),
					tabIndex: 0,
					role: "listitem",
					children: [/* @__PURE__ */ jsxs("div", {
						style: linkInfoStyle,
						children: [/* @__PURE__ */ jsx("span", {
							style: { color: colors.mint },
							children: /* @__PURE__ */ jsx(LinkIcon, {})
						}), /* @__PURE__ */ jsxs("div", {
							style: linkTextStyle,
							children: [/* @__PURE__ */ jsxs("span", {
								style: linkTokenStyle,
								"data-testid": "share-link-input",
								children: [
									link.token.slice(0, 8),
									"...",
									link.token.slice(-4)
								]
							}), /* @__PURE__ */ jsxs("span", {
								style: linkMetaStyle,
								children: [
									permissionLabels[link.permission],
									" access · ",
									formatExpiry(link.expiresAt)
								]
							})]
						})]
					}), /* @__PURE__ */ jsxs("div", {
						style: actionsStyle$1,
						children: [
							/* @__PURE__ */ jsx(Badge, {
								variant: link.permission === "admin" ? "mod" : link.permission === "edit" ? "sub" : "muted",
								size: "sm",
								children: permissionLabels[link.permission]
							}),
							/* @__PURE__ */ jsx(IconButton, {
								variant: "default",
								onClick: () => onCopyLink(link),
								"aria-label": "Copy link",
								title: "Copy link",
								disabled: isLinkLoading,
								"data-testid": "copy-link-button",
								children: /* @__PURE__ */ jsx(CopyIcon, {})
							}),
							/* @__PURE__ */ jsx(IconButton, {
								variant: "danger",
								onClick: () => onDeleteLink(link.id),
								"aria-label": "Delete link",
								title: "Delete link",
								disabled: isLinkLoading,
								"data-testid": "delete-link-button",
								children: /* @__PURE__ */ jsx(TrashIcon, {})
							})
						]
					})]
				}, link.id);
			})
		})]
	})] });
}
function MembersTab({ members, currentUserId, inviteEmail, inviteRole, emailError, onInviteEmailChange, onInviteRoleChange, onInvite, onRemoveMember, onUpdateMemberRole, isLoading, loadingMemberId }) {
	const [hoveredMemberId, setHoveredMemberId] = useState(null);
	const [focusedMemberId, setFocusedMemberId] = useState(null);
	const [roleSelectFocused, setRoleSelectFocused] = useState(false);
	const [memberSelectFocused, setMemberSelectFocused] = useState(null);
	const handleKeyDown = (e) => {
		if (e.key === "Enter") onInvite();
	};
	return /* @__PURE__ */ jsxs(Fragment, { children: [/* @__PURE__ */ jsxs("div", {
		style: sectionStyle,
		children: [/* @__PURE__ */ jsx("div", {
			style: sectionTitleStyle,
			children: "Invite Member"
		}), /* @__PURE__ */ jsxs("div", {
			style: createRowStyle,
			children: [
				/* @__PURE__ */ jsxs("div", {
					style: { flex: 1 },
					children: [/* @__PURE__ */ jsx(Input, {
						size: "md",
						error: emailError !== null,
						leftElement: null,
						rightElement: null,
						placeholder: "Enter email address",
						value: inviteEmail,
						onChange: (e) => onInviteEmailChange(e.target.value),
						onKeyDown: handleKeyDown,
						disabled: isLoading,
						"aria-invalid": emailError !== null,
						"aria-describedby": emailError ? "email-error" : void 0
					}), emailError && /* @__PURE__ */ jsx("div", {
						id: "email-error",
						style: {
							fontFamily: fonts.sans,
							fontSize: fontSizes.xs,
							color: colors.danger,
							marginTop: spacing.xs
						},
						children: emailError
					})]
				}),
				/* @__PURE__ */ jsxs("select", {
					value: inviteRole,
					onChange: (e) => onInviteRoleChange(e.target.value),
					style: getSelectStyle(roleSelectFocused),
					onFocus: () => setRoleSelectFocused(true),
					onBlur: () => setRoleSelectFocused(false),
					"aria-label": "Member role",
					disabled: isLoading,
					children: [
						/* @__PURE__ */ jsx("option", {
							value: "viewer",
							children: "Viewer"
						}),
						/* @__PURE__ */ jsx("option", {
							value: "editor",
							children: "Editor"
						}),
						/* @__PURE__ */ jsx("option", {
							value: "admin",
							children: "Admin"
						})
					]
				}),
				/* @__PURE__ */ jsx(Button, {
					variant: "primary",
					size: "sm",
					loading: isLoading && loadingMemberId === null,
					leftIcon: /* @__PURE__ */ jsx(SendIcon$1, {}),
					rightIcon: null,
					onClick: onInvite,
					disabled: !inviteEmail.trim() || isLoading,
					children: "Invite"
				})
			]
		})]
	}), /* @__PURE__ */ jsxs("div", {
		style: sectionStyle,
		children: [/* @__PURE__ */ jsxs("div", {
			style: sectionTitleStyle,
			children: [
				"Project Members (",
				members.length,
				")"
			]
		}), members.length === 0 ? /* @__PURE__ */ jsx("div", {
			style: emptyStateStyle$1,
			children: /* @__PURE__ */ jsx("p", { children: "No members yet" })
		}) : /* @__PURE__ */ jsx("div", {
			style: listStyle,
			role: "list",
			children: members.map((member) => {
				const isCurrentUser = member.userId === currentUserId;
				const isOwner = member.role === "owner";
				const canRemove = !isOwner && !isCurrentUser;
				const canChangeRole = !isOwner;
				const isHovered = hoveredMemberId === member.id;
				const isFocused = focusedMemberId === member.id;
				const isMemberLoading = loadingMemberId === member.id;
				const isSelectFocused = memberSelectFocused === member.id;
				return /* @__PURE__ */ jsxs("div", {
					style: getItemStyle(isHovered, isFocused),
					onMouseEnter: () => setHoveredMemberId(member.id),
					onMouseLeave: () => setHoveredMemberId(null),
					onFocus: () => setFocusedMemberId(member.id),
					onBlur: () => setFocusedMemberId(null),
					tabIndex: 0,
					role: "listitem",
					children: [/* @__PURE__ */ jsxs("div", {
						style: memberInfoStyle,
						children: [/* @__PURE__ */ jsx(Avatar, {
							src: member.user.avatarUrl,
							alt: member.user.displayName,
							name: member.user.displayName,
							size: "sm",
							status: null,
							glow: null
						}), /* @__PURE__ */ jsxs("div", {
							style: memberTextStyle,
							children: [/* @__PURE__ */ jsxs("span", {
								style: memberNameStyle,
								children: [member.user.displayName, isCurrentUser && /* @__PURE__ */ jsxs("span", {
									style: {
										color: colors.textMuted,
										fontWeight: fontWeights.normal
									},
									children: [" ", "(you)"]
								})]
							}), /* @__PURE__ */ jsx("span", {
								style: memberEmailStyle,
								children: member.user.email
							})]
						})]
					}), /* @__PURE__ */ jsxs("div", {
						style: actionsStyle$1,
						children: [canChangeRole ? /* @__PURE__ */ jsxs("select", {
							value: member.role,
							onChange: (e) => onUpdateMemberRole(member.id, e.target.value),
							style: {
								...getSelectStyle(isSelectFocused),
								minWidth: "100px"
							},
							onFocus: () => setMemberSelectFocused(member.id),
							onBlur: () => setMemberSelectFocused(null),
							"aria-label": `Role for ${member.user.displayName}`,
							disabled: isMemberLoading,
							children: [
								/* @__PURE__ */ jsx("option", {
									value: "viewer",
									children: "Viewer"
								}),
								/* @__PURE__ */ jsx("option", {
									value: "editor",
									children: "Editor"
								}),
								/* @__PURE__ */ jsx("option", {
									value: "admin",
									children: "Admin"
								})
							]
						}) : /* @__PURE__ */ jsx(Badge, {
							variant: roleBadgeVariants[member.role],
							size: "sm",
							children: roleLabels[member.role]
						}), canRemove && /* @__PURE__ */ jsx(IconButton, {
							variant: "danger",
							onClick: () => onRemoveMember(member.id),
							"aria-label": `Remove ${member.user.displayName}`,
							title: "Remove member",
							disabled: isMemberLoading,
							"data-testid": "remove-member-button",
							children: /* @__PURE__ */ jsx(TrashIcon, {})
						})]
					})]
				}, member.id);
			})
		})]
	})] });
}
function ShareDialog({ isOpen, onClose, projectName, links, members, currentUserId, onCreateLink, onDeleteLink, onCopyLink, onInviteMember, onRemoveMember, onUpdateMemberRole, size, style }) {
	const [activeTab, setActiveTab] = useState("links");
	const [inviteEmail, setInviteEmail] = useState("");
	const [inviteRole, setInviteRole] = useState("viewer");
	const [newLinkPermission, setNewLinkPermission] = useState("view");
	const [emailError, setEmailError] = useState(null);
	const [isLoading, setIsLoading] = useState(false);
	const [loadingLinkId, setLoadingLinkId] = useState(null);
	const [loadingMemberId, setLoadingMemberId] = useState(null);
	const withLoading = useCallback(async (fn, setSpecificLoading = null, clearSpecificLoading = null) => {
		setIsLoading(true);
		if (setSpecificLoading) setSpecificLoading();
		try {
			await fn();
		} finally {
			setIsLoading(false);
			if (clearSpecificLoading) clearSpecificLoading();
		}
	}, []);
	const handleInviteEmailChange = useCallback((email) => {
		setInviteEmail(email);
		if (emailError) setEmailError(null);
	}, [emailError]);
	const handleInvite = useCallback(async () => {
		const trimmedEmail = inviteEmail.trim();
		if (!trimmedEmail) return;
		if (!validateEmail(trimmedEmail)) {
			setEmailError("Please enter a valid email address");
			return;
		}
		setEmailError(null);
		await withLoading(() => onInviteMember(trimmedEmail, inviteRole), null, null);
		setInviteEmail("");
	}, [
		inviteEmail,
		inviteRole,
		onInviteMember,
		withLoading
	]);
	const handleCreateLink = useCallback(async () => {
		await withLoading(() => onCreateLink(newLinkPermission));
	}, [
		newLinkPermission,
		onCreateLink,
		withLoading
	]);
	const handleCopyLink = useCallback(async (link) => {
		await withLoading(() => onCopyLink(link), () => setLoadingLinkId(link.id), () => setLoadingLinkId(null));
	}, [onCopyLink, withLoading]);
	const handleDeleteLink = useCallback(async (linkId) => {
		await withLoading(() => onDeleteLink(linkId), () => setLoadingLinkId(linkId), () => setLoadingLinkId(null));
	}, [onDeleteLink, withLoading]);
	const handleRemoveMember = useCallback(async (memberId) => {
		await withLoading(() => onRemoveMember(memberId), () => setLoadingMemberId(memberId), () => setLoadingMemberId(null));
	}, [onRemoveMember, withLoading]);
	const handleUpdateMemberRole = useCallback(async (memberId, role) => {
		await withLoading(() => onUpdateMemberRole(memberId, role), () => setLoadingMemberId(memberId), () => setLoadingMemberId(null));
	}, [onUpdateMemberRole, withLoading]);
	return /* @__PURE__ */ jsx(Modal, {
		isOpen,
		onClose,
		title: `Share "${projectName}"`,
		size: size ?? "lg",
		style,
		"data-testid": "share-dialog",
		children: /* @__PURE__ */ jsxs(Tabs, {
			value: activeTab,
			defaultValue: null,
			onChange: setActiveTab,
			"aria-label": "Share options",
			children: [
				/* @__PURE__ */ jsxs(Tabs.List, {
					"aria-label": "Share tabs",
					children: [/* @__PURE__ */ jsx(Tabs.Tab, {
						value: "links",
						children: "Share Links"
					}), /* @__PURE__ */ jsx(Tabs.Tab, {
						value: "members",
						children: "Members"
					})]
				}),
				/* @__PURE__ */ jsx(Tabs.Panel, {
					value: "links",
					children: /* @__PURE__ */ jsx(LinksTab, {
						links,
						newLinkPermission,
						onNewLinkPermissionChange: setNewLinkPermission,
						onCreateLink: handleCreateLink,
						onCopyLink: handleCopyLink,
						onDeleteLink: handleDeleteLink,
						isLoading,
						loadingLinkId
					})
				}),
				/* @__PURE__ */ jsx(Tabs.Panel, {
					value: "members",
					children: /* @__PURE__ */ jsx(MembersTab, {
						members,
						currentUserId,
						inviteEmail,
						inviteRole,
						emailError,
						onInviteEmailChange: handleInviteEmailChange,
						onInviteRoleChange: setInviteRole,
						onInvite: handleInvite,
						onRemoveMember: handleRemoveMember,
						onUpdateMemberRole: handleUpdateMemberRole,
						isLoading,
						loadingMemberId
					})
				})
			]
		})
	});
}
//#endregion
//#region organisms/ThreadPanel.tsx
/**
* EITELKEIT ThreadPanel
* ═══════════════════════════════════════════════════════════════════
* Slide-in panel for viewing and adding messages to a workflow node thread
*
* Features:
* - Slide-in animation from right
* - Displays thread messages with author and time
* - Input for sending new messages
* - Integrates with existing chat RPC endpoints
*/
const CloseIcon = () => /* @__PURE__ */ jsxs("svg", {
	width: "16",
	height: "16",
	viewBox: "0 0 16 16",
	fill: "none",
	stroke: "currentColor",
	strokeWidth: "2",
	strokeLinecap: "round",
	strokeLinejoin: "round",
	children: [/* @__PURE__ */ jsx("line", {
		x1: "4",
		y1: "4",
		x2: "12",
		y2: "12"
	}), /* @__PURE__ */ jsx("line", {
		x1: "12",
		y1: "4",
		x2: "4",
		y2: "12"
	})]
});
const SendIcon = () => /* @__PURE__ */ jsx("svg", {
	width: "16",
	height: "16",
	viewBox: "0 0 16 16",
	fill: "none",
	stroke: "currentColor",
	strokeWidth: "1.5",
	strokeLinecap: "round",
	strokeLinejoin: "round",
	children: /* @__PURE__ */ jsx("path", { d: "M14 2L2 7l5 2.5L9.5 14 14 2z" })
});
const MessageIcon = () => /* @__PURE__ */ jsx("svg", {
	width: "16",
	height: "16",
	viewBox: "0 0 16 16",
	fill: "none",
	stroke: "currentColor",
	strokeWidth: "1.5",
	strokeLinecap: "round",
	strokeLinejoin: "round",
	children: /* @__PURE__ */ jsx("path", { d: "M2.5 3.5h11a1 1 0 0 1 1 1v7a1 1 0 0 1-1 1H5l-3 2v-10a1 1 0 0 1 1-1z" })
});
function formatTime(date) {
	const diff = (/* @__PURE__ */ new Date()).getTime() - date.getTime();
	const minutes = Math.floor(diff / 6e4);
	const hours = Math.floor(minutes / 60);
	const days = Math.floor(hours / 24);
	if (minutes < 1) return "just now";
	if (minutes < 60) return `${minutes}m ago`;
	if (hours < 24) return `${hours}h ago`;
	if (days < 7) return `${days}d ago`;
	return date.toLocaleDateString(void 0, {
		month: "short",
		day: "numeric"
	});
}
function getInitials(name) {
	return name.split(" ").map((n) => n[0]).join("").toUpperCase().slice(0, 2);
}
const panelBaseStyle = {
	position: "fixed",
	top: 0,
	right: 0,
	width: "360px",
	maxWidth: "100vw",
	height: "100vh",
	background: colors.surface,
	borderLeft: `1px solid ${colors.border}`,
	boxShadow: shadows.lg,
	zIndex: zIndices.modal,
	display: "flex",
	flexDirection: "column",
	transition: `transform ${durations.slow} ${easings.expressive}`
};
const headerStyle$2 = {
	display: "flex",
	alignItems: "center",
	justifyContent: "space-between",
	padding: spacing.md,
	borderBottom: `1px solid ${colors.border}`,
	background: colors.voidLight
};
const headerTitleStyle = {
	display: "flex",
	alignItems: "center",
	gap: spacing.sm
};
const titleStyle$1 = {
	fontFamily: fonts.sans,
	fontSize: fontSizes.base,
	fontWeight: fontWeights.semibold,
	color: colors.textPrimary,
	margin: 0
};
const nodeNameStyle = {
	fontFamily: fonts.mono,
	fontSize: fontSizes.sm,
	color: colors.mint,
	marginTop: "2px"
};
const messagesContainerStyle = {
	flex: 1,
	overflowY: "auto",
	padding: spacing.md,
	display: "flex",
	flexDirection: "column",
	gap: spacing.md
};
const emptyStateStyle = {
	flex: 1,
	display: "flex",
	flexDirection: "column",
	alignItems: "center",
	justifyContent: "center",
	padding: spacing.xl,
	color: colors.textMuted,
	fontFamily: fonts.sans,
	fontSize: fontSizes.sm,
	textAlign: "center"
};
const inputContainerStyle = {
	display: "flex",
	alignItems: "flex-end",
	gap: spacing.sm,
	padding: spacing.md,
	borderTop: `1px solid ${colors.border}`,
	background: colors.voidLight
};
const backdropStyle = {
	position: "fixed",
	top: 0,
	left: 0,
	right: 0,
	bottom: 0,
	background: "rgba(0, 0, 0, 0.4)",
	zIndex: zIndices.overlay,
	transition: `opacity ${durations.normal} ${easings.productive}`
};
function MessageItem({ message, isCurrentUser }) {
	const [isHovered, setIsHovered] = useState(false);
	const containerStyle = {
		display: "flex",
		gap: spacing.sm,
		padding: spacing.sm,
		borderRadius: radii.md,
		background: isHovered ? colors.surfaceElevated : "transparent",
		transition: transitions.all
	};
	const avatarStyle = {
		width: 32,
		height: 32,
		borderRadius: radii.full,
		background: isCurrentUser ? colors.mint : colors.voidLighter,
		color: isCurrentUser ? colors.void : colors.textSecondary,
		display: "flex",
		alignItems: "center",
		justifyContent: "center",
		fontFamily: fonts.sans,
		fontSize: fontSizes.xs,
		fontWeight: fontWeights.semibold,
		flexShrink: 0
	};
	const contentContainerStyle = {
		flex: 1,
		minWidth: 0
	};
	const authorRowStyle = {
		display: "flex",
		alignItems: "baseline",
		gap: spacing.sm,
		marginBottom: "2px"
	};
	const authorNameStyle = {
		fontFamily: fonts.sans,
		fontSize: fontSizes.sm,
		fontWeight: fontWeights.medium,
		color: isCurrentUser ? colors.mint : colors.textPrimary
	};
	const timeStyle = {
		fontFamily: fonts.mono,
		fontSize: fontSizes.xs,
		color: colors.textMuted
	};
	const messageContentStyle = {
		fontFamily: fonts.sans,
		fontSize: fontSizes.sm,
		color: colors.textSecondary,
		lineHeight: 1.5,
		wordBreak: "break-word"
	};
	return /* @__PURE__ */ jsxs("div", {
		style: containerStyle,
		onMouseEnter: () => setIsHovered(true),
		onMouseLeave: () => setIsHovered(false),
		"data-testid": "thread-message",
		children: [message.authorAvatarUrl ? /* @__PURE__ */ jsx("img", {
			src: message.authorAvatarUrl,
			alt: message.authorName,
			style: {
				...avatarStyle,
				objectFit: "cover"
			}
		}) : /* @__PURE__ */ jsx("div", {
			style: avatarStyle,
			children: getInitials(message.authorName)
		}), /* @__PURE__ */ jsxs("div", {
			style: contentContainerStyle,
			children: [/* @__PURE__ */ jsxs("div", {
				style: authorRowStyle,
				children: [
					/* @__PURE__ */ jsx("span", {
						style: authorNameStyle,
						children: message.authorName
					}),
					/* @__PURE__ */ jsx("span", {
						style: timeStyle,
						children: formatTime(message.createdAt)
					}),
					message.editedAt && /* @__PURE__ */ jsx("span", {
						style: {
							...timeStyle,
							fontStyle: "italic"
						},
						children: "(edited)"
					})
				]
			}), /* @__PURE__ */ jsx("p", {
				style: messageContentStyle,
				children: message.content
			})]
		})]
	});
}
function MessageInput({ onSend, disabled }) {
	const [content, setContent] = useState("");
	const [isFocused, setIsFocused] = useState(false);
	const [isSendHovered, setIsSendHovered] = useState(false);
	const textareaRef = useRef(null);
	const handleSubmit = () => {
		const trimmed = content.trim();
		if (trimmed && !disabled) {
			onSend(trimmed);
			setContent("");
			if (textareaRef.current) textareaRef.current.style.height = "auto";
		}
	};
	const handleKeyDown = (e) => {
		if (e.key === "Enter" && !e.shiftKey) {
			e.preventDefault();
			handleSubmit();
		}
	};
	const handleInput = (e) => {
		setContent(e.target.value);
		if (textareaRef.current) {
			textareaRef.current.style.height = "auto";
			textareaRef.current.style.height = `${Math.min(textareaRef.current.scrollHeight, 120)}px`;
		}
	};
	const textareaStyle = {
		flex: 1,
		minHeight: "40px",
		maxHeight: "120px",
		padding: `${spacing.sm} ${spacing.md}`,
		background: colors.surface,
		border: `1px solid ${isFocused ? colors.mint : colors.border}`,
		borderRadius: radii.md,
		color: colors.textPrimary,
		fontFamily: fonts.sans,
		fontSize: fontSizes.sm,
		lineHeight: 1.5,
		resize: "none",
		outline: "none",
		transition: transitions.all,
		boxShadow: isFocused ? shadows.focusRing : "none"
	};
	const sendButtonStyle = {
		display: "flex",
		alignItems: "center",
		justifyContent: "center",
		width: 40,
		height: 40,
		padding: 0,
		background: content.trim() ? colors.mint : colors.surface,
		border: content.trim() ? "none" : `1px solid ${colors.border}`,
		borderRadius: radii.md,
		color: content.trim() ? colors.void : colors.textMuted,
		cursor: content.trim() && !disabled ? "pointer" : "not-allowed",
		transition: transitions.all,
		opacity: disabled ? .5 : 1,
		...isSendHovered && content.trim() && !disabled ? {
			background: colors.mintDim,
			boxShadow: shadows.mintGlow
		} : {}
	};
	return /* @__PURE__ */ jsxs("div", {
		style: inputContainerStyle,
		children: [/* @__PURE__ */ jsx("textarea", {
			ref: textareaRef,
			value: content,
			onChange: handleInput,
			onKeyDown: handleKeyDown,
			onFocus: () => setIsFocused(true),
			onBlur: () => setIsFocused(false),
			placeholder: "Add a comment...",
			style: textareaStyle,
			disabled,
			rows: 1,
			"data-testid": "thread-input"
		}), /* @__PURE__ */ jsx("button", {
			type: "button",
			onClick: handleSubmit,
			onMouseEnter: () => setIsSendHovered(true),
			onMouseLeave: () => setIsSendHovered(false),
			style: sendButtonStyle,
			disabled: !content.trim() || disabled,
			"aria-label": "Send message",
			"data-testid": "thread-send-button",
			children: /* @__PURE__ */ jsx(SendIcon, {})
		})]
	});
}
function CloseButton({ onClick }) {
	const [isHovered, setIsHovered] = useState(false);
	return /* @__PURE__ */ jsx("button", {
		type: "button",
		onClick,
		onMouseEnter: () => setIsHovered(true),
		onMouseLeave: () => setIsHovered(false),
		style: {
			display: "flex",
			alignItems: "center",
			justifyContent: "center",
			width: 32,
			height: 32,
			padding: 0,
			background: isHovered ? colors.surfaceElevated : "transparent",
			border: "none",
			borderRadius: radii.sm,
			color: isHovered ? colors.textPrimary : colors.textMuted,
			cursor: "pointer",
			transition: transitions.all
		},
		"aria-label": "Close thread panel",
		"data-testid": "thread-close-button",
		children: /* @__PURE__ */ jsx(CloseIcon, {})
	});
}
function ThreadPanel({ isOpen, onClose, nodeId, nodeName, messages, onSendMessage, isLoading, currentUserId, style }) {
	const messagesEndRef = useRef(null);
	const prevMessagesLengthRef = useRef(messages.length);
	useEffect(() => {
		if (messages.length > prevMessagesLengthRef.current) messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
		prevMessagesLengthRef.current = messages.length;
	}, [messages.length]);
	useEffect(() => {
		const handleKeyDown = (e) => {
			if (e.key === "Escape" && isOpen) onClose();
		};
		document.addEventListener("keydown", handleKeyDown);
		return () => document.removeEventListener("keydown", handleKeyDown);
	}, [isOpen, onClose]);
	const handleBackdropClick = (e) => {
		if (e.target === e.currentTarget) onClose();
	};
	if (!isOpen) return null;
	const panelStyle = {
		...panelBaseStyle,
		transform: isOpen ? "translateX(0)" : "translateX(100%)",
		...style
	};
	return /* @__PURE__ */ jsxs(Fragment, { children: [/* @__PURE__ */ jsx("div", {
		style: {
			...backdropStyle,
			opacity: isOpen ? 1 : 0,
			pointerEvents: isOpen ? "auto" : "none"
		},
		onClick: handleBackdropClick
	}), /* @__PURE__ */ jsxs("aside", {
		role: "dialog",
		"aria-modal": "true",
		"aria-labelledby": "thread-panel-title",
		style: panelStyle,
		"data-testid": "thread-panel",
		children: [
			/* @__PURE__ */ jsxs("header", {
				style: headerStyle$2,
				children: [/* @__PURE__ */ jsxs("div", {
					style: headerTitleStyle,
					children: [/* @__PURE__ */ jsx("span", {
						style: { color: colors.mint },
						children: /* @__PURE__ */ jsx(MessageIcon, {})
					}), /* @__PURE__ */ jsxs("div", { children: [/* @__PURE__ */ jsx("h2", {
						id: "thread-panel-title",
						style: titleStyle$1,
						children: "Thread"
					}), /* @__PURE__ */ jsx("div", {
						style: nodeNameStyle,
						children: nodeName
					})] })]
				}), /* @__PURE__ */ jsx(CloseButton, { onClick: onClose })]
			}),
			/* @__PURE__ */ jsx("div", {
				style: messagesContainerStyle,
				children: isLoading && messages.length === 0 ? /* @__PURE__ */ jsxs("div", {
					style: emptyStateStyle,
					children: [/* @__PURE__ */ jsx(LoadingSpinner, {}), /* @__PURE__ */ jsx("span", {
						style: { marginTop: spacing.md },
						children: "Loading messages..."
					})]
				}) : messages.length === 0 ? /* @__PURE__ */ jsxs("div", {
					style: emptyStateStyle,
					children: [
						/* @__PURE__ */ jsx("span", {
							style: {
								color: colors.mint,
								marginBottom: spacing.sm
							},
							children: /* @__PURE__ */ jsx(MessageIcon, {})
						}),
						/* @__PURE__ */ jsx("span", { children: "No comments yet" }),
						/* @__PURE__ */ jsx("span", {
							style: {
								fontSize: fontSizes.xs,
								marginTop: spacing.xs
							},
							children: "Be the first to start the conversation"
						})
					]
				}) : /* @__PURE__ */ jsxs(Fragment, { children: [messages.map((message) => /* @__PURE__ */ jsx(MessageItem, {
					message,
					isCurrentUser: message.authorId === currentUserId
				}, message.id)), /* @__PURE__ */ jsx("div", { ref: messagesEndRef })] })
			}),
			/* @__PURE__ */ jsx(MessageInput, {
				onSend: onSendMessage,
				disabled: isLoading
			})
		]
	})] });
}
function LoadingSpinner() {
	return /* @__PURE__ */ jsxs("svg", {
		width: "24",
		height: "24",
		viewBox: "0 0 24 24",
		fill: "none",
		style: { animation: "thread-panel-spin 1s linear infinite" },
		children: [/* @__PURE__ */ jsx("style", { children: `
        @keyframes thread-panel-spin {
          to { transform: rotate(360deg); }
        }
      ` }), /* @__PURE__ */ jsx("circle", {
			cx: "12",
			cy: "12",
			r: "10",
			stroke: colors.mint,
			strokeWidth: "2",
			strokeLinecap: "round",
			strokeDasharray: "50 30"
		})]
	});
}
//#endregion
//#region organisms/ActionConfirmation.tsx
/**
* EITELKEIT ActionConfirmation
* ======================================================================
* Modal component for confirming AI action execution with parameters display
*/
const ImageIcon = () => /* @__PURE__ */ jsxs("svg", {
	width: "24",
	height: "24",
	viewBox: "0 0 24 24",
	fill: "none",
	stroke: "currentColor",
	strokeWidth: "2",
	strokeLinecap: "round",
	strokeLinejoin: "round",
	children: [
		/* @__PURE__ */ jsx("rect", {
			x: "3",
			y: "3",
			width: "18",
			height: "18",
			rx: "2",
			ry: "2"
		}),
		/* @__PURE__ */ jsx("circle", {
			cx: "8.5",
			cy: "8.5",
			r: "1.5"
		}),
		/* @__PURE__ */ jsx("polyline", { points: "21 15 16 10 5 21" })
	]
});
const VideoIcon = () => /* @__PURE__ */ jsxs("svg", {
	width: "24",
	height: "24",
	viewBox: "0 0 24 24",
	fill: "none",
	stroke: "currentColor",
	strokeWidth: "2",
	strokeLinecap: "round",
	strokeLinejoin: "round",
	children: [/* @__PURE__ */ jsx("polygon", { points: "23 7 16 12 23 17 23 7" }), /* @__PURE__ */ jsx("rect", {
		x: "1",
		y: "5",
		width: "15",
		height: "14",
		rx: "2",
		ry: "2"
	})]
});
const WorkflowIcon = () => /* @__PURE__ */ jsxs("svg", {
	width: "24",
	height: "24",
	viewBox: "0 0 24 24",
	fill: "none",
	stroke: "currentColor",
	strokeWidth: "2",
	strokeLinecap: "round",
	strokeLinejoin: "round",
	children: [/* @__PURE__ */ jsx("circle", {
		cx: "12",
		cy: "12",
		r: "3"
	}), /* @__PURE__ */ jsx("path", { d: "M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1 0 2.83 2 2 0 0 1-2.83 0l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-2 2 2 2 0 0 1-2-2v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83 0 2 2 0 0 1 0-2.83l.06-.06a1.65 1.65 0 0 0 .33-1.82 1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1-2-2 2 2 0 0 1 2-2h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 0-2.83 2 2 0 0 1 2.83 0l.06.06a1.65 1.65 0 0 0 1.82.33H9a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 2-2 2 2 0 0 1 2 2v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 0 2 2 0 0 1 0 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82V9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 2 2 2 2 0 0 1-2 2h-.09a1.65 1.65 0 0 0-1.51 1z" })]
});
const PixooIcon = () => /* @__PURE__ */ jsxs("svg", {
	width: "24",
	height: "24",
	viewBox: "0 0 24 24",
	fill: "none",
	stroke: "currentColor",
	strokeWidth: "2",
	strokeLinecap: "round",
	strokeLinejoin: "round",
	children: [
		/* @__PURE__ */ jsx("rect", {
			x: "2",
			y: "3",
			width: "20",
			height: "14",
			rx: "2",
			ry: "2"
		}),
		/* @__PURE__ */ jsx("line", {
			x1: "8",
			y1: "21",
			x2: "16",
			y2: "21"
		}),
		/* @__PURE__ */ jsx("line", {
			x1: "12",
			y1: "17",
			x2: "12",
			y2: "21"
		})
	]
});
const SpinnerIcon = () => /* @__PURE__ */ jsx("svg", {
	width: "16",
	height: "16",
	viewBox: "0 0 16 16",
	fill: "none",
	stroke: "currentColor",
	strokeWidth: "2",
	strokeLinecap: "round",
	children: /* @__PURE__ */ jsx("path", { d: "M8 1v3M8 12v3M1 8h3M12 8h3M2.93 2.93l2.12 2.12M10.95 10.95l2.12 2.12M2.93 13.07l2.12-2.12M10.95 5.05l2.12-2.12" })
});
const ACTION_CONFIG = {
	generate_image: {
		label: "Generate Image",
		description: "Generate a new image using AI",
		icon: /* @__PURE__ */ jsx(ImageIcon, {})
	},
	generate_video: {
		label: "Generate Video",
		description: "Generate a video using AI",
		icon: /* @__PURE__ */ jsx(VideoIcon, {})
	},
	run_workflow: {
		label: "Run Workflow",
		description: "Execute a workflow pipeline",
		icon: /* @__PURE__ */ jsx(WorkflowIcon, {})
	},
	push_pixoo: {
		label: "Push to Pixoo",
		description: "Send image to Pixoo display",
		icon: /* @__PURE__ */ jsx(PixooIcon, {})
	}
};
/**
* Format a parameter value for display
*/
function formatParamValue(value) {
	if (value === null) return "null";
	if (value === void 0) return "undefined";
	if (typeof value === "string") return value.length > 100 ? `${value.slice(0, 100)}...` : value;
	if (typeof value === "number" || typeof value === "boolean") return String(value);
	if (Array.isArray(value)) return `[${value.length} items]`;
	if (typeof value === "object") return JSON.stringify(value).slice(0, 100);
	return String(value);
}
const overlayStyle = {
	position: "fixed",
	inset: 0,
	background: colors.surfaceOverlay,
	backdropFilter: "blur(4px)",
	display: "flex",
	alignItems: "center",
	justifyContent: "center",
	zIndex: 400
};
const cardStyle = {
	background: colors.surface,
	border: `1px solid ${colors.border}`,
	borderRadius: radii.lg,
	boxShadow: shadows.lg,
	width: "100%",
	maxWidth: "480px",
	padding: spacing.xl,
	fontFamily: fonts.sans
};
const headerStyle$1 = {
	display: "flex",
	alignItems: "center",
	gap: spacing.md,
	marginBottom: spacing.lg
};
const iconContainerStyle = {
	display: "flex",
	alignItems: "center",
	justifyContent: "center",
	width: "48px",
	height: "48px",
	background: colors.mintGlow,
	borderRadius: radii.md,
	color: colors.mint
};
const titleStyle = {
	fontFamily: fonts.sans,
	fontSize: fontSizes.xl,
	fontWeight: fontWeights.semibold,
	color: colors.textPrimary,
	margin: 0
};
const descriptionStyle = {
	fontFamily: fonts.sans,
	fontSize: fontSizes.sm,
	color: colors.textMuted,
	margin: 0
};
const sectionLabelStyle = {
	fontFamily: fonts.sans,
	fontSize: fontSizes.xs,
	fontWeight: fontWeights.semibold,
	color: colors.textMuted,
	textTransform: "uppercase",
	letterSpacing: "0.05em",
	marginBottom: spacing.sm
};
const paramsContainerStyle = {
	background: colors.void,
	border: `1px solid ${colors.border}`,
	borderRadius: radii.md,
	padding: spacing.md,
	marginBottom: spacing.lg,
	maxHeight: "200px",
	overflowY: "auto"
};
const paramRowStyle = {
	display: "flex",
	justifyContent: "space-between",
	alignItems: "flex-start",
	padding: `${spacing.xs} 0`,
	borderBottom: `1px solid ${colors.border}`
};
const paramRowLastStyle = {
	...paramRowStyle,
	borderBottom: "none"
};
const paramKeyStyle = {
	fontFamily: fonts.mono,
	fontSize: fontSizes.sm,
	color: colors.mint,
	fontWeight: fontWeights.medium
};
const paramValueStyle = {
	fontFamily: fonts.mono,
	fontSize: fontSizes.sm,
	color: colors.textSecondary,
	maxWidth: "60%",
	textAlign: "right",
	wordBreak: "break-word"
};
const emptyParamsStyle = {
	fontFamily: fonts.sans,
	fontSize: fontSizes.sm,
	color: colors.textMuted,
	fontStyle: "italic",
	textAlign: "center",
	padding: spacing.md
};
const actionsStyle = {
	display: "flex",
	justifyContent: "flex-end",
	gap: spacing.sm
};
function ConfirmButton({ variant, onClick, disabled, loading, children, "data-testid": testId }) {
	const [isHovered, setIsHovered] = useState(false);
	const [isFocused, setIsFocused] = useState(false);
	const baseStyle = {
		display: "inline-flex",
		alignItems: "center",
		justifyContent: "center",
		gap: spacing.xs,
		padding: `${spacing.sm} ${spacing.lg}`,
		borderRadius: radii.md,
		fontFamily: fonts.sans,
		fontSize: fontSizes.sm,
		fontWeight: fontWeights.semibold,
		cursor: disabled ? "not-allowed" : "pointer",
		opacity: disabled ? .5 : 1,
		transition: transitions.all,
		border: "none",
		outline: isFocused ? `2px solid ${colors.mint}` : "none",
		outlineOffset: "2px"
	};
	const primaryStyle = {
		...baseStyle,
		background: isHovered && !disabled ? colors.mintDim : colors.mint,
		color: colors.void,
		boxShadow: isHovered && !disabled ? shadows.mintGlow : "none"
	};
	const ghostStyle = {
		...baseStyle,
		background: isHovered && !disabled ? colors.surfaceElevated : "transparent",
		color: colors.textSecondary,
		border: `1px solid ${colors.border}`
	};
	return /* @__PURE__ */ jsxs("button", {
		style: variant === "primary" ? primaryStyle : ghostStyle,
		onClick,
		disabled,
		onMouseEnter: () => setIsHovered(true),
		onMouseLeave: () => setIsHovered(false),
		onFocus: () => setIsFocused(true),
		onBlur: () => setIsFocused(false),
		type: "button",
		"data-testid": testId,
		children: [loading && /* @__PURE__ */ jsx("span", {
			style: {
				animation: "spin 1s linear infinite",
				display: "inline-flex"
			},
			children: /* @__PURE__ */ jsx(SpinnerIcon, {})
		}), children]
	});
}
function ActionConfirmation({ action, params, onConfirm, onCancel, isExecuting, style }) {
	const config = ACTION_CONFIG[action];
	const paramEntries = Object.entries(params);
	const handleOverlayClick = (e) => {
		if (e.target === e.currentTarget && !isExecuting) onCancel();
	};
	const handleKeyDown = (e) => {
		if (e.key === "Escape" && !isExecuting) onCancel();
	};
	return /* @__PURE__ */ jsx("div", {
		style: overlayStyle,
		onClick: handleOverlayClick,
		onKeyDown: handleKeyDown,
		role: "dialog",
		"aria-modal": "true",
		"aria-labelledby": "action-confirmation-title",
		"data-testid": "action-confirmation",
		children: /* @__PURE__ */ jsxs("div", {
			style: {
				...cardStyle,
				...style
			},
			children: [
				/* @__PURE__ */ jsxs("div", {
					style: headerStyle$1,
					children: [/* @__PURE__ */ jsx("div", {
						style: iconContainerStyle,
						children: config.icon
					}), /* @__PURE__ */ jsxs("div", { children: [/* @__PURE__ */ jsx("h2", {
						id: "action-confirmation-title",
						style: titleStyle,
						"data-testid": "action-type",
						children: config.label
					}), /* @__PURE__ */ jsx("p", {
						style: descriptionStyle,
						children: config.description
					})] })]
				}),
				/* @__PURE__ */ jsx("div", {
					style: sectionLabelStyle,
					children: "Parameters"
				}),
				/* @__PURE__ */ jsx("div", {
					style: paramsContainerStyle,
					"data-testid": "action-params",
					children: paramEntries.length === 0 ? /* @__PURE__ */ jsx("div", {
						style: emptyParamsStyle,
						children: "No parameters"
					}) : paramEntries.map(([key, value], index) => /* @__PURE__ */ jsxs("div", {
						style: index === paramEntries.length - 1 ? paramRowLastStyle : paramRowStyle,
						children: [/* @__PURE__ */ jsx("span", {
							style: paramKeyStyle,
							children: key
						}), /* @__PURE__ */ jsx("span", {
							style: paramValueStyle,
							children: formatParamValue(value)
						})]
					}, key))
				}),
				/* @__PURE__ */ jsxs("div", {
					style: actionsStyle,
					children: [/* @__PURE__ */ jsx(ConfirmButton, {
						variant: "ghost",
						onClick: onCancel,
						disabled: isExecuting,
						loading: false,
						"data-testid": "cancel-button",
						children: "Cancel"
					}), /* @__PURE__ */ jsx(ConfirmButton, {
						variant: "primary",
						onClick: onConfirm,
						disabled: isExecuting,
						loading: isExecuting,
						"data-testid": "confirm-button",
						children: isExecuting ? "Executing..." : "Confirm"
					})]
				})
			]
		})
	});
}
//#endregion
//#region organisms/Canvas.tsx
const Canvas = forwardRef(function Canvas({ offset, zoom, gridSize, onPan, onZoom, svgContent, children, style }, ref) {
	const containerRef = useRef(null);
	const [isPanning, setIsPanning] = useState(false);
	const [panStart, setPanStart] = useState({
		x: 0,
		y: 0
	});
	const offsetRef = useRef(offset);
	const zoomRef = useRef(zoom);
	const onPanRef = useRef(onPan);
	const onZoomRef = useRef(onZoom);
	const touchStateRef = useRef(null);
	useEffect(() => {
		offsetRef.current = offset;
	}, [offset]);
	useEffect(() => {
		zoomRef.current = zoom;
	}, [zoom]);
	useEffect(() => {
		onPanRef.current = onPan;
	}, [onPan]);
	useEffect(() => {
		onZoomRef.current = onZoom;
	}, [onZoom]);
	const handleMouseDown = useCallback((e) => {
		if (e.button === 0) {
			setIsPanning(true);
			setPanStart({
				x: e.clientX,
				y: e.clientY
			});
		}
	}, []);
	const handleMouseMove = useCallback((e) => {
		if (isPanning && onPan) {
			const dx = e.clientX - panStart.x;
			const dy = e.clientY - panStart.y;
			setPanStart({
				x: e.clientX,
				y: e.clientY
			});
			onPan({
				x: offset.x + dx,
				y: offset.y + dy
			});
		}
	}, [
		isPanning,
		panStart,
		offset,
		onPan
	]);
	const handleMouseUp = useCallback(() => {
		setIsPanning(false);
	}, []);
	const handleWheel = useCallback((e) => {
		if (e.ctrlKey || e.metaKey) {
			e.preventDefault();
			if (!onZoom || !onPan) return;
			const rect = (containerRef.current ?? ref?.current)?.getBoundingClientRect();
			if (!rect) return;
			const mouseX = e.clientX - rect.left;
			const mouseY = e.clientY - rect.top;
			const deltaModeMultiplier = e.deltaMode === 1 ? 20 : 1;
			const zoomDelta = -e.deltaY * .001 * deltaModeMultiplier;
			const newZoom = Math.min(Math.max(zoom + zoomDelta, .1), 3);
			onPan({
				x: mouseX - (mouseX - offset.x) * (newZoom / zoom),
				y: mouseY - (mouseY - offset.y) * (newZoom / zoom)
			});
			onZoom(newZoom);
		} else if (onPan) onPan({
			x: offset.x - e.deltaX,
			y: offset.y - e.deltaY
		});
	}, [
		zoom,
		offset,
		onPan,
		onZoom,
		ref
	]);
	const handleTouchStart = useCallback((e) => {
		if (e.touches.length === 1) {
			const touch = e.touches[0];
			touchStateRef.current = {
				type: "pan",
				lastX: touch.clientX,
				lastY: touch.clientY
			};
		} else if (e.touches.length === 2) {
			const t1 = e.touches[0];
			const t2 = e.touches[1];
			touchStateRef.current = {
				type: "pinch",
				lastDist: Math.hypot(t2.clientX - t1.clientX, t2.clientY - t1.clientY),
				lastMidX: (t1.clientX + t2.clientX) / 2,
				lastMidY: (t1.clientY + t2.clientY) / 2
			};
		}
	}, []);
	const handleTouchEnd = useCallback((e) => {
		if (e.touches.length === 1) {
			const touch = e.touches[0];
			touchStateRef.current = {
				type: "pan",
				lastX: touch.clientX,
				lastY: touch.clientY
			};
		} else if (e.touches.length === 0) touchStateRef.current = null;
	}, []);
	useEffect(() => {
		const el = containerRef.current;
		if (el === null) return;
		const onTouchMove = (e) => {
			e.preventDefault();
			const state = touchStateRef.current;
			if (state === null) return;
			const currentOffset = offsetRef.current;
			const currentZoom = zoomRef.current;
			const pan = onPanRef.current;
			const zoomFn = onZoomRef.current;
			if (e.touches.length === 1 && state.type === "pan" && pan !== null) {
				const touch = e.touches[0];
				const dx = touch.clientX - state.lastX;
				const dy = touch.clientY - state.lastY;
				touchStateRef.current = {
					type: "pan",
					lastX: touch.clientX,
					lastY: touch.clientY
				};
				pan({
					x: currentOffset.x + dx,
					y: currentOffset.y + dy
				});
			} else if (e.touches.length === 2 && state.type === "pinch" && pan !== null && zoomFn !== null) {
				const t1 = e.touches[0];
				const t2 = e.touches[1];
				const midX = (t1.clientX + t2.clientX) / 2;
				const midY = (t1.clientY + t2.clientY) / 2;
				const dist = Math.hypot(t2.clientX - t1.clientX, t2.clientY - t1.clientY);
				if (state.lastDist === 0) return;
				const scaleFactor = dist / state.lastDist;
				const newZoom = Math.min(Math.max(currentZoom * scaleFactor, .1), 3);
				const rect = el.getBoundingClientRect();
				const relMidX = midX - rect.left;
				const relMidY = midY - rect.top;
				const newOffsetX = relMidX - (relMidX - currentOffset.x) * (newZoom / currentZoom);
				const newOffsetY = relMidY - (relMidY - currentOffset.y) * (newZoom / currentZoom);
				touchStateRef.current = {
					type: "pinch",
					lastDist: dist,
					lastMidX: midX,
					lastMidY: midY
				};
				pan({
					x: newOffsetX,
					y: newOffsetY
				});
				zoomFn(newZoom);
			}
		};
		el.addEventListener("touchmove", onTouchMove, { passive: false });
		return () => el.removeEventListener("touchmove", onTouchMove);
	}, []);
	useEffect(() => {
		const handleGlobalMouseUp = () => {
			setIsPanning(false);
		};
		window.addEventListener("mouseup", handleGlobalMouseUp);
		return () => window.removeEventListener("mouseup", handleGlobalMouseUp);
	}, []);
	const resolvedGridSize = gridSize ?? null;
	const gridBackground = resolvedGridSize !== null ? {
		backgroundImage: [`repeating-linear-gradient(0deg, ${colors.border} 0px, ${colors.border} 1px, transparent 1px, transparent ${resolvedGridSize}px)`, `repeating-linear-gradient(90deg, ${colors.border} 0px, ${colors.border} 1px, transparent 1px, transparent ${resolvedGridSize}px)`].join(", "),
		backgroundSize: `${resolvedGridSize * zoom}px ${resolvedGridSize * zoom}px`,
		backgroundPosition: `${offset.x}px ${offset.y}px`
	} : {};
	return /* @__PURE__ */ jsx("div", {
		ref: (node) => {
			containerRef.current = node;
			if (typeof ref === "function") ref(node);
			else if (ref) ref.current = node;
		},
		style: {
			position: "relative",
			overflow: "hidden",
			width: "100%",
			height: "100%",
			backgroundColor: colors.void,
			cursor: isPanning ? "grabbing" : "grab",
			touchAction: "none",
			...gridBackground,
			...style
		},
		onMouseDown: handleMouseDown,
		onMouseMove: handleMouseMove,
		onMouseUp: handleMouseUp,
		onMouseLeave: handleMouseUp,
		onWheel: handleWheel,
		onTouchStart: handleTouchStart,
		onTouchEnd: handleTouchEnd,
		onTouchCancel: handleTouchEnd,
		children: /* @__PURE__ */ jsxs("div", {
			style: {
				position: "absolute",
				inset: 0,
				transformOrigin: "0 0",
				transform: `translate(${offset.x}px, ${offset.y}px) scale(${zoom})`
			},
			children: [svgContent !== null ? /* @__PURE__ */ jsx("svg", {
				style: {
					position: "absolute",
					inset: 0,
					width: "100%",
					height: "100%",
					pointerEvents: "none",
					overflow: "visible"
				},
				children: svgContent
			}) : null, children]
		})
	});
});
//#endregion
//#region organisms/CanvasNode.tsx
/**
* EITELKEIT CanvasNode
* ═══════════════════════════════════════════════════════════════════
* Generic draggable canvas node component
* Position absolute, drag via mousedown/mousemove/mouseup on window
* Touch: tap-to-select, drag, long-press (500ms) for context menu
*/
const INTERACTIVE_TAGS = new Set([
	"INPUT",
	"TEXTAREA",
	"SELECT",
	"BUTTON",
	"A"
]);
function isInteractiveElement(el) {
	if (!(el instanceof HTMLElement)) return false;
	if (INTERACTIVE_TAGS.has(el.tagName)) return true;
	if (el.getAttribute("role") === "button") return true;
	if (el.getAttribute("contenteditable") === "true") return true;
	return false;
}
const CanvasNode = forwardRef(function CanvasNode({ id, x, y, width, height, selected, onDrag, onSelect, onContextMenu, zoom, children, style }, ref) {
	const [isHovered, setIsHovered] = useState(false);
	const dragStateRef = useRef(null);
	const touchDragRef = useRef(null);
	const longPressTimerRef = useRef(null);
	const w = width ?? 200;
	const h = height ?? 120;
	const z = zoom ?? 1;
	const handleMouseDown = useCallback((e) => {
		if (isInteractiveElement(e.target)) return;
		if (e.button !== 0) return;
		e.preventDefault();
		dragStateRef.current = {
			startMouseX: e.clientX,
			startMouseY: e.clientY,
			startNodeX: x,
			startNodeY: y,
			didDrag: false
		};
		const handleMouseMove = (moveEvent) => {
			const state = dragStateRef.current;
			if (!state) return;
			const dx = moveEvent.clientX - state.startMouseX;
			const dy = moveEvent.clientY - state.startMouseY;
			if (!state.didDrag && (Math.abs(dx) > 2 || Math.abs(dy) > 2)) state.didDrag = true;
			if (state.didDrag) {
				const dpr = window.devicePixelRatio || 1;
				onDrag(id, {
					x: state.startNodeX + dx / (z * dpr),
					y: state.startNodeY + dy / (z * dpr)
				});
			}
		};
		const handleMouseUp = () => {
			const state = dragStateRef.current;
			if (state && !state.didDrag) onSelect(id);
			dragStateRef.current = null;
			window.removeEventListener("mousemove", handleMouseMove);
			window.removeEventListener("mouseup", handleMouseUp);
		};
		window.addEventListener("mousemove", handleMouseMove);
		window.addEventListener("mouseup", handleMouseUp);
	}, [
		id,
		x,
		y,
		z,
		onDrag,
		onSelect
	]);
	const handleTouchStart = useCallback((e) => {
		if (isInteractiveElement(e.target)) return;
		if (e.touches.length !== 1) return;
		e.stopPropagation();
		const touch = e.touches[0];
		touchDragRef.current = {
			touchId: touch.identifier,
			startTouchX: touch.clientX,
			startTouchY: touch.clientY,
			startNodeX: x,
			startNodeY: y,
			didDrag: false
		};
		longPressTimerRef.current = setTimeout(() => {
			const state = touchDragRef.current;
			if (state !== null && !state.didDrag && onContextMenu) onContextMenu(id, {
				x: touch.clientX,
				y: touch.clientY
			});
			touchDragRef.current = null;
			longPressTimerRef.current = null;
		}, 500);
		const handleTouchMove = (moveEvent) => {
			const state = touchDragRef.current;
			if (state === null) return;
			const t = Array.from(moveEvent.changedTouches).find((ct) => ct.identifier === state.touchId);
			if (t === void 0) return;
			const dx = t.clientX - state.startTouchX;
			const dy = t.clientY - state.startTouchY;
			if (!state.didDrag && (Math.abs(dx) > 8 || Math.abs(dy) > 8)) {
				state.didDrag = true;
				if (longPressTimerRef.current !== null) {
					clearTimeout(longPressTimerRef.current);
					longPressTimerRef.current = null;
				}
			}
			if (state.didDrag) {
				const dpr = window.devicePixelRatio || 1;
				onDrag(id, {
					x: state.startNodeX + dx / (z * dpr),
					y: state.startNodeY + dy / (z * dpr)
				});
			}
		};
		const handleTouchEnd = (endEvent) => {
			const state = touchDragRef.current;
			if (longPressTimerRef.current !== null) {
				clearTimeout(longPressTimerRef.current);
				longPressTimerRef.current = null;
			}
			if (state !== null) {
				if (Array.from(endEvent.changedTouches).find((ct) => ct.identifier === state.touchId) !== void 0 && !state.didDrag) onSelect(id);
				touchDragRef.current = null;
			}
			window.removeEventListener("touchmove", handleTouchMove);
			window.removeEventListener("touchend", handleTouchEnd);
			window.removeEventListener("touchcancel", handleTouchEnd);
		};
		window.addEventListener("touchmove", handleTouchMove, { passive: true });
		window.addEventListener("touchend", handleTouchEnd);
		window.addEventListener("touchcancel", handleTouchEnd);
	}, [
		id,
		x,
		y,
		z,
		onDrag,
		onSelect,
		onContextMenu
	]);
	useEffect(() => {
		return () => {
			dragStateRef.current = null;
			touchDragRef.current = null;
			if (longPressTimerRef.current !== null) clearTimeout(longPressTimerRef.current);
		};
	}, []);
	const borderColor = selected ? colors.borderActive : isHovered ? colors.borderHover : colors.border;
	return /* @__PURE__ */ jsx("div", {
		ref,
		onMouseDown: handleMouseDown,
		onMouseEnter: () => setIsHovered(true),
		onMouseLeave: () => setIsHovered(false),
		onTouchStart: handleTouchStart,
		style: {
			position: "absolute",
			left: x,
			top: y,
			width: w,
			height: h,
			background: colors.surface,
			border: `1px solid ${borderColor}`,
			borderRadius: radii.lg,
			boxShadow: selected ? shadows.mintGlow : shadows.none,
			transition: `border-color ${transitions.fast}, box-shadow ${transitions.fast}`,
			cursor: "grab",
			userSelect: "none",
			overflow: "hidden",
			...style
		},
		children
	});
});
//#endregion
//#region organisms/CanvasEdge.tsx
/**
* EITELKEIT CanvasEdge
* ═══════════════════════════════════════════════════════════════════
* SVG Bezier edge component for canvas-based graph visualization
*
* Features:
* - Cubic bezier curves with horizontal control points
* - Optional animated stroke-dasharray
* - Optional clickable midpoint target circle
* - Hover state with opacity feedback
*/
const CanvasEdge = forwardRef(function CanvasEdge({ fromX, fromY, toX, toY, color, animated, strokeWidth, onClick }, ref) {
	const [isHovered, setIsHovered] = useState(false);
	const c = color ?? colors.mintDim;
	const sw = strokeWidth ?? 2;
	const a = animated ?? false;
	const hasClick = onClick !== null;
	const dx = Math.abs(toX - fromX) * .5;
	const pathData = `M ${fromX} ${fromY} C ${fromX + dx} ${fromY}, ${toX - dx} ${toY}, ${toX} ${toY}`;
	const midX = (fromX + toX) / 2;
	const midY = (fromY + toY) / 2;
	return /* @__PURE__ */ jsxs("g", {
		ref,
		onMouseEnter: () => setIsHovered(true),
		onMouseLeave: () => setIsHovered(false),
		children: [
			/* @__PURE__ */ jsx("path", {
				d: pathData,
				fill: "none",
				stroke: c,
				strokeWidth: sw,
				style: {
					opacity: isHovered ? .8 : 1,
					transition: "opacity 200ms ease-in-out",
					...a && {
						strokeDasharray: "8 4",
						animation: "stroke-dash 20s linear infinite"
					}
				}
			}),
			a && /* @__PURE__ */ jsx("defs", { children: /* @__PURE__ */ jsx("style", { children: `
                @keyframes stroke-dash {
                  to {
                    stroke-dashoffset: -12;
                  }
                }
              ` }) }),
			hasClick && /* @__PURE__ */ jsxs(Fragment, { children: [/* @__PURE__ */ jsx("path", {
				d: pathData,
				fill: "none",
				stroke: "transparent",
				strokeWidth: 12,
				style: {
					cursor: "pointer",
					opacity: 0
				},
				onClick: onClick ?? void 0
			}), /* @__PURE__ */ jsx("circle", {
				cx: midX,
				cy: midY,
				r: 4,
				fill: c,
				style: {
					cursor: "pointer",
					opacity: isHovered ? .6 : .3,
					transition: "opacity 200ms ease-in-out"
				},
				onClick: onClick ?? void 0
			})] })
		]
	});
});
//#endregion
//#region organisms/PerformanceMonitor.tsx
/**
* EITELKEIT PerformanceMonitor
* ═══════════════════════════════════════════════════════════════════
* Generic performance metrics overlay. Accepts all data via props --
* the consumer is responsible for FPS measurement, memory polling,
* and store bindings.
*/
const statGridStyle = {
	display: "grid",
	gridTemplateColumns: "1fr 1fr",
	gap: spacing.xs
};
function badgeVariantFromStatus(status) {
	switch (status) {
		case "live": return "success";
		case "away": return "warning";
		case "error": return "danger";
	}
}
const PerformanceMonitor = forwardRef(function PerformanceMonitor({ groups, status, visible, style }, ref) {
	if (!visible) return null;
	return /* @__PURE__ */ jsx("div", {
		ref,
		children: /* @__PURE__ */ jsxs(GlassPanel, {
			blur: "sm",
			intensity: "heavy",
			bordered: true,
			style: {
				padding: `${spacing.xs} ${spacing.md}`,
				minWidth: 180,
				display: "flex",
				flexDirection: "column",
				gap: spacing.xs,
				fontFamily: fonts.mono,
				fontSize: fontSizes.xs,
				lineHeight: 1.6,
				pointerEvents: "none",
				userSelect: "none",
				...style ?? {}
			},
			children: [groups.map((group, groupIndex) => /* @__PURE__ */ jsxs(React.Fragment, { children: [group.label !== null && groupIndex > 0 && /* @__PURE__ */ jsx("div", { style: {
				borderTop: `1px solid ${colors.glassBorder}`,
				margin: `${spacing.xs} 0`
			} }), /* @__PURE__ */ jsx("div", {
				style: statGridStyle,
				children: group.metrics.map((m) => /* @__PURE__ */ jsx(StatDisplay, {
					label: m.label,
					value: m.value,
					trend: m.trend,
					trendValue: m.trendValue,
					size: "sm"
				}, m.label))
			})] }, group.label ?? `group-${String(groupIndex)}`)), status !== null && /* @__PURE__ */ jsxs("div", {
				style: {
					display: "flex",
					justifyContent: "space-between",
					alignItems: "center",
					gap: spacing.md
				},
				children: [/* @__PURE__ */ jsx("span", {
					style: {
						color: colors.textMuted,
						fontSize: fontSizes.xs,
						fontFamily: fonts.mono,
						textTransform: "uppercase",
						letterSpacing: "0.06em"
					},
					children: status.label
				}), /* @__PURE__ */ jsxs("div", {
					style: {
						display: "flex",
						alignItems: "center",
						gap: spacing.xs
					},
					children: [/* @__PURE__ */ jsx(StatusDot, {
						status: status.status,
						size: "sm",
						pulse: status.pulse,
						ringPulse: null,
						label: null
					}), /* @__PURE__ */ jsx(Badge, {
						variant: badgeVariantFromStatus(status.status),
						size: "sm",
						children: status.text
					})]
				})]
			})]
		})
	});
});
//#endregion
//#region organisms/TreeLayerPanel.tsx
/**
* EITELKEIT TreeLayerPanel
* ═══════════════════════════════════════════════════════════════════
* Flat layer list with visibility/lock toggles, selection, and drag-to-reorder.
* Uses TreeView molecule for rendering. Designed for scene compositor layer panels.
*/
function VisibilityIcon({ visible }) {
	if (visible) return /* @__PURE__ */ jsxs("svg", {
		width: "12",
		height: "12",
		viewBox: "0 0 24 24",
		fill: "none",
		stroke: colors.mintDim,
		strokeWidth: "2",
		strokeLinecap: "round",
		strokeLinejoin: "round",
		children: [/* @__PURE__ */ jsx("path", { d: "M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" }), /* @__PURE__ */ jsx("circle", {
			cx: "12",
			cy: "12",
			r: "3"
		})]
	});
	return /* @__PURE__ */ jsxs("svg", {
		width: "12",
		height: "12",
		viewBox: "0 0 24 24",
		fill: "none",
		stroke: colors.textMuted,
		strokeWidth: "2",
		strokeLinecap: "round",
		strokeLinejoin: "round",
		children: [
			/* @__PURE__ */ jsx("path", { d: "M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94" }),
			/* @__PURE__ */ jsx("path", { d: "M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19" }),
			/* @__PURE__ */ jsx("line", {
				x1: "1",
				y1: "1",
				x2: "23",
				y2: "23"
			})
		]
	});
}
function LockIcon({ locked }) {
	if (locked) return /* @__PURE__ */ jsxs("svg", {
		width: "12",
		height: "12",
		viewBox: "0 0 24 24",
		fill: "none",
		stroke: colors.danger,
		strokeWidth: "2",
		strokeLinecap: "round",
		strokeLinejoin: "round",
		children: [/* @__PURE__ */ jsx("rect", {
			x: "3",
			y: "11",
			width: "18",
			height: "11",
			rx: "2",
			ry: "2"
		}), /* @__PURE__ */ jsx("path", { d: "M7 11V7a5 5 0 0 1 10 0v4" })]
	});
	return /* @__PURE__ */ jsxs("svg", {
		width: "12",
		height: "12",
		viewBox: "0 0 24 24",
		fill: "none",
		stroke: colors.mintDim,
		strokeWidth: "2",
		strokeLinecap: "round",
		strokeLinejoin: "round",
		children: [/* @__PURE__ */ jsx("rect", {
			x: "3",
			y: "11",
			width: "18",
			height: "11",
			rx: "2",
			ry: "2"
		}), /* @__PURE__ */ jsx("path", { d: "M7 11V7a5 5 0 0 1 5-5v0a5 5 0 0 1 5 5v1" })]
	});
}
const iconButtonBase = {
	display: "flex",
	alignItems: "center",
	justifyContent: "center",
	padding: "2px",
	borderRadius: "2px",
	cursor: "pointer",
	border: "none",
	background: "transparent",
	transition: "opacity 0.15s, background 0.15s"
};
function StatusIcons({ visible, locked, onToggleVisible, onToggleLocked }) {
	const [hoveredIcon, setHoveredIcon] = useState(null);
	return /* @__PURE__ */ jsxs("span", {
		style: {
			display: "flex",
			gap: "4px",
			alignItems: "center"
		},
		children: [onToggleVisible !== null && /* @__PURE__ */ jsx("span", {
			role: "button",
			tabIndex: -1,
			onClick: onToggleVisible,
			onMouseEnter: () => setHoveredIcon("visible"),
			onMouseLeave: () => setHoveredIcon(null),
			title: "Toggle visibility",
			style: {
				...iconButtonBase,
				opacity: hoveredIcon === "visible" ? 1 : visible ? .8 : .5,
				background: hoveredIcon === "visible" ? "rgba(255,255,255,0.06)" : "transparent"
			},
			children: /* @__PURE__ */ jsx(VisibilityIcon, { visible })
		}), onToggleLocked !== null && /* @__PURE__ */ jsx("span", {
			role: "button",
			tabIndex: -1,
			onClick: onToggleLocked,
			onMouseEnter: () => setHoveredIcon("locked"),
			onMouseLeave: () => setHoveredIcon(null),
			title: "Toggle lock",
			style: {
				...iconButtonBase,
				opacity: hoveredIcon === "locked" ? 1 : locked ? .8 : .5,
				background: hoveredIcon === "locked" ? "rgba(255,255,255,0.06)" : "transparent"
			},
			children: /* @__PURE__ */ jsx(LockIcon, { locked })
		})]
	});
}
const containerStyle = {
	display: "flex",
	flexDirection: "column",
	gap: 0
};
const headerStyle = {
	fontSize: fontSizes.xs,
	fontWeight: fontWeights.semibold,
	color: colors.mintDim,
	textTransform: "uppercase",
	letterSpacing: letterSpacings.wider,
	padding: `${spacing.sm} ${spacing.md} ${spacing.xs}`
};
function TreeLayerPanel({ items, selectedId, onSelect, onReorder, onToggleVisibility, onToggleLock, header, style }) {
	const [dragItemId, setDragItemId] = useState(null);
	const containerRef = useRef(null);
	const treeNodes = useMemo(() => items.map((item) => ({
		id: item.id,
		label: item.label,
		children: null,
		icon: /* @__PURE__ */ jsx(StatusIcons, {
			visible: item.visible,
			locked: item.locked,
			onToggleVisible: onToggleVisibility !== null ? (e) => {
				e.stopPropagation();
				onToggleVisibility(item.id);
			} : null,
			onToggleLocked: onToggleLock !== null ? (e) => {
				e.stopPropagation();
				onToggleLock(item.id);
			} : null
		})
	})), [
		items,
		onToggleVisibility,
		onToggleLock
	]);
	const handleSelect = useCallback((id) => {
		onSelect(id);
	}, [onSelect]);
	const handleDragStart = useCallback((e) => {
		const target = e.target.closest("[role=\"treeitem\"]");
		if (target === null) return;
		const node = items[Array.from(containerRef.current?.querySelectorAll("[role=\"treeitem\"]") ?? []).indexOf(target)];
		if (node === void 0) return;
		setDragItemId(node.id);
		e.dataTransfer.effectAllowed = "move";
		e.dataTransfer.setData("text/plain", node.id);
	}, [items]);
	const handleDragOver = useCallback((e) => {
		e.preventDefault();
		e.dataTransfer.dropEffect = "move";
	}, []);
	const handleDrop = useCallback((e) => {
		e.preventDefault();
		const target = e.target.closest("[role=\"treeitem\"]");
		if (target === null || dragItemId === null) return;
		const node = items[Array.from(containerRef.current?.querySelectorAll("[role=\"treeitem\"]") ?? []).indexOf(target)];
		if (node === void 0 || dragItemId === node.id) return;
		const fromIndex = items.findIndex((item) => item.id === dragItemId);
		const toIndex = items.findIndex((item) => item.id === node.id);
		if (fromIndex === -1 || toIndex === -1) return;
		onReorder(fromIndex, toIndex);
		setDragItemId(null);
	}, [
		dragItemId,
		items,
		onReorder
	]);
	const handleDragLeave = useCallback(() => {}, []);
	const resolvedStyle = style ?? {};
	if (items.length === 0) return /* @__PURE__ */ jsxs("div", {
		style: {
			...containerStyle,
			...resolvedStyle
		},
		children: [header !== null && /* @__PURE__ */ jsx("div", {
			style: headerStyle,
			children: header
		}), /* @__PURE__ */ jsx("div", {
			style: {
				padding: spacing.md,
				fontSize: fontSizes.xs,
				color: colors.textMuted
			},
			children: "No layers"
		})]
	});
	return /* @__PURE__ */ jsxs("div", {
		style: {
			...containerStyle,
			...resolvedStyle
		},
		children: [header !== null && /* @__PURE__ */ jsx("div", {
			style: headerStyle,
			children: header
		}), /* @__PURE__ */ jsx("div", {
			ref: containerRef,
			draggable: true,
			onDragStart: handleDragStart,
			onDragOver: handleDragOver,
			onDrop: handleDrop,
			onDragLeave: handleDragLeave,
			style: { cursor: "grab" },
			children: /* @__PURE__ */ jsx(TreeView, {
				nodes: treeNodes,
				onSelect: handleSelect,
				selectedId,
				style: null
			})
		})]
	});
}
//#endregion
//#region organisms/SettingsFormBuilder.tsx
/**
* EITELKEIT SettingsFormBuilder
* ═══════════════════════════════════════════════════════════════════
* Zod-schema-driven form builder. Inspects a ZodObject's shape to
* auto-generate labelled form fields grouped by an optional meta map.
*/
function unwrapOuter(field) {
	let current = field;
	if (current instanceof z.ZodDefault) current = current.removeDefault();
	if (current instanceof z.ZodNullable) return {
		inner: current.unwrap(),
		nullable: true
	};
	if (current instanceof z.ZodOptional) return {
		inner: current.unwrap(),
		nullable: true
	};
	return {
		inner: current,
		nullable: false
	};
}
function labelFromKey(key) {
	return key.replace(/([a-z])([A-Z])/g, "$1 $2").replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());
}
function InlineSlider({ value, min, max, step, disabled, onChange }) {
	const [isHovered, setIsHovered] = useState(false);
	const pct = max > min ? (value - min) / (max - min) * 100 : 0;
	return /* @__PURE__ */ jsxs("div", {
		style: {
			display: "flex",
			alignItems: "center",
			gap: spacing.md
		},
		children: [/* @__PURE__ */ jsx("input", {
			type: "range",
			min,
			max,
			step,
			value,
			disabled,
			onChange: (e) => onChange(Number(e.target.value)),
			onMouseEnter: () => setIsHovered(true),
			onMouseLeave: () => setIsHovered(false),
			style: {
				flex: 1,
				height: "4px",
				appearance: "none",
				WebkitAppearance: "none",
				background: `linear-gradient(to right, ${colors.mint} ${pct}%, ${colors.surface} ${pct}%)`,
				borderRadius: "2px",
				outline: "none",
				cursor: disabled ? "not-allowed" : "pointer",
				opacity: disabled ? .5 : 1,
				transition: transitions.all
			}
		}), /* @__PURE__ */ jsx("span", {
			style: {
				fontFamily: fonts.mono,
				fontSize: fontSizes.sm,
				color: isHovered ? colors.mint : colors.textSecondary,
				minWidth: "36px",
				textAlign: "right",
				transition: transitions.colors
			},
			children: value
		})]
	});
}
function ClearButton({ onClick, disabled }) {
	const [isHovered, setIsHovered] = useState(false);
	return /* @__PURE__ */ jsx("button", {
		type: "button",
		onClick,
		disabled,
		onMouseEnter: () => setIsHovered(true),
		onMouseLeave: () => setIsHovered(false),
		style: {
			background: "transparent",
			border: "none",
			color: isHovered ? colors.mint : colors.textMuted,
			cursor: disabled ? "not-allowed" : "pointer",
			padding: "0 4px",
			fontSize: fontSizes.sm,
			fontFamily: fonts.sans,
			transition: transitions.colors,
			opacity: disabled ? .5 : 1
		},
		"aria-label": "Clear value",
		children: "✕"
	});
}
function GroupHeader({ label }) {
	return /* @__PURE__ */ jsx("div", {
		style: {
			paddingBottom: spacing.sm,
			marginBottom: spacing.md,
			borderBottom: `1px solid ${colors.border}`
		},
		children: /* @__PURE__ */ jsx("span", {
			style: {
				fontFamily: fonts.sans,
				fontSize: fontSizes.xs,
				fontWeight: fontWeights.semibold,
				textTransform: "uppercase",
				letterSpacing: letterSpacings.wider,
				color: colors.textMuted
			},
			children: label
		})
	});
}
function SettingsFormBuilder(props) {
	const { schema, values, onChange, meta: metaProp, disabled, style: styleProp } = props;
	const meta = metaProp ?? {};
	const shape = schema.shape;
	const keys = Object.keys(shape);
	const grouped = useMemo(() => {
		const groups = /* @__PURE__ */ new Map();
		const ungrouped = [];
		for (const key of keys) {
			const groupName = meta[key]?.group ?? null;
			if (groupName !== null) {
				const arr = groups.get(groupName) ?? [];
				arr.push(key);
				groups.set(groupName, arr);
			} else ungrouped.push(key);
		}
		const result = [];
		if (ungrouped.length > 0) result.push({
			group: null,
			fields: ungrouped
		});
		for (const [groupName, fields] of groups) result.push({
			group: groupName,
			fields
		});
		return result;
	}, [keys, meta]);
	const renderField = useCallback((key) => {
		const field = shape[key];
		const fieldMeta = meta[key] ?? {};
		const { inner, nullable } = unwrapOuter(field);
		const value = values[key];
		const label = field.description ?? labelFromKey(key);
		const hint = fieldMeta.hint ?? null;
		const placeholder = fieldMeta.placeholder ?? null;
		const componentHint = fieldMeta.component ?? null;
		if (componentHint === "switch" || inner instanceof z.ZodBoolean) return /* @__PURE__ */ jsx(FormField, {
			label: null,
			required: null,
			error: null,
			hint,
			id: key,
			children: /* @__PURE__ */ jsx(Switch, {
				checked: value ?? false,
				defaultChecked: null,
				onChange: (checked) => onChange(key, checked),
				size: null,
				label,
				disabled
			})
		}, key);
		if (componentHint === "textarea") return /* @__PURE__ */ jsx(FormField, {
			label,
			required: null,
			error: null,
			hint,
			id: key,
			children: /* @__PURE__ */ jsx(Textarea, {
				id: key,
				value: value ?? "",
				onChange: (e) => onChange(key, e.target.value),
				placeholder: placeholder ?? void 0,
				disabled,
				error: null,
				resize: null
			})
		}, key);
		if (componentHint === "select" || inner instanceof z.ZodEnum) {
			let options;
			if (fieldMeta.options) options = fieldMeta.options.map((o) => ({
				label: o.label,
				value: o.value,
				disabled: o.disabled !== null ? true : null
			}));
			else if (inner instanceof z.ZodEnum) options = inner.options.map((v) => ({
				label: v,
				value: v,
				disabled: null
			}));
			else options = [];
			return /* @__PURE__ */ jsx(FormField, {
				label,
				required: null,
				error: null,
				hint,
				id: key,
				children: /* @__PURE__ */ jsx(Select, {
					id: key,
					options,
					value: value ?? "",
					onChange: (e) => onChange(key, e.target.value),
					disabled,
					size: null,
					error: null
				})
			}, key);
		}
		if (componentHint === "slider" || inner instanceof z.ZodNumber && fieldMeta.min != null && fieldMeta.max != null) {
			const min = fieldMeta.min ?? 0;
			const max = fieldMeta.max ?? 100;
			const step = fieldMeta.step ?? 1;
			return /* @__PURE__ */ jsx(FormField, {
				label,
				required: null,
				error: null,
				hint,
				id: key,
				children: /* @__PURE__ */ jsx(InlineSlider, {
					value: value ?? min,
					min,
					max,
					step,
					disabled,
					onChange: (v) => onChange(key, v)
				})
			}, key);
		}
		if (inner instanceof z.ZodNumber) return /* @__PURE__ */ jsx(FormField, {
			label,
			required: null,
			error: null,
			hint,
			id: key,
			children: /* @__PURE__ */ jsx(Input, {
				id: key,
				type: "number",
				value: value != null ? String(value) : "",
				onChange: (e) => {
					const raw = e.target.value;
					onChange(key, raw === "" ? nullable ? null : 0 : Number(raw));
				},
				placeholder: placeholder ?? void 0,
				min: fieldMeta.min ?? void 0,
				max: fieldMeta.max ?? void 0,
				step: fieldMeta.step ?? void 0,
				disabled,
				size: null,
				error: null,
				leftElement: null,
				rightElement: nullable && value != null ? /* @__PURE__ */ jsx(ClearButton, {
					onClick: () => onChange(key, null),
					disabled
				}) : null
			})
		}, key);
		return /* @__PURE__ */ jsx(FormField, {
			label,
			required: null,
			error: null,
			hint,
			id: key,
			children: /* @__PURE__ */ jsx(Input, {
				id: key,
				type: "text",
				value: value ?? "",
				onChange: (e) => onChange(key, e.target.value || (nullable ? null : "")),
				placeholder: placeholder ?? void 0,
				disabled,
				size: null,
				error: null,
				leftElement: null,
				rightElement: nullable && value != null && value !== "" ? /* @__PURE__ */ jsx(ClearButton, {
					onClick: () => onChange(key, null),
					disabled
				}) : null
			})
		}, key);
	}, [
		shape,
		meta,
		values,
		onChange,
		disabled
	]);
	return /* @__PURE__ */ jsx("div", {
		style: {
			display: "flex",
			flexDirection: "column",
			gap: spacing.md,
			...styleProp ?? {}
		},
		children: grouped.map((section, i) => /* @__PURE__ */ jsxs("div", { children: [section.group !== null && /* @__PURE__ */ jsx(GroupHeader, { label: section.group }), /* @__PURE__ */ jsx("div", {
			style: {
				display: "flex",
				flexDirection: "column",
				gap: spacing.md
			},
			children: section.fields.map(renderField)
		})] }, section.group ?? `__ungrouped_${i}`))
	});
}
SettingsFormBuilder.displayName = "SettingsFormBuilder";
//#endregion
//#region organisms/ThemeProfile.tsx
function ThemeProfile({ initialThemeId, applyGlobal, onChange }) {
	const shouldApplyGlobal = applyGlobal ?? false;
	const themeCtx = useTheme();
	const [localTheme, setLocalTheme] = useState(initialThemeId !== null && initialThemeId in THEMES_BY_ID ? THEMES_BY_ID[initialThemeId] : THEMES_BY_ID[DEFAULT_THEME_ID]);
	const displayTheme = shouldApplyGlobal ? themeCtx.theme : localTheme;
	function handleChange(t) {
		if (shouldApplyGlobal) themeCtx.setTheme(t);
		else setLocalTheme(t);
		onChange?.(t);
	}
	return /* @__PURE__ */ jsxs("div", {
		style: {
			display: "flex",
			flexDirection: "column",
			gap: "16px"
		},
		children: [/* @__PURE__ */ jsxs("div", {
			style: {
				display: "flex",
				alignItems: "center",
				justifyContent: "space-between"
			},
			children: [/* @__PURE__ */ jsx("span", {
				style: {
					fontFamily: fonts.mono,
					fontSize: "12px",
					color: themeCtx.colors.textMuted,
					textTransform: "uppercase",
					letterSpacing: "0.08em"
				},
				children: "Theme"
			}), /* @__PURE__ */ jsx("span", {
				style: {
					fontFamily: fonts.mono,
					fontSize: "12px",
					color: themeCtx.colors.mint,
					fontWeight: 600
				},
				children: displayTheme.meta.name
			})]
		}), /* @__PURE__ */ jsx(ThemeSwitcher, {
			activeThemeId: displayTheme.meta.id,
			onThemeChange: handleChange
		})]
	});
}
//#endregion
//#region organisms/Dashboard.tsx
/**
* EITELKEIT Dashboard
* ═══════════════════════════════════════════════════════════════════
* Stats dashboard organism with header and a grid of stat cards.
*/
function StatCard({ stat }) {
	const [isHovered, setIsHovered] = useState(false);
	const trendIcon = stat.trend === "up" ? "↑" : stat.trend === "down" ? "↓" : "—";
	const trendColor = stat.trend === "up" ? colors.mint : stat.trend === "down" ? colors.error : colors.textMuted;
	return /* @__PURE__ */ jsxs("div", {
		onMouseEnter: () => setIsHovered(true),
		onMouseLeave: () => setIsHovered(false),
		style: {
			background: isHovered ? colors.surfaceElevated : colors.surface,
			border: `1px solid ${isHovered ? colors.borderHover : colors.border}`,
			borderRadius: radii.card,
			padding: spacing.lg,
			display: "flex",
			flexDirection: "column",
			gap: spacing.sm,
			transition: transitions.all,
			boxShadow: isHovered ? shadows.md : shadows.sm
		},
		children: [/* @__PURE__ */ jsx("span", {
			style: {
				fontFamily: fonts.sans,
				fontSize: fontSizes.xs,
				fontWeight: fontWeights.semibold,
				color: colors.textMuted,
				textTransform: "uppercase",
				letterSpacing: letterSpacings.wider
			},
			children: stat.label
		}), /* @__PURE__ */ jsxs("div", {
			style: {
				display: "flex",
				alignItems: "flex-end",
				justifyContent: "space-between",
				gap: spacing.sm
			},
			children: [/* @__PURE__ */ jsx("span", {
				style: {
					fontFamily: fonts.mono,
					fontSize: fontSizes["3xl"],
					fontWeight: fontWeights.bold,
					color: colors.textPrimary,
					lineHeight: 1
				},
				children: stat.value
			}), stat.trend !== null && /* @__PURE__ */ jsx("span", {
				style: {
					fontFamily: fonts.mono,
					fontSize: fontSizes.sm,
					fontWeight: fontWeights.semibold,
					color: trendColor,
					padding: `${spacing.xs} ${spacing.sm}`,
					background: stat.trend === "up" ? colors.mintGlow : stat.trend === "down" ? colors.dangerGlow : colors.glassBg,
					borderRadius: radii.sm,
					lineHeight: 1
				},
				children: trendIcon
			})]
		})]
	});
}
function Dashboard({ stats, title, style }) {
	const resolvedStats = stats ?? [];
	return /* @__PURE__ */ jsxs("div", {
		style: {
			display: "flex",
			flexDirection: "column",
			gap: spacing.lg,
			...style
		},
		children: [title !== null && /* @__PURE__ */ jsxs("div", {
			style: {
				display: "flex",
				alignItems: "center",
				justifyContent: "space-between",
				paddingBottom: spacing.md,
				borderBottom: `1px solid ${colors.border}`
			},
			children: [/* @__PURE__ */ jsx("h2", {
				style: {
					margin: 0,
					fontFamily: fonts.mono,
					fontSize: fontSizes.lg,
					fontWeight: fontWeights.semibold,
					color: colors.textPrimary,
					letterSpacing: letterSpacings.tight
				},
				children: title
			}), /* @__PURE__ */ jsxs("span", {
				style: {
					fontFamily: fonts.mono,
					fontSize: fontSizes.xs,
					color: colors.mint,
					textTransform: "uppercase",
					letterSpacing: letterSpacings.wider
				},
				children: [resolvedStats.length, " metrics"]
			})]
		}), resolvedStats.length > 0 ? /* @__PURE__ */ jsx("div", {
			style: {
				display: "grid",
				gridTemplateColumns: "repeat(auto-fill, minmax(180px, 1fr))",
				gap: spacing.md
			},
			children: resolvedStats.map((stat, i) => /* @__PURE__ */ jsx(StatCard, { stat }, `${stat.label}-${i}`))
		}) : /* @__PURE__ */ jsx("div", {
			style: {
				display: "flex",
				alignItems: "center",
				justifyContent: "center",
				padding: spacing["2xl"],
				border: `1px dashed ${colors.border}`,
				borderRadius: radii.card
			},
			children: /* @__PURE__ */ jsx("span", {
				style: {
					fontFamily: fonts.mono,
					fontSize: fontSizes.sm,
					color: colors.textMuted
				},
				children: "No metrics available"
			})
		})]
	});
}
//#endregion
//#region organisms/LibraryBrowser.tsx
/**
* EITELKEIT LibraryBrowser
* ═══════════════════════════════════════════════════════════════════
* Browseable library with client-side search and item selection.
*/
function LibraryItemRow({ item, onSelect }) {
	const [isHovered, setIsHovered] = useState(false);
	return /* @__PURE__ */ jsxs("button", {
		type: "button",
		onClick: () => onSelect?.(item.id),
		onMouseEnter: () => setIsHovered(true),
		onMouseLeave: () => setIsHovered(false),
		style: {
			display: "flex",
			flexDirection: "column",
			gap: spacing.xs,
			width: "100%",
			textAlign: "left",
			padding: spacing.md,
			borderRadius: radii.md,
			border: `1px solid ${isHovered ? colors.borderActive : colors.border}`,
			background: isHovered ? colors.surfaceElevated : colors.surface,
			cursor: onSelect !== null ? "pointer" : "default",
			transition: transitions.all,
			boxShadow: isHovered ? shadows.glow : "none"
		},
		children: [
			/* @__PURE__ */ jsx("span", {
				style: {
					fontFamily: fonts.mono,
					fontSize: fontSizes.base,
					fontWeight: fontWeights.semibold,
					color: isHovered ? colors.mint : colors.textPrimary,
					transition: transitions.colors
				},
				children: item.label
			}),
			item.description !== null && /* @__PURE__ */ jsx("span", {
				style: {
					fontFamily: fonts.sans,
					fontSize: fontSizes.sm,
					color: colors.textSecondary,
					lineHeight: 1.4
				},
				children: item.description
			}),
			item.tags !== null && item.tags.length > 0 && /* @__PURE__ */ jsx("div", {
				style: {
					display: "flex",
					flexWrap: "wrap",
					gap: spacing.xs,
					marginTop: spacing.xs
				},
				children: item.tags.map((tag) => /* @__PURE__ */ jsx("span", {
					style: {
						fontFamily: fonts.mono,
						fontSize: fontSizes.xs,
						fontWeight: fontWeights.medium,
						color: colors.mint,
						background: colors.mintGlow,
						border: `1px solid ${colors.borderActive}`,
						borderRadius: radii.sm,
						padding: `2px 6px`,
						textTransform: "uppercase",
						letterSpacing: letterSpacings.wide
					},
					children: tag
				}, tag))
			})
		]
	});
}
function LibraryBrowser({ items, onSelect, title, style }) {
	const [searchQuery, setSearchQuery] = useState("");
	const [searchHovered, setSearchHovered] = useState(false);
	const [searchFocused, setSearchFocused] = useState(false);
	const resolvedItems = items ?? [];
	const filteredItems = useMemo(() => {
		const query = searchQuery.toLowerCase().trim();
		if (query === "") return resolvedItems;
		return resolvedItems.filter((item) => {
			const matchesLabel = item.label.toLowerCase().includes(query);
			const matchesDescription = item.description !== null && item.description.toLowerCase().includes(query);
			const matchesTags = item.tags !== null && item.tags.some((tag) => tag.toLowerCase().includes(query));
			return matchesLabel || matchesDescription || matchesTags;
		});
	}, [resolvedItems, searchQuery]);
	return /* @__PURE__ */ jsxs("div", {
		style: {
			display: "flex",
			flexDirection: "column",
			gap: spacing.md,
			...style
		},
		children: [
			title !== null && /* @__PURE__ */ jsxs("div", {
				style: {
					display: "flex",
					alignItems: "center",
					justifyContent: "space-between",
					paddingBottom: spacing.md,
					borderBottom: `1px solid ${colors.border}`
				},
				children: [/* @__PURE__ */ jsx("h2", {
					style: {
						margin: 0,
						fontFamily: fonts.mono,
						fontSize: fontSizes.lg,
						fontWeight: fontWeights.semibold,
						color: colors.textPrimary,
						letterSpacing: letterSpacings.tight
					},
					children: title
				}), /* @__PURE__ */ jsxs("span", {
					style: {
						fontFamily: fonts.mono,
						fontSize: fontSizes.xs,
						color: colors.textMuted,
						textTransform: "uppercase",
						letterSpacing: letterSpacings.wider
					},
					children: [
						filteredItems.length,
						" / ",
						resolvedItems.length
					]
				})]
			}),
			/* @__PURE__ */ jsxs("div", {
				style: { position: "relative" },
				children: [
					/* @__PURE__ */ jsx("span", {
						style: {
							position: "absolute",
							left: spacing.md,
							top: "50%",
							transform: "translateY(-50%)",
							color: colors.textMuted,
							fontFamily: fonts.mono,
							fontSize: fontSizes.sm,
							pointerEvents: "none"
						},
						children: "⌕"
					}),
					/* @__PURE__ */ jsx("input", {
						type: "text",
						value: searchQuery,
						onChange: (e) => setSearchQuery(e.target.value),
						placeholder: "Search...",
						onMouseEnter: () => setSearchHovered(true),
						onMouseLeave: () => setSearchHovered(false),
						onFocus: () => setSearchFocused(true),
						onBlur: () => setSearchFocused(false),
						style: {
							width: "100%",
							boxSizing: "border-box",
							padding: `${spacing.sm} ${spacing.md} ${spacing.sm} ${spacing["2xl"]}`,
							fontFamily: fonts.mono,
							fontSize: fontSizes.base,
							color: colors.textPrimary,
							background: colors.surface,
							border: `1px solid ${searchFocused ? colors.borderActive : searchHovered ? colors.borderHover : colors.border}`,
							borderRadius: radii.input,
							outline: "none",
							transition: transitions.all,
							boxShadow: searchFocused ? shadows.focusRing : "none"
						}
					}),
					searchQuery.length > 0 && /* @__PURE__ */ jsx("button", {
						type: "button",
						onClick: () => setSearchQuery(""),
						style: {
							position: "absolute",
							right: spacing.sm,
							top: "50%",
							transform: "translateY(-50%)",
							background: "transparent",
							border: "none",
							color: colors.textMuted,
							cursor: "pointer",
							fontSize: fontSizes.base,
							padding: spacing.xs,
							lineHeight: 1
						},
						"aria-label": "Clear search",
						children: "×"
					})
				]
			}),
			/* @__PURE__ */ jsx("div", {
				style: {
					display: "flex",
					flexDirection: "column",
					gap: spacing.sm,
					maxHeight: "400px",
					overflowY: "auto",
					paddingRight: spacing.xs
				},
				children: filteredItems.length > 0 ? filteredItems.map((item) => /* @__PURE__ */ jsx(LibraryItemRow, {
					item,
					onSelect
				}, item.id)) : /* @__PURE__ */ jsxs("div", {
					style: {
						display: "flex",
						flexDirection: "column",
						alignItems: "center",
						justifyContent: "center",
						padding: spacing["2xl"],
						border: `1px dashed ${colors.border}`,
						borderRadius: radii.card,
						gap: spacing.sm
					},
					children: [/* @__PURE__ */ jsx("span", {
						style: {
							fontFamily: fonts.mono,
							fontSize: fontSizes.lg,
							color: colors.textMuted
						},
						children: "∅"
					}), /* @__PURE__ */ jsx("span", {
						style: {
							fontFamily: fonts.mono,
							fontSize: fontSizes.sm,
							color: colors.textMuted,
							textAlign: "center"
						},
						children: searchQuery.length > 0 ? `No results for "${searchQuery}"` : "No items available"
					})]
				})
			})
		]
	});
}
//#endregion
//#region organisms/ExtendedThinkingViewer.tsx
/**
* EITELKEIT ExtendedThinkingViewer
* ═══════════════════════════════════════════════════════════════════
* Visualizes Claude extended thinking: thinking blocks, tool calls,
* and final response with streaming support.
*/
const BLOCK_CONFIG = {
	thinking: {
		label: "THINKING",
		borderColor: colors.mint,
		bgColor: `${colors.mint}08`,
		labelColor: colors.mint
	},
	tool_use: {
		label: "TOOL USE",
		borderColor: "#555577",
		bgColor: "rgba(85, 85, 119, 0.08)",
		labelColor: "#8888bb"
	},
	tool_result: {
		label: "TOOL RESULT",
		borderColor: "#446644",
		bgColor: "rgba(68, 102, 68, 0.08)",
		labelColor: "#66aa66"
	},
	text: {
		label: "RESPONSE",
		borderColor: colors.info ?? "#4488ff",
		bgColor: "rgba(68, 136, 255, 0.05)",
		labelColor: colors.info ?? "#4488ff"
	}
};
function ExtendedThinkingViewer({ blocks, isStreaming, title, maxHeight }) {
	const [isCollapsed, setIsCollapsed] = useState(false);
	const endRef = useRef(null);
	const resolvedBlocks = blocks ?? [];
	const resolvedStreaming = isStreaming ?? false;
	const resolvedTitle = title ?? "Extended Thinking";
	const resolvedMaxHeight = maxHeight ?? "600px";
	useEffect(() => {
		if (resolvedStreaming && endRef.current) endRef.current.scrollIntoView({ behavior: "smooth" });
	}, [resolvedBlocks.length, resolvedStreaming]);
	return /* @__PURE__ */ jsxs("div", {
		style: {
			display: "flex",
			flexDirection: "column",
			background: "#0a0a0f",
			border: `1px solid ${resolvedStreaming ? colors.mint : "#222233"}`,
			borderRadius: radii.lg ?? "8px",
			overflow: "hidden",
			fontFamily: fonts.mono,
			boxShadow: resolvedStreaming ? `0 0 20px ${colors.mint}22` : "none",
			transition: "border-color 0.3s ease, box-shadow 0.3s ease"
		},
		children: [
			/* @__PURE__ */ jsxs("div", {
				style: {
					display: "flex",
					alignItems: "center",
					justifyContent: "space-between",
					padding: `${spacing.sm ?? "8px"} ${spacing.md ?? "16px"}`,
					background: "#0d0d18",
					borderBottom: isCollapsed ? "none" : "1px solid #222233",
					cursor: "pointer",
					userSelect: "none"
				},
				role: "button",
				tabIndex: 0,
				"aria-expanded": !isCollapsed,
				"aria-label": `${resolvedTitle} — ${isCollapsed ? "expand" : "collapse"}`,
				onClick: () => setIsCollapsed(!isCollapsed),
				onKeyDown: (e) => {
					if (e.key === "Enter" || e.key === " ") setIsCollapsed(!isCollapsed);
				},
				children: [/* @__PURE__ */ jsxs("div", {
					style: {
						display: "flex",
						alignItems: "center",
						gap: spacing.sm ?? "8px"
					},
					children: [
						resolvedStreaming && /* @__PURE__ */ jsx("div", { style: {
							width: "8px",
							height: "8px",
							borderRadius: "50%",
							background: colors.mint,
							animation: "etv-pulse 1.2s ease-in-out infinite",
							flexShrink: 0
						} }),
						/* @__PURE__ */ jsx("span", {
							style: {
								fontSize: fontSizes.xs ?? "12px",
								fontWeight: fontWeights.semibold ?? 600,
								color: colors.mint,
								textTransform: "uppercase",
								letterSpacing: letterSpacings.wider ?? "0.1em"
							},
							children: resolvedTitle
						}),
						/* @__PURE__ */ jsxs("span", {
							style: {
								fontSize: fontSizes.xs ?? "12px",
								color: "#555577"
							},
							children: [
								resolvedBlocks.length,
								" block",
								resolvedBlocks.length !== 1 ? "s" : "",
								resolvedStreaming ? " · streaming..." : ""
							]
						})
					]
				}), /* @__PURE__ */ jsx("span", {
					style: {
						color: "#555577",
						fontSize: fontSizes.xs ?? "12px",
						transform: isCollapsed ? "rotate(-90deg)" : "none",
						transition: "transform 0.2s ease"
					},
					children: "▼"
				})]
			}),
			!isCollapsed && /* @__PURE__ */ jsxs("div", {
				style: {
					overflowY: "auto",
					maxHeight: resolvedMaxHeight,
					padding: spacing.sm ?? "8px",
					display: "flex",
					flexDirection: "column",
					gap: "6px"
				},
				children: [
					resolvedBlocks.map((block, idx) => /* @__PURE__ */ jsx(BlockItem, {
						block,
						index: idx
					}, block.id ?? `block-${idx}`)),
					resolvedStreaming && resolvedBlocks.length === 0 && /* @__PURE__ */ jsx("div", {
						style: {
							padding: spacing.md ?? "16px",
							color: "#555577",
							fontSize: fontSizes.sm ?? "14px",
							textAlign: "center"
						},
						children: "Waiting for thinking blocks..."
					}),
					/* @__PURE__ */ jsx("div", { ref: endRef })
				]
			}),
			/* @__PURE__ */ jsx("style", { children: `
        @keyframes etv-pulse {
          0%, 100% { opacity: 1; transform: scale(1); }
          50% { opacity: 0.4; transform: scale(1.3); }
        }
        @keyframes etv-cursor {
          0%, 50% { opacity: 1; }
          51%, 100% { opacity: 0; }
        }
      ` })
		]
	});
}
function BlockItem({ block, index }) {
	const [expanded, setExpanded] = useState(true);
	const config = BLOCK_CONFIG[block.type];
	const depth = block.depth ?? 0;
	return /* @__PURE__ */ jsxs("div", {
		style: {
			borderLeft: `3px solid ${config.borderColor}`,
			background: config.bgColor,
			borderRadius: "4px",
			overflow: "hidden",
			marginLeft: `${depth * 16}px`
		},
		children: [/* @__PURE__ */ jsxs("div", {
			style: {
				display: "flex",
				alignItems: "center",
				justifyContent: "space-between",
				padding: "4px 10px",
				cursor: "pointer",
				background: `${config.borderColor}10`
			},
			role: "button",
			tabIndex: 0,
			"aria-expanded": expanded,
			onClick: () => setExpanded(!expanded),
			onKeyDown: (e) => {
				if (e.key === "Enter" || e.key === " ") setExpanded(!expanded);
			},
			children: [/* @__PURE__ */ jsxs("div", {
				style: {
					display: "flex",
					alignItems: "center",
					gap: "8px"
				},
				children: [
					/* @__PURE__ */ jsx("span", {
						style: {
							fontSize: "10px",
							fontWeight: 700,
							color: config.labelColor,
							letterSpacing: "0.08em",
							textTransform: "uppercase"
						},
						children: config.label
					}),
					block.id !== null && /* @__PURE__ */ jsxs("span", {
						style: {
							fontSize: "10px",
							color: "#444466"
						},
						children: ["#", block.id.slice(0, 8)]
					}),
					/* @__PURE__ */ jsxs("span", {
						style: {
							fontSize: "10px",
							color: "#333355"
						},
						children: [
							"[",
							index + 1,
							"]"
						]
					})
				]
			}), /* @__PURE__ */ jsxs("div", {
				style: {
					display: "flex",
					alignItems: "center",
					gap: "12px"
				},
				children: [block.durationMs !== null && /* @__PURE__ */ jsx("span", {
					style: {
						fontSize: "10px",
						color: "#444466"
					},
					children: block.durationMs < 1e3 ? `${block.durationMs}ms` : `${(block.durationMs / 1e3).toFixed(1)}s`
				}), /* @__PURE__ */ jsx("span", {
					style: {
						fontSize: "10px",
						color: "#333355"
					},
					children: expanded ? "▼" : "▶"
				})]
			})]
		}), expanded && /* @__PURE__ */ jsx("div", {
			style: {
				padding: "8px 12px",
				fontSize: fontSizes.sm ?? "14px",
				color: block.type === "thinking" ? "#ccddcc" : "#aaaacc",
				lineHeight: 1.6,
				whiteSpace: "pre-wrap",
				overflowWrap: "break-word",
				fontFamily: block.type === "text" ? fonts.sans : fonts.mono,
				maxHeight: "300px",
				overflowY: "auto"
			},
			children: block.content
		})]
	});
}
//#endregion
//#region organisms/CommandPalette.tsx
function fuzzyMatch(query, text) {
	if (!query) return true;
	const q = query.toLowerCase();
	const t = text.toLowerCase();
	if (t.includes(q)) return true;
	let qi = 0;
	for (let i = 0; i < t.length && qi < q.length; i++) if (t[i] === q[qi]) qi++;
	return qi === q.length;
}
function CommandPalette({ isOpen, commands, onClose, isMobile }) {
	const [query, setQuery] = useState("");
	const [selectedIdx, setSelectedIdx] = useState(0);
	const [isAnimatingOut, setIsAnimatingOut] = useState(false);
	const inputRef = useRef(null);
	const haptic = useHaptic();
	const mobile = isMobile ?? (typeof window !== "undefined" && window.matchMedia("(pointer: coarse)").matches);
	const filtered = commands.filter((c) => fuzzyMatch(query, c.label) || fuzzyMatch(query, c.description));
	useEffect(() => {
		if (isOpen) {
			setQuery("");
			setSelectedIdx(0);
			setIsAnimatingOut(false);
		}
	}, [isOpen]);
	const close = useCallback(() => setIsAnimatingOut(true), []);
	const selectCommand = useCallback((cmd) => {
		haptic("medium");
		cmd.action();
		onClose();
	}, [haptic, onClose]);
	useEffect(() => {
		if (!isOpen) return;
		const handler = (e) => {
			if (e.key === "Escape") {
				close();
				return;
			}
			if (e.key === "ArrowDown") {
				e.preventDefault();
				setSelectedIdx((i) => Math.min(i + 1, filtered.length - 1));
			}
			if (e.key === "ArrowUp") {
				e.preventDefault();
				setSelectedIdx((i) => Math.max(i - 1, 0));
			}
			if (e.key === "Enter" && filtered[selectedIdx] !== void 0) selectCommand(filtered[selectedIdx]);
		};
		document.addEventListener("keydown", handler);
		return () => document.removeEventListener("keydown", handler);
	}, [
		isOpen,
		filtered,
		selectedIdx,
		close,
		selectCommand
	]);
	const innerContent = /* @__PURE__ */ jsxs(Fragment, { children: [
		/* @__PURE__ */ jsx("div", {
			style: {
				padding: `${spacing[3]} ${spacing[4]}`,
				borderBottom: `1px solid ${colors.border}`,
				flexShrink: 0
			},
			children: /* @__PURE__ */ jsx("input", {
				ref: inputRef,
				role: "searchbox",
				type: "text",
				placeholder: "Type a command…",
				value: query,
				onChange: (e) => {
					setQuery(e.target.value);
					setSelectedIdx(0);
				},
				onAnimationEnd: mobile ? () => inputRef.current?.focus({ preventScroll: true }) : void 0,
				style: {
					width: "100%",
					background: "transparent",
					border: "none",
					outline: "none",
					color: colors.text,
					fontSize: 16,
					fontFamily: "var(--font-mono)",
					caretColor: colors.mint
				}
			})
		}),
		/* @__PURE__ */ jsxs("div", {
			style: {
				overflowY: "auto",
				flex: 1,
				minHeight: 0,
				paddingBottom: mobile ? "calc(8px + var(--safe-bottom, 0px))" : spacing[2]
			},
			children: [filtered.length === 0 && /* @__PURE__ */ jsx("div", {
				style: {
					padding: spacing[6],
					textAlign: "center",
					color: colors.textMuted,
					fontSize: 14
				},
				children: "No commands found"
			}), filtered.map((cmd, idx) => /* @__PURE__ */ jsxs("div", {
				onPointerDown: (e) => {
					if (e.pointerType !== "mouse" || e.button === 0) {
						e.preventDefault();
						selectCommand(cmd);
					}
				},
				style: {
					display: "flex",
					alignItems: "center",
					gap: spacing[3],
					padding: mobile ? `${spacing[4]} ${spacing[4]}` : `${spacing[3]} ${spacing[4]}`,
					minHeight: mobile ? 56 : 44,
					cursor: "pointer",
					touchAction: "manipulation",
					background: idx === selectedIdx ? colors.mintGlow : "transparent",
					borderLeft: idx === selectedIdx ? `2px solid ${colors.mint}` : "2px solid transparent",
					transition: `background ${durations.fast} ease`
				},
				children: [
					/* @__PURE__ */ jsx("span", {
						style: {
							fontSize: 18,
							width: 24,
							textAlign: "center",
							flexShrink: 0
						},
						children: cmd.icon
					}),
					/* @__PURE__ */ jsxs("div", {
						style: {
							flex: 1,
							minWidth: 0
						},
						children: [/* @__PURE__ */ jsx("div", {
							style: {
								color: colors.text,
								fontSize: 14,
								fontFamily: "var(--font-mono)",
								fontWeight: 500
							},
							children: cmd.label
						}), /* @__PURE__ */ jsx("div", {
							style: {
								color: colors.textMuted,
								fontSize: 12,
								marginTop: 2
							},
							children: cmd.description
						})]
					}),
					!mobile && /* @__PURE__ */ jsx("kbd", {
						style: {
							fontSize: 11,
							color: colors.textMuted,
							background: colors.voidLight,
							border: `1px solid ${colors.border}`,
							borderRadius: radii.xs,
							padding: "2px 6px"
						},
						children: "↵"
					})
				]
			}, cmd.id))]
		}),
		!mobile && /* @__PURE__ */ jsx("div", {
			style: {
				padding: `${spacing[2]} ${spacing[4]}`,
				borderTop: `1px solid ${colors.border}`,
				display: "flex",
				gap: spacing[4],
				flexShrink: 0
			},
			children: [
				"↑↓",
				"↵",
				"Esc"
			].map((key, i) => /* @__PURE__ */ jsxs("span", {
				style: {
					display: "flex",
					alignItems: "center",
					gap: 4,
					fontSize: 11,
					color: colors.textMuted
				},
				children: [/* @__PURE__ */ jsx("kbd", {
					style: {
						background: colors.voidLight,
						border: `1px solid ${colors.border}`,
						borderRadius: radii.xs,
						padding: "1px 5px",
						fontFamily: "var(--font-mono)"
					},
					children: key
				}), [
					"Navigate",
					"Select",
					"Close"
				][i]]
			}, key))
		})
	] });
	if (mobile) return /* @__PURE__ */ jsx(BottomSheet, {
		isOpen,
		onClose,
		title: null,
		dragHandle: true,
		children: innerContent
	});
	if (!isOpen && !isAnimatingOut) return null;
	const backdropStyle = {
		position: "fixed",
		inset: 0,
		background: "rgba(0,0,0,0.7)",
		zIndex: zIndices.modal,
		animation: isAnimatingOut ? `pal-bd-out ${durations.fast} ease-out forwards` : `pal-bd-in ${durations.fast} ease-out forwards`
	};
	const desktopSheetStyle = {
		position: "fixed",
		top: "15%",
		left: "50%",
		transform: "translateX(-50%)",
		width: "100%",
		maxWidth: 600,
		borderRadius: radii.xl,
		zIndex: zIndices.modal + 1,
		background: colors.glassBg,
		backdropFilter: "blur(20px)",
		WebkitBackdropFilter: "blur(20px)",
		border: `1px solid ${colors.border}`,
		boxShadow: shadows.glowStrong,
		overflow: "hidden",
		animation: isAnimatingOut ? `pal-modal-out ${durations.fast} ease-in forwards` : `pal-modal-in ${durations.fast} ease-out forwards`
	};
	const handleSheetAnimEnd = () => {
		if (isAnimatingOut) {
			onClose();
			setIsAnimatingOut(false);
			return;
		}
		inputRef.current?.focus({ preventScroll: true });
	};
	return /* @__PURE__ */ jsxs(Fragment, { children: [
		/* @__PURE__ */ jsx("style", { children: `
        @keyframes pal-bd-in   { from{opacity:0} to{opacity:1} }
        @keyframes pal-bd-out  { from{opacity:1} to{opacity:0} }
        @keyframes pal-modal-in  { from{opacity:0;transform:translateX(-50%) translateY(-8px)} to{opacity:1;transform:translateX(-50%) translateY(0)} }
        @keyframes pal-modal-out { from{opacity:1;transform:translateX(-50%) translateY(0)} to{opacity:0;transform:translateX(-50%) translateY(-8px)} }
      ` }),
		/* @__PURE__ */ jsx("div", {
			"data-testid": "palette-backdrop",
			style: backdropStyle,
			onPointerDown: (e) => {
				if (e.target === e.currentTarget) {
					haptic("light");
					close();
				}
			}
		}),
		/* @__PURE__ */ jsx("div", {
			role: "dialog",
			"aria-modal": "true",
			"aria-label": "Command Palette",
			style: desktopSheetStyle,
			onAnimationEnd: handleSheetAnimEnd,
			onPointerDown: (e) => e.stopPropagation(),
			children: innerContent
		})
	] });
}
//#endregion
//#region organisms/GitSidebarPanel.tsx
function GitSidebarPanel({ status, log, onCommit, onRefresh, isLoading }) {
	const [commitMsg, setCommitMsg] = useState("");
	const [section, setSection] = useState("status");
	const tabBtn = (active) => ({
		flex: 1,
		background: active ? colors.mintGlow : "transparent",
		color: active ? colors.mint : colors.textMuted,
		border: "none",
		borderBottom: active ? `2px solid ${colors.mint}` : "2px solid transparent",
		padding: `${spacing[2]} 0`,
		cursor: "pointer",
		fontSize: 12,
		fontFamily: "var(--font-mono)",
		touchAction: "manipulation",
		transition: `all ${durations.fast} ease`
	});
	return /* @__PURE__ */ jsxs("div", {
		style: {
			display: "flex",
			flexDirection: "column",
			height: "100%",
			background: colors.voidLight,
			borderLeft: `1px solid ${colors.border}`,
			overflow: "hidden"
		},
		children: [
			/* @__PURE__ */ jsxs("div", {
				style: {
					display: "flex",
					alignItems: "center",
					justifyContent: "space-between",
					padding: `${spacing[3]} ${spacing[4]}`,
					borderBottom: `1px solid ${colors.border}`,
					flexShrink: 0
				},
				children: [/* @__PURE__ */ jsxs("div", {
					style: {
						display: "flex",
						alignItems: "center",
						gap: spacing[2]
					},
					children: [
						/* @__PURE__ */ jsxs("span", {
							style: {
								fontSize: 14,
								fontFamily: "var(--font-mono)",
								color: colors.mint
							},
							children: ["⎇ ", status?.branch ?? "—"]
						}),
						(status?.ahead ?? 0) > 0 && /* @__PURE__ */ jsxs("span", {
							style: {
								fontSize: 11,
								color: colors.success,
								fontFamily: "var(--font-mono)"
							},
							children: ["↑", status?.ahead]
						}),
						(status?.behind ?? 0) > 0 && /* @__PURE__ */ jsxs("span", {
							style: {
								fontSize: 11,
								color: colors.warning,
								fontFamily: "var(--font-mono)"
							},
							children: ["↓", status?.behind]
						})
					]
				}), /* @__PURE__ */ jsx("button", {
					type: "button",
					onClick: onRefresh,
					disabled: isLoading,
					style: {
						background: "transparent",
						border: "none",
						color: colors.textMuted,
						cursor: "pointer",
						fontSize: 16,
						touchAction: "manipulation",
						opacity: isLoading ? .5 : 1
					},
					"aria-label": "Refresh git status",
					children: isLoading ? "⟳" : "↺"
				})]
			}),
			/* @__PURE__ */ jsxs("div", {
				style: {
					display: "flex",
					flexShrink: 0
				},
				children: [/* @__PURE__ */ jsxs("button", {
					type: "button",
					style: tabBtn(section === "status"),
					onClick: () => setSection("status"),
					children: [
						"STATUS",
						" ",
						status !== null ? `(${status.staged.length + status.unstaged.length + status.untracked.length})` : ""
					]
				}), /* @__PURE__ */ jsx("button", {
					type: "button",
					style: tabBtn(section === "log"),
					onClick: () => setSection("log"),
					children: "LOG"
				})]
			}),
			/* @__PURE__ */ jsxs("div", {
				style: {
					flex: 1,
					overflowY: "auto",
					padding: spacing[2]
				},
				children: [section === "status" && status !== null && /* @__PURE__ */ jsxs(Fragment, { children: [
					status.staged.length > 0 && /* @__PURE__ */ jsx(FileGroup, {
						label: `${status.staged.length} staged`,
						files: status.staged,
						badge: "S",
						color: colors.success
					}),
					status.unstaged.length > 0 && /* @__PURE__ */ jsx(FileGroup, {
						label: `${status.unstaged.length} unstaged`,
						files: status.unstaged,
						badge: "M",
						color: colors.warning
					}),
					status.untracked.length > 0 && /* @__PURE__ */ jsx(FileGroup, {
						label: `${status.untracked.length} untracked`,
						files: status.untracked,
						badge: "?",
						color: colors.textMuted
					})
				] }), section === "log" && log !== null && log.commits.map((c) => /* @__PURE__ */ jsxs("div", {
					style: {
						padding: `${spacing[2]} ${spacing[3]}`,
						borderRadius: radii.sm,
						marginBottom: spacing[1]
					},
					children: [/* @__PURE__ */ jsxs("div", {
						style: {
							fontSize: 12,
							color: colors.text,
							fontFamily: "var(--font-mono)"
						},
						children: [
							/* @__PURE__ */ jsx("span", {
								style: { color: colors.mint },
								children: c.shortSha
							}),
							" ",
							c.message
						]
					}), /* @__PURE__ */ jsxs("div", {
						style: {
							fontSize: 10,
							color: colors.textMuted,
							marginTop: 2
						},
						children: [
							c.author.name,
							" · ",
							new Date(c.date).toLocaleDateString()
						]
					})]
				}, c.sha))]
			}),
			status !== null && (status.staged.length > 0 || status.unstaged.length > 0) && /* @__PURE__ */ jsxs("div", {
				style: {
					padding: spacing[3],
					borderTop: `1px solid ${colors.border}`,
					flexShrink: 0
				},
				children: [/* @__PURE__ */ jsx("textarea", {
					value: commitMsg,
					onChange: (e) => setCommitMsg(e.target.value),
					placeholder: "Commit message…",
					rows: 2,
					style: {
						width: "100%",
						background: colors.void,
						border: `1px solid ${colors.border}`,
						borderRadius: radii.sm,
						color: colors.text,
						fontSize: 12,
						fontFamily: "var(--font-mono)",
						padding: spacing[2],
						resize: "none",
						boxSizing: "border-box"
					}
				}), /* @__PURE__ */ jsx("button", {
					type: "button",
					disabled: !commitMsg.trim(),
					onClick: () => {
						onCommit(commitMsg);
						setCommitMsg("");
					},
					style: {
						marginTop: spacing[2],
						width: "100%",
						background: commitMsg.trim() ? colors.mint : colors.voidLight,
						color: commitMsg.trim() ? colors.void : colors.textMuted,
						border: "none",
						borderRadius: radii.sm,
						padding: spacing[2],
						fontSize: 12,
						fontFamily: "var(--font-mono)",
						fontWeight: 600,
						cursor: commitMsg.trim() ? "pointer" : "not-allowed",
						touchAction: "manipulation"
					},
					children: "Commit & Push"
				})]
			})
		]
	});
}
function FileGroup({ label, files, badge, color }) {
	return /* @__PURE__ */ jsxs("div", {
		style: { marginBottom: spacing[3] },
		children: [/* @__PURE__ */ jsx("div", {
			style: {
				fontSize: 10,
				color: colors.textMuted,
				padding: `${spacing[1]} ${spacing[2]}`,
				fontFamily: "var(--font-mono)"
			},
			children: label
		}), files.map((f) => /* @__PURE__ */ jsxs("div", {
			style: {
				display: "flex",
				alignItems: "center",
				gap: spacing[2],
				padding: `${spacing[1]} ${spacing[2]}`,
				fontSize: 11,
				fontFamily: "var(--font-mono)",
				color: colors.text
			},
			children: [/* @__PURE__ */ jsx("span", {
				style: {
					fontSize: 9,
					fontWeight: 700,
					color,
					width: 12,
					textAlign: "center"
				},
				children: badge
			}), /* @__PURE__ */ jsx("span", {
				style: {
					flex: 1,
					overflow: "hidden",
					textOverflow: "ellipsis",
					whiteSpace: "nowrap"
				},
				children: f.split("/").pop()
			})]
		}, f))]
	});
}
//#endregion
//#region organisms/NotificationSetup.tsx
function NotificationSetup({ isSubscribed, onSubscribe, onUnsubscribe, isLoading, error }) {
	const btnStyle = {
		background: isSubscribed ? "transparent" : colors.mint,
		color: isSubscribed ? colors.textMuted : colors.void,
		border: isSubscribed ? `1px solid ${colors.border}` : "none",
		borderRadius: radii.md,
		padding: `${spacing[3]} ${spacing[5]}`,
		fontSize: 14,
		fontFamily: "var(--font-mono)",
		fontWeight: 600,
		cursor: isLoading ? "not-allowed" : "pointer",
		opacity: isLoading ? .6 : 1,
		touchAction: "manipulation",
		minHeight: 48
	};
	return /* @__PURE__ */ jsxs("div", {
		style: { padding: spacing[4] },
		children: [
			/* @__PURE__ */ jsxs("div", {
				style: {
					display: "flex",
					alignItems: "center",
					gap: spacing[3],
					marginBottom: spacing[4]
				},
				children: [/* @__PURE__ */ jsx("span", {
					style: { fontSize: 24 },
					children: "🔔"
				}), /* @__PURE__ */ jsxs("div", { children: [/* @__PURE__ */ jsx("div", {
					style: {
						fontSize: 14,
						color: colors.text,
						fontWeight: 600
					},
					children: "Push Notifications"
				}), /* @__PURE__ */ jsx("div", {
					style: {
						fontSize: 12,
						color: colors.textMuted,
						marginTop: 2
					},
					children: isSubscribed ? "Receiving notifications from this Mac" : "Get notified when long tasks complete"
				})] })]
			}),
			/* @__PURE__ */ jsx("button", {
				type: "button",
				onClick: isSubscribed ? onUnsubscribe : onSubscribe,
				disabled: isLoading,
				style: btnStyle,
				children: isLoading ? "…" : isSubscribed ? "Disable Notifications" : "Enable Push Notifications"
			}),
			error !== null && /* @__PURE__ */ jsx("div", {
				style: {
					marginTop: spacing[3],
					padding: spacing[3],
					background: `${colors.danger}22`,
					border: `1px solid ${colors.danger}44`,
					borderRadius: radii.sm,
					fontSize: 12,
					color: colors.danger,
					fontFamily: "var(--font-mono)"
				},
				children: error
			})
		]
	});
}
//#endregion
//#region organisms/SshProfilePanel.tsx
function SshProfilePanel({ profiles, onConnect, onSave, onDelete }) {
	const [showAdd, setShowAdd] = useState(false);
	const [form, setForm] = useState({
		label: "",
		host: "",
		port: 22,
		user: "root",
		identityFile: null,
		jumpHost: null
	});
	const mintBtn = {
		background: colors.mint,
		color: colors.void,
		border: "none",
		borderRadius: radii.sm,
		padding: `${spacing[1]} ${spacing[3]}`,
		fontSize: 11,
		fontFamily: "var(--font-mono)",
		fontWeight: 700,
		cursor: "pointer",
		touchAction: "manipulation",
		minHeight: 32,
		whiteSpace: "nowrap"
	};
	return /* @__PURE__ */ jsxs("div", {
		style: {
			display: "flex",
			flexDirection: "column",
			height: "100%",
			background: colors.voidLight
		},
		children: [
			/* @__PURE__ */ jsxs("div", {
				style: {
					display: "flex",
					alignItems: "center",
					justifyContent: "space-between",
					padding: `${spacing[3]} ${spacing[4]}`,
					borderBottom: `1px solid ${colors.border}`
				},
				children: [/* @__PURE__ */ jsx("span", {
					style: {
						fontSize: 13,
						fontFamily: "var(--font-mono)",
						color: colors.text,
						fontWeight: 600
					},
					children: "SSH Profiles"
				}), /* @__PURE__ */ jsx("button", {
					type: "button",
					onClick: () => setShowAdd(!showAdd),
					style: {
						...mintBtn,
						background: "transparent",
						color: colors.mint,
						border: `1px solid ${colors.mint}44`
					},
					children: showAdd ? "✕ Cancel" : "+ Add"
				})]
			}),
			showAdd && /* @__PURE__ */ jsxs("div", {
				style: {
					padding: spacing[4],
					borderBottom: `1px solid ${colors.border}`
				},
				children: [[
					"label",
					"host",
					"user"
				].map((field) => /* @__PURE__ */ jsx("input", {
					type: "text",
					placeholder: field.charAt(0).toUpperCase() + field.slice(1),
					value: form[field],
					onChange: (e) => setForm({
						...form,
						[field]: e.target.value
					}),
					style: {
						width: "100%",
						background: colors.void,
						border: `1px solid ${colors.border}`,
						borderRadius: radii.sm,
						color: colors.text,
						fontSize: 12,
						fontFamily: "var(--font-mono)",
						padding: spacing[2],
						marginBottom: spacing[2],
						boxSizing: "border-box"
					}
				}, field)), /* @__PURE__ */ jsx("button", {
					type: "button",
					disabled: !form.label || !form.host || !form.user,
					onClick: () => {
						onSave({
							...form,
							id: `${form.label}-${Date.now()}`
						});
						setShowAdd(false);
					},
					style: {
						...mintBtn,
						width: "100%",
						minHeight: 44,
						marginTop: spacing[2]
					},
					children: "Save Profile"
				})]
			}),
			/* @__PURE__ */ jsxs("div", {
				style: {
					flex: 1,
					overflowY: "auto"
				},
				children: [profiles.length === 0 && /* @__PURE__ */ jsx("div", {
					style: {
						padding: spacing[6],
						textAlign: "center",
						color: colors.textMuted,
						fontSize: 13
					},
					children: "No SSH profiles configured"
				}), profiles.map((p) => /* @__PURE__ */ jsxs("div", {
					style: {
						display: "flex",
						alignItems: "center",
						gap: spacing[3],
						padding: `${spacing[3]} ${spacing[4]}`,
						borderBottom: `1px solid ${colors.border}`
					},
					children: [
						/* @__PURE__ */ jsxs("div", {
							style: {
								flex: 1,
								minWidth: 0
							},
							children: [/* @__PURE__ */ jsx("div", {
								style: {
									fontSize: 13,
									color: colors.text,
									fontFamily: "var(--font-mono)",
									fontWeight: 500
								},
								children: p.label
							}), /* @__PURE__ */ jsxs("div", {
								style: {
									fontSize: 11,
									color: colors.textMuted,
									marginTop: 2
								},
								children: [
									p.user,
									"@",
									p.host,
									":",
									p.port,
									p.jumpHost !== null && ` via ${p.jumpHost}`
								]
							})]
						}),
						/* @__PURE__ */ jsx("button", {
							type: "button",
							"aria-label": `Connect to ${p.label}`,
							onClick: () => onConnect(p.id),
							style: mintBtn,
							children: "Connect"
						}),
						/* @__PURE__ */ jsx("button", {
							type: "button",
							"aria-label": `Delete ${p.label}`,
							onClick: () => onDelete(p.id),
							style: {
								...mintBtn,
								background: "transparent",
								color: colors.danger,
								border: `1px solid ${colors.danger}44`
							},
							children: "✕"
						})
					]
				}, p.id))]
			})
		]
	});
}
//#endregion
//#region organisms/ActivationHeatmap.tsx
function ActivationHeatmap({ layerName, activations, scaleIndex, activeBlockIndex, onBlockClick }) {
	const blockSize = Math.max(1, 16 >> (scaleIndex ?? 0));
	return /* @__PURE__ */ jsxs("div", {
		style: {
			marginBottom: spacing[2],
			position: "relative"
		},
		children: [
			/* @__PURE__ */ jsxs("div", {
				style: {
					fontSize: 11,
					color: colors.textMuted,
					marginBottom: 4
				},
				children: [
					layerName,
					" (Scale ",
					scaleIndex ?? 0,
					")"
				]
			}),
			/* @__PURE__ */ jsx("div", {
				style: {
					display: "grid",
					gridTemplateColumns: `repeat(${Math.ceil(activations.length / blockSize)}, 1fr)`,
					gap: 1
				},
				children: activations.map((a, i) => /* @__PURE__ */ jsx("div", {
					style: {
						height: 8,
						background: `rgba(57, 255, 159, ${Math.min(1, Math.abs(a))})`,
						borderRadius: radii.xs,
						cursor: onBlockClick !== null ? "pointer" : "default",
						border: activeBlockIndex === i ? `1px solid ${colors.mint}` : "none",
						boxSizing: "border-box"
					},
					title: `Val: ${a.toFixed(3)}`,
					onClick: (e) => {
						e.stopPropagation();
						onBlockClick?.(i, e);
					}
				}, i))
			}),
			activeBlockIndex !== null && activations[activeBlockIndex] !== void 0 && /* @__PURE__ */ jsx(ActivationFlyout, { activation: activations[activeBlockIndex] })
		]
	});
}
function ActivationFlyout({ activation }) {
	const int8Values = useMemo(() => {
		return Array.from({ length: 16 }, (_, i) => {
			const pseudoRandom = Math.sin(activation * 1234.5678 + i) * 127;
			return Math.floor(pseudoRandom);
		});
	}, [activation]);
	const dotProduct = useMemo(() => {
		return int8Values.reduce((acc, val) => acc + val, 0);
	}, [int8Values]);
	const quantError = useMemo(() => {
		return Math.abs(activation - dotProduct / 1e3).toFixed(4);
	}, [activation, dotProduct]);
	return /* @__PURE__ */ jsx("div", {
		style: {
			position: "absolute",
			top: "100%",
			left: 0,
			zIndex: 50,
			marginTop: spacing[2]
		},
		onClick: (e) => e.stopPropagation(),
		children: /* @__PURE__ */ jsxs(GlassPanel, {
			blur: "md",
			intensity: "heavy",
			bordered: true,
			style: {
				padding: spacing[3],
				width: 260
			},
			children: [
				/* @__PURE__ */ jsx("div", {
					style: {
						fontSize: 12,
						color: colors.textPrimary,
						marginBottom: spacing[2],
						fontWeight: 600
					},
					children: "v128 Introspection"
				}),
				/* @__PURE__ */ jsx("div", {
					style: {
						display: "grid",
						gridTemplateColumns: "repeat(4, 1fr)",
						gap: spacing[1],
						marginBottom: spacing[3]
					},
					children: int8Values.map((v, i) => /* @__PURE__ */ jsx("div", {
						style: {
							background: "rgba(0,0,0,0.3)",
							padding: "2px 4px",
							borderRadius: radii.sm,
							fontSize: 10,
							color: colors.textSecondary,
							textAlign: "center",
							fontFamily: "monospace"
						},
						children: v
					}, i))
				}),
				/* @__PURE__ */ jsxs("div", {
					style: {
						display: "flex",
						justifyContent: "space-between",
						fontSize: 11
					},
					children: [/* @__PURE__ */ jsx("span", {
						style: { color: colors.textMuted },
						children: "Dot-Product:"
					}), /* @__PURE__ */ jsx("span", {
						style: {
							color: colors.textPrimary,
							fontFamily: "monospace"
						},
						children: dotProduct
					})]
				}),
				/* @__PURE__ */ jsxs("div", {
					style: {
						display: "flex",
						justifyContent: "space-between",
						fontSize: 11,
						marginTop: spacing[1]
					},
					children: [/* @__PURE__ */ jsx("span", {
						style: { color: colors.textMuted },
						children: "Quant-Error:"
					}), /* @__PURE__ */ jsx("span", {
						style: {
							color: colors.danger,
							fontFamily: "monospace"
						},
						children: quantError
					})]
				})
			]
		})
	});
}
//#endregion
//#region organisms/VortexInspector.tsx
function VortexInspector({ vortexId, state, audioActivations, textTokens, auditChainId = null, onDropBlob, dimensions = null }) {
	const [activeFlyout, setActiveFlyout] = useState(null);
	const handleDragOver = (e) => {
		e.preventDefault();
	};
	const handleDrop = (e) => {
		e.preventDefault();
		const cid = e.dataTransfer.getData("text/plain");
		if (cid && onDropBlob) onDropBlob(cid);
	};
	return /* @__PURE__ */ jsxs(GlassPanel, {
		blur: "lg",
		intensity: "heavy",
		bordered: true,
		style: {
			padding: spacing[4],
			display: "flex",
			flexDirection: "column",
			gap: spacing[4]
		},
		onClick: () => setActiveFlyout(null),
		onDragOver: handleDragOver,
		onDrop: handleDrop,
		children: [/* @__PURE__ */ jsxs("div", {
			style: {
				display: "flex",
				justifyContent: "space-between",
				alignItems: "center"
			},
			children: [/* @__PURE__ */ jsxs("div", {
				style: {
					display: "flex",
					alignItems: "center",
					gap: spacing[2]
				},
				children: [/* @__PURE__ */ jsxs("h3", {
					style: {
						color: colors.mint,
						margin: 0,
						fontSize: 16
					},
					children: ["Vortex ", vortexId]
				}), auditChainId && /* @__PURE__ */ jsxs("span", {
					style: {
						fontSize: 10,
						background: colors.surfaceElevated,
						padding: "2px 6px",
						borderRadius: radii.full,
						color: colors.textSecondary
					},
					children: ["Audit: ", auditChainId.slice(0, 8)]
				})]
			}), /* @__PURE__ */ jsxs("div", {
				style: {
					display: "flex",
					alignItems: "center",
					gap: spacing[2]
				},
				children: [/* @__PURE__ */ jsx("div", { style: {
					width: 8,
					height: 8,
					borderRadius: "50%",
					background: state === "running" ? colors.mint : state === "error" ? colors.danger : colors.textMuted,
					boxShadow: state === "running" ? `0 0 8px ${colors.mint}` : "none"
				} }), /* @__PURE__ */ jsx("span", {
					style: {
						color: colors.textSecondary,
						fontSize: 12
					},
					children: state.toUpperCase()
				})]
			})]
		}), /* @__PURE__ */ jsxs("div", {
			style: {
				display: "flex",
				gap: spacing[4],
				flexDirection: "column"
			},
			children: [dimensions && /* @__PURE__ */ jsxs("div", {
				style: {
					display: "grid",
					gridTemplateColumns: "repeat(3, 1fr)",
					gap: spacing[2],
					background: "rgba(0,255,135,0.05)",
					padding: spacing[3],
					borderRadius: radii.md,
					border: `1px solid rgba(0,255,135,0.1)`
				},
				children: [
					/* @__PURE__ */ jsxs("div", {
						style: {
							fontSize: 10,
							color: colors.textMuted
						},
						children: [
							"D1: CONTENT",
							/* @__PURE__ */ jsx("br", {}),
							/* @__PURE__ */ jsxs("span", {
								style: {
									color: colors.mint,
									fontFamily: "monospace"
								},
								children: [dimensions.d1_content.slice(0, 8), "..."]
							})
						]
					}),
					/* @__PURE__ */ jsxs("div", {
						style: {
							fontSize: 10,
							color: colors.textMuted
						},
						children: [
							"D2: SEMANTIC",
							/* @__PURE__ */ jsx("br", {}),
							/* @__PURE__ */ jsxs("span", {
								style: {
									color: colors.mint,
									fontFamily: "monospace"
								},
								children: ["0x", dimensions.d2_semantic.toString(16)]
							})
						]
					}),
					/* @__PURE__ */ jsxs("div", {
						style: {
							fontSize: 10,
							color: colors.textMuted
						},
						children: [
							"D3: TEMPORAL",
							/* @__PURE__ */ jsx("br", {}),
							/* @__PURE__ */ jsx("span", {
								style: {
									color: colors.mint,
									fontFamily: "monospace"
								},
								children: (/* @__PURE__ */ new Date(dimensions.d3_temporal / 1e3)).toISOString().split("T")[1]
							})
						]
					}),
					/* @__PURE__ */ jsxs("div", {
						style: {
							fontSize: 10,
							color: colors.textMuted
						},
						children: [
							"D4: OWNERSHIP",
							/* @__PURE__ */ jsx("br", {}),
							/* @__PURE__ */ jsxs("span", {
								style: {
									color: colors.mint,
									fontFamily: "monospace"
								},
								children: [dimensions.d4_ownership.slice(0, 8), "..."]
							})
						]
					}),
					/* @__PURE__ */ jsxs("div", {
						style: {
							fontSize: 10,
							color: colors.textMuted
						},
						children: [
							"D5: ENCRYPTION",
							/* @__PURE__ */ jsx("br", {}),
							/* @__PURE__ */ jsxs("span", {
								style: {
									color: colors.mint,
									fontFamily: "monospace"
								},
								children: ["L", dimensions.d5_encryption]
							})
						]
					}),
					/* @__PURE__ */ jsxs("div", {
						style: {
							fontSize: 10,
							color: colors.textMuted
						},
						children: [
							"D6: SPATIAL",
							/* @__PURE__ */ jsx("br", {}),
							/* @__PURE__ */ jsxs("span", {
								style: {
									color: colors.mint,
									fontFamily: "monospace"
								},
								children: ["DEV_", dimensions.d6_spatial]
							})
						]
					})
				]
			}), /* @__PURE__ */ jsxs("div", {
				style: {
					display: "flex",
					gap: spacing[4]
				},
				children: [Object.keys(audioActivations).length > 0 && /* @__PURE__ */ jsxs("div", {
					style: {
						flex: 1,
						background: "rgba(0,0,0,0.2)",
						padding: spacing[3],
						borderRadius: radii.md
					},
					children: [/* @__PURE__ */ jsx("div", {
						style: {
							fontSize: 12,
							fontWeight: 600,
							color: colors.textPrimary,
							marginBottom: spacing[3]
						},
						children: "Zipformer Audio Hierarchy"
					}), Object.entries(audioActivations).map(([layer, acts], idx) => /* @__PURE__ */ jsx(ActivationHeatmap, {
						layerName: layer,
						activations: acts,
						scaleIndex: idx % 4,
						activeBlockIndex: activeFlyout?.layer === layer ? activeFlyout.index : null,
						onBlockClick: (index) => setActiveFlyout({
							layer,
							index
						})
					}, layer))]
				}), /* @__PURE__ */ jsxs("div", {
					style: {
						flex: 1,
						background: "rgba(0,0,0,0.2)",
						padding: spacing[3],
						borderRadius: radii.md
					},
					children: [/* @__PURE__ */ jsx("div", {
						style: {
							fontSize: 12,
							fontWeight: 600,
							color: colors.textPrimary,
							marginBottom: spacing[3]
						},
						children: "LLM Text Tokens"
					}), /* @__PURE__ */ jsx("div", {
						style: {
							display: "flex",
							flexWrap: "wrap",
							gap: spacing[2]
						},
						children: textTokens.map((t, i) => /* @__PURE__ */ jsx("span", {
							style: {
								background: colors.surface,
								color: colors.textPrimary,
								padding: "4px 8px",
								borderRadius: radii.sm,
								fontSize: 13,
								fontFamily: "monospace"
							},
							children: t
						}, i))
					})]
				})]
			})]
		})]
	});
}
//#endregion
export { useToast as A, MemoryViewer as C, BentoGrid as D, AgentPanel as E, Sidebar as M, BentoLayouts as O, ThinkingStateViewer as S, AgentOrchestra as T, ActionConfirmation as _, GitSidebarPanel as a, ToolCallVisualizer as b, LibraryBrowser as c, SettingsFormBuilder as d, TreeLayerPanel as f, Canvas as g, CanvasNode as h, NotificationSetup as i, Header as j, ToastProvider as k, Dashboard as l, CanvasEdge as m, ActivationHeatmap as n, CommandPalette as o, PerformanceMonitor as p, SshProfilePanel as r, ExtendedThinkingViewer as s, VortexInspector as t, ThemeProfile as u, ThreadPanel as v, ConsciousnessStream as w, CouncilDeliberation as x, ShareDialog as y };

//# sourceMappingURL=organisms-frQpQ00h.mjs.map